<script lang="js">
import axios from "axios";
import Swal from "sweetalert2";

export default {
  name: "Academics",
  data() {
    return {
      logger: null,
      loading: false,
      academicSessions: [],
      courses: [],
      departments: [],
      programs: [],
      schools: [],

      programTypes: [],
      programModes: [],
      programSessionPayments: [],
      paymentTypes: [],
      paymentTargetOptions: {
        students: [],
        applicants: [],
        programTargets: []
      },
      paymentModalInstance: null,
      paymentModalMode: "add",
      paymentFormError: "",
      paymentFormSubmitting: false,
      paymentTargetProgramValues: [],
      selectedProgramSessionPaymentSession: "all",
      searchQuery: "",
      currentTab: "sessions", // sessions, courses, schools, departments, programs, requirements, programTypes, programModes, programSessionPayments
      pagination: {
        currentPage: 1,
        itemsPerPage: 10,
        totalItems: 0
      },
      paymentTypesPagination: {
        currentPage: 1,
        itemsPerPage: 10,
        totalPages: 1
      },
      // New academic session form data
      newAcademicSession: {
        sessionYear: "",
        startDate: "",
        endDate: "",
        status: "open",
        description: ""
      },
      // New course form data
      newCourse: {
        code: "",
        title: "",
        description: "",
        creditUnits: 0,
        department: "",
        program: "",
        level: "",
        semester: ""
      },
      // New school form data
      newSchool: {
        name: "",
        code: "",
        description: ""
      },
      // New department form data
      newDepartment: {
        name: "",
        code: "",
        description: "",
        school: ""
      },
      // New program form data
      newProgram: {
        name: "",
        code: "",
        description: "",
        department: "",
        isHND: false,
        isND: false,
        isPartTime: false,
        isFullTime: false,
        programTypes: [],
        // minUnits and maxUnits removed as they should only be in ProgramOfferings
      },
      // Cache for enhanced program data when navigating between modals
      cachedEnhancedProgram: null,

      // New program type form data
      newProgramType: {
        type: "",
        description: "",
        modes: [
          { mode: "", description: "" }
        ]
      },
      // New program mode form data
      newProgramMode: {
        mode: "",
        description: ""
      },
      // New program session payment form data
      newProgramSessionPayment: {
        payment: "",
        academicSession: "",
        programType: "",
        programMode: "",
        applicableTo: "students",
        targetUserIds: [],
        targetPrograms: [],
        paymentOrder: null,
        amount: 0,
        dueDate: "",
        applyLateFee: false,
        lateFee: 0,
        active: true
      },
      // Payment form for add/edit modal
      paymentForm: {
        _id: null,
        payment: "",
        academicSession: "",
        programType: "",
        programMode: "",
        applicableTo: "students",
        targetUserIds: [],
        targetPrograms: [],
        paymentOrder: null,
        amount: 0,
        dueDate: "",
        applyLateFee: false,
        lateFee: 0,
        active: true
      },
      // Payment type form for add/edit modal
      paymentTypeForm: {
        _id: null,
        name: "",
        description: "",
        amount: 0,
        active: true
      },
      // Editing items
      editingItem: null,
      editMode: false,
      // Level options
      levelOptions: [
        { value: "ND1", label: "ND1" },
        { value: "ND2", label: "ND2" },
        { value: "HND1", label: "HND1" },
        { value: "HND2", label: "HND2" }
      ],
      // Semester options
      semesterOptions: [
        { value: "First", label: "First" },
        { value: "Second", label: "Second" }
      ]
    };
  },
  created() {
    // Create a logger instance for this component
    this.logger = this.$createLogger('academicsMgt');
  },
  computed: {
    activeSessions() {
      return (this.academicSessions || []).filter(session =>
        session && session.status === 'open'
      );
    },

    sortedAcademicSessionsForPaymentFilter() {
      return [...(this.academicSessions || [])].sort((leftSession, rightSession) => {
        const getSessionSortValue = (session) => {
          const startYearFromSessionYear = parseInt(
            String(session?.sessionYear || "").split("/")[0],
            10
          );

          if (!Number.isNaN(startYearFromSessionYear)) {
            return startYearFromSessionYear;
          }

          const startDateYear = session?.startDate
            ? new Date(session.startDate).getFullYear()
            : Number.NEGATIVE_INFINITY;

          if (!Number.isNaN(startDateYear)) {
            return startDateYear;
          }

          const createdAtTime = session?.createdAt
            ? new Date(session.createdAt).getTime()
            : Number.NEGATIVE_INFINITY;

          return Number.isNaN(createdAtTime)
            ? Number.NEGATIVE_INFINITY
            : createdAtTime;
        };

        return getSessionSortValue(rightSession) - getSessionSortValue(leftSession);
      });
    },

    filteredSessions() {
      if (!this.searchQuery) return this.academicSessions;

      const query = this.searchQuery.toLowerCase();
      return this.academicSessions.filter(session =>
        session.sessionYear?.toLowerCase().includes(query)
      );
    },

    paginatedSessions() {
      const start = (this.pagination.currentPage - 1) * this.pagination.itemsPerPage;
      const end = start + this.pagination.itemsPerPage;
      return this.filteredSessions.slice(start, end);
    },

    filteredCourses() {
      if (!this.searchQuery) return this.courses;

      const query = this.searchQuery.toLowerCase();
      return this.courses.filter(course =>
        course.code?.toLowerCase().includes(query) ||
        course.title?.toLowerCase().includes(query)
      );
    },

    paginatedCourses() {
      const start = (this.pagination.currentPage - 1) * this.pagination.itemsPerPage;
      const end = start + this.pagination.itemsPerPage;
      return this.filteredCourses.slice(start, end);
    },

    filteredSchools() {
      if (!this.searchQuery) return this.schools;

      const query = this.searchQuery.toLowerCase();
      return this.schools.filter(school =>
        school.name?.toLowerCase().includes(query) ||
        school.code?.toLowerCase().includes(query)
      );
    },

    paginatedSchools() {
      const start = (this.pagination.currentPage - 1) * this.pagination.itemsPerPage;
      const end = start + this.pagination.itemsPerPage;
      return this.filteredSchools.slice(start, end);
    },

    filteredDepartments() {
      if (!this.searchQuery) return this.departments;

      const query = this.searchQuery.toLowerCase();
      return this.departments.filter(department =>
        department.name?.toLowerCase().includes(query) ||
        department.code?.toLowerCase().includes(query)
      );
    },

    paginatedDepartments() {
      const start = (this.pagination.currentPage - 1) * this.pagination.itemsPerPage;
      const end = start + this.pagination.itemsPerPage;
      return this.filteredDepartments.slice(start, end);
    },

    filteredPrograms() {
      if (!this.searchQuery) return this.programs;

      const query = this.searchQuery.toLowerCase();
      return this.programs.filter(program =>
        program.name?.toLowerCase().includes(query) ||
        program.code?.toLowerCase().includes(query)
      );
    },

    paginatedPrograms() {
      const start = (this.pagination.currentPage - 1) * this.pagination.itemsPerPage;
      const end = start + this.pagination.itemsPerPage;
      return this.filteredPrograms.slice(start, end);
    },



    filteredProgramTypes() {
      if (!this.searchQuery) return this.programTypes;

      const query = this.searchQuery.toLowerCase();
      return this.programTypes.filter(type =>
        type.type?.toLowerCase().includes(query) ||
        type.description?.toLowerCase().includes(query)
      );
    },

    paginatedProgramTypes() {
      const start = (this.pagination.currentPage - 1) * this.pagination.itemsPerPage;
      const end = start + this.pagination.itemsPerPage;
      return this.filteredProgramTypes.slice(start, end);
    },

    filteredProgramModes() {
      if (!this.searchQuery) return this.programModes;

      const query = this.searchQuery.toLowerCase();
      return this.programModes.filter(mode =>
        mode.mode?.toLowerCase().includes(query) ||
        mode.description?.toLowerCase().includes(query)
      );
    },

    paginatedProgramModes() {
      const start = (this.pagination.currentPage - 1) * this.pagination.itemsPerPage;
      const end = start + this.pagination.itemsPerPage;
      return this.filteredProgramModes.slice(start, end);
    },

    filteredProgramSessionPayments() {
      let filteredPayments = this.programSessionPayments;

      if (this.selectedProgramSessionPaymentSession !== "all") {
        filteredPayments = filteredPayments.filter((payment) => {
          const academicSessionId =
            payment.academicSession?._id || payment.academicSession;

          return academicSessionId === this.selectedProgramSessionPaymentSession;
        });
      }

      if (!this.searchQuery) return filteredPayments;

      const query = this.searchQuery.toLowerCase();
      return filteredPayments.filter(payment =>
        payment.payment?.name?.toLowerCase().includes(query) ||
        payment.academicSession?.sessionYear?.toLowerCase().includes(query) ||
        payment.programType?.type?.toLowerCase().includes(query) ||
        payment.programMode?.mode?.toLowerCase().includes(query)
      );
    },

    paginatedProgramSessionPayments() {
      const start = (this.pagination.currentPage - 1) * this.pagination.itemsPerPage;
      const end = start + this.pagination.itemsPerPage;
      return this.filteredProgramSessionPayments.slice(start, end);
    },

    filteredPaymentTypes() {
      const validPaymentTypes = (this.paymentTypes || []).filter(type => type && type._id);
      if (!this.searchQuery) return validPaymentTypes;

      const query = this.searchQuery.toLowerCase();
      return validPaymentTypes.filter(type =>
        type.name?.toLowerCase().includes(query) ||
        type.description?.toLowerCase().includes(query)
      );
    },

    paginatedPaymentTypes() {
      const start = (this.pagination.currentPage - 1) * this.pagination.itemsPerPage;
      const end = start + this.pagination.itemsPerPage;
      return this.filteredPaymentTypes.slice(start, end);
    },

    paymentModalTitle() {
      return this.paymentModalMode === 'edit'
        ? 'Edit Program Session Payment'
        : 'Add New Payment';
    },

    paymentAudiencePreviewItems() {
      if (this.paymentForm.applicableTo === 'students') {
        return this.paymentTargetOptions.students || [];
      }

      if (this.paymentForm.applicableTo === 'applicants') {
        return this.paymentTargetOptions.applicants || [];
      }

      return [];
    },

    shouldShowPaymentAudiencePreview() {
      return ['students', 'applicants'].includes(this.paymentForm.applicableTo);
    },

    shouldShowPaymentStudentTargets() {
      return this.paymentForm.applicableTo === 'individual_students';
    },

    shouldShowPaymentProgramTargets() {
      return this.paymentForm.applicableTo === 'specific_program';
    },

    selectedSpecificProgramTargetCount() {
      return this.paymentTargetProgramValues.length;
    },

    totalPages() {
      let items;
      if (this.currentTab === 'sessions') {
        items = this.filteredSessions.length;
      } else if (this.currentTab === 'courses') {
        items = this.filteredCourses.length;
      } else if (this.currentTab === 'schools') {
        items = this.filteredSchools.length;
      } else if (this.currentTab === 'departments') {
        items = this.filteredDepartments.length;
      } else if (this.currentTab === 'programs') {
        items = this.filteredPrograms.length;
      } else if (this.currentTab === 'programTypes') {
        items = this.filteredProgramTypes.length;
      } else if (this.currentTab === 'programModes') {
        items = this.filteredProgramModes.length;
      } else if (this.currentTab === 'programSessionPayments') {
        items = this.filteredProgramSessionPayments.length;
      }
      return Math.ceil(items / this.pagination.itemsPerPage);
    }
  },
  mounted() {
    this.fetchAcademicSessions();
    this.fetchSchools();
    this.fetchProgramTypes();
    this.fetchProgramModes();
  },
  beforeUnmount() {
    if (this.paymentModalInstance) {
      this.paymentModalInstance.dispose();
      this.paymentModalInstance = null;
    }
  },
  watch: {
    currentTab(newTab) {
      this.pagination.currentPage = 1;
      this.searchQuery = '';
      if (newTab === 'courses' && this.courses.length === 0) {
        this.fetchCourses();
      } else if (newTab === 'programs' && this.programs.length === 0) {
        this.fetchPrograms();
      } else if (newTab === 'schools') {
        this.fetchSchools();
        this.fetchAllDepartments(); // Always fetch departments when viewing schools
      } else if (newTab === 'departments') {
        this.fetchAllDepartments();
        this.fetchPrograms(); // Always fetch programs when viewing departments to ensure program count is accurate
      } else if (newTab === 'programTypes' && this.programTypes.length === 0) {
        this.fetchProgramTypes();
      } else if (newTab === 'programModes' && this.programModes.length === 0) {
        this.fetchProgramModes();
      } else if (newTab === 'programSessionPayments' && this.programSessionPayments.length === 0) {
        this.fetchProgramSessionPayments();
        this.fetchPaymentTypes();
      }
    },
    selectedProgramSessionPaymentSession() {
      this.pagination.currentPage = 1;
    }
  },
  methods: {
    // Fetch academic sessions
    async fetchAcademicSessions() {
      this.loading = true;
      try {
        const response = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/academic-sessions`, {
          // Headers not needed - cookies are automatic
        });

        if (response.data) {
          this.academicSessions = response.data;
          this.pagination.totalItems = this.academicSessions.length;
        } else {
          Swal.fire('Error', 'Failed to fetch academic sessions', 'error');
        }
      } catch (error) {
        this.logger.error('Error fetching academic sessions:', error);
        Swal.fire('Error', 'Failed to fetch academic sessions', 'error');
      } finally {
        this.loading = false;
      }
    },

    // Fetch courses
    async fetchCourses() {
      this.loading = true;
      try {
        // Fetch program courses which include course details
        const response = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/program-courses`, {
        });

        if (response.data) {
          // Transform the data to match the expected format
          this.courses = response.data.map(programCourse => {
            // Extract nested structure data
            const structure = programCourse.program && programCourse.program.structure ? programCourse.program.structure : null;
            const department = programCourse.program && programCourse.program.department ? programCourse.program.department : null;
            const school = department && department.school ? department.school : null;

            let lecturerDisplay = 'Not assigned';
            let lecturerIds = [];

            if (Array.isArray(programCourse.lecturer) && programCourse.lecturer.length) {
              lecturerDisplay = programCourse.lecturer
                .map(lec => `${lec.userId?.firstName || ''} ${lec.userId?.lastName || ''}`.trim())
                .join(', ');
              lecturerIds = programCourse.lecturer.map(lec => lec._id);
            }

            // Build program display name: "ND FT Computer Science"
            let programDisplay = 'N/A';
            if (programCourse.program && programCourse.program.program) {
              const programName = programCourse.program.program.name;
              const programType = structure && structure.type ? structure.type.type : '';
              const programMode = structure && structure.mode ? structure.mode.mode.toUpperCase() : '';

              // Combine type + mode + program name
              const parts = [];
              if (programType) parts.push(programType);
              if (programMode) parts.push(programMode);
              if (programName) parts.push(programName);

              programDisplay = parts.join(' ');
            }

            return {
              _id: programCourse._id,
              code: programCourse.course ? programCourse.course.code : 'N/A',
              title: programCourse.course ? programCourse.course.title : 'N/A',
              description: programCourse.course ? programCourse.course.description : '',
              units: programCourse.units,
              hours: programCourse.hours,
              program: programDisplay,
              programOfferingId: programCourse.program ? programCourse.program._id : null,
              department: department ? department.name : 'N/A',
              departmentId: department ? department._id : null,
              schoolId: school ? school._id : null,
              schoolName: school ? school.name : 'N/A',
              programTypeId: structure && structure.type ? structure.type._id : null,
              programTypeName: structure && structure.type ? structure.type.type : 'N/A',
              programModeId: structure && structure.mode ? structure.mode._id : null,
              programModeName: structure && structure.mode ? structure.mode.mode : 'N/A',
              year: programCourse.year,
              semester: programCourse.semester,
              category: programCourse.category || '',
              lecturer: lecturerDisplay,
              lecturerIds: lecturerIds, // Array of lecturer IDs
              session: programCourse.session ? programCourse.session.sessionYear : 'N/A',
              sessionId: programCourse.session ? programCourse.session._id : null,
              active: programCourse.active,
              courseId: programCourse.course ? programCourse.course._id : null,
              // Store structure for year/semester options
              structureYears: structure && structure.years ? structure.years : []
            };
          });
          this.pagination.totalItems = this.courses.length;
        } else {
          Swal.fire('Error', 'Failed to fetch courses', 'error');
        }
      } catch (error) {
        this.logger.error('Error fetching courses:', error);
        Swal.fire('Error', 'Failed to fetch courses', 'error');
      } finally {
        this.loading = false;
      }
    },

    // Fetch schools
    async fetchSchools() {
      try {
        const response = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/schools`);
        this.schools = response.data.data ? response.data.data.schools : response.data;
      } catch (error) {
        this.logger.error('Error fetching schools:', error);
        Swal.fire('Error', 'Failed to fetch schools data', 'error');
      }
    },

    // Fetch departments by school ID
    async fetchDepartmentsBySchool(schoolId) {
      try {
        this.loading = true;
        const response = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/departments/school/${schoolId}`);
        this.departments = response.data;
        this.loading = false;
        return response.data;
      } catch (error) {
        this.logger.error('Error fetching departments:', error);
        this.loading = false;
        return [];
      }
    },

    // Fetch programs by department ID
    async fetchProgramsByDepartment(departmentId) {
      try {
        this.loading = true;
        const response = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/program-offerings/department/${departmentId}`);

        // Group offerings by program
        const programMap = {};

        response.data.forEach(offering => {
          if (offering.program && offering.program._id) {
            const programId = offering.program._id;

            if (!programMap[programId]) {
              // Create a new program entry with offerings array
              programMap[programId] = {
                _id: programId,
                name: offering.program.name,
                code: offering.program.code,
                description: offering.program.description,
                department: offering.department, // Store department from offering
                offerings: [offering]
              };
            } else {
              // Add to existing program's offerings
              programMap[programId].offerings.push(offering);
            }
          }
        });

        // Convert map to array
        this.programs = Object.values(programMap);
        this.loading = false;
        return this.programs;
      } catch (error) {
        this.logger.error('Error fetching program offerings by department:', error);
        this.loading = false;
        return [];
      }
    },

    // Fetch all programs with their offerings
    async fetchPrograms() {
      this.loading = true;
      try {
        // Fetch all program offerings
        const offeringsResponse = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/program-offerings`,
        );

        if (offeringsResponse.data) {
          // Group offerings by program
          const programMap = {};

          offeringsResponse.data.forEach(offering => {
            if (offering.program && offering.program._id) {
              const programId = offering.program._id;

              if (!programMap[programId]) {
                // Create a new program entry with offerings array
                programMap[programId] = {
                  _id: programId,
                  name: offering.program.name,
                  code: offering.program.code,
                  description: offering.program.description,
                  department: offering.department, // Store department from offering
                  offerings: [offering]
                };
              } else {
                // Add to existing program's offerings
                programMap[programId].offerings.push(offering);
              }
            }
          });

          // Convert map to array
          this.programs = Object.values(programMap);
          this.pagination.totalItems = this.programs.length;
        } else {
          Swal.fire('Error', 'Failed to fetch program offerings', 'error');
        }
      } catch (error) {
        this.logger.error('Error fetching program offerings:', error);
        Swal.fire('Error', 'Failed to fetch program offerings', 'error');
      } finally {
        this.loading = false;
      }
    },

    // Fetch all departments
    async fetchAllDepartments() {
      this.loading = true;
      try {
        const response = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/departments`);
        // Headers not needed - cookies are automatic

        if (response.data) {
          this.departments = response.data;
          this.pagination.totalItems = this.departments.length;
        } else {
          Swal.fire('Error', 'Failed to fetch departments', 'error');
        }
      } catch (error) {
        this.logger.error('Error fetching departments:', error);
        Swal.fire('Error', 'Failed to fetch departments', 'error');
      } finally {
        this.loading = false;
      }
    },

    // Fetch program types
    async fetchProgramTypes() {
      this.loading = true;
      try {
        const response = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/programtypes`);

        if (response.data) {
          this.programTypes = response.data;
          this.pagination.totalItems = this.programTypes.length;
        } else {
          Swal.fire('Error', 'Failed to fetch program types', 'error');
        }
      } catch (error) {
        this.logger.error('Error fetching program types:', error);
        Swal.fire('Error', 'Failed to fetch program types', 'error');
      } finally {
        this.loading = false;
      }
    },

    // Fetch program modes
    async fetchProgramModes() {
      this.loading = true;
      try {
        const response = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/programmodes`);
        // Headers not needed - cookies are automatic

        if (response.data) {
          this.programModes = response.data;
          this.pagination.totalItems = this.programModes.length;
        } else {
          Swal.fire('Error', 'Failed to fetch program modes', 'error');
        }
      } catch (error) {
        this.logger.error('Error fetching program modes:', error);
        Swal.fire('Error', 'Failed to fetch program modes', 'error');
      } finally {
        this.loading = false;
      }
    },

    // Generate session year based on start and end dates
    generateSessionYear() {
      if (this.newAcademicSession.startDate && this.newAcademicSession.endDate) {
        const startYear = new Date(this.newAcademicSession.startDate).getFullYear();
        const endYear = new Date(this.newAcademicSession.endDate).getFullYear();
        this.newAcademicSession.sessionYear = `${startYear}/${endYear}`;
      }
    },

    // Add new program type
    openAddProgramTypeModal() {
      this.editMode = false;
      this.newProgramType = {
        type: "",
        description: ""
      };

      Swal.fire({
        title: 'Add New Program Type',
        html: `
          <form id="addProgramTypeForm" class="text-start">
            <div class="mb-3">
              <label class="form-label">Type Code <span class="text-danger">*</span></label>
              <input type="text" id="type" class="form-control" placeholder="e.g. ND" maxlength="10" required />
            </div>
            <div class="mb-3">
              <label class="form-label">Description <span class="text-danger">*</span></label>
              <input type="text" id="description" class="form-control" placeholder="e.g. National Diploma" required />
            </div>
          </form>
        `,
        width: '600px',
        showCancelButton: true,
        confirmButtonText: 'Add Program Type',
        preConfirm: () => {
          const type = document.getElementById('type').value;
          const description = document.getElementById('description').value;

          if (!type || !description) {
            Swal.showValidationMessage('Please fill all required fields');
            return false;
          }

          return {
            type,
            description
          };
        }
      }).then((result) => {
        if (result.isConfirmed) {
          this.createProgramType(result.value);
        }
      });
    },

    // Create new program type
    async createProgramType(programTypeData) {
      try {
        this.loading = true;
        const response = await axios.post(
          `${process.env.VUE_APP_API_BASE_URL}/programtypes`,
          {
            type: programTypeData.type,
            description: programTypeData.description
          },
          // Headers not needed - cookies are automatic
        );

        if (response.data) {
          Swal.fire('Success', 'Program type added successfully', 'success');
          this.fetchProgramTypes();
        } else {
          Swal.fire('Error', 'Failed to add program type', 'error');
        }
      } catch (error) {
        this.logger.error('Error creating program type:', error);
        Swal.fire('Error', error.response?.data?.msg || 'Failed to add program type', 'error');
      } finally {
        this.loading = false;
      }
    },

    // Edit program type
    editProgramType(programType) {
      this.editMode = true;
      this.editingItem = { ...programType };

      Swal.fire({
        title: 'Edit Program Type',
        html: `
          <form id="editProgramTypeForm" class="text-start">
            <div class="mb-3">
              <label class="form-label">Type Code <span class="text-danger">*</span></label>
              <input type="text" id="edit-type" class="form-control" value="${programType.type}" placeholder="e.g. ND" maxlength="10" required />
            </div>
            <div class="mb-3">
              <label class="form-label">Description <span class="text-danger">*</span></label>
              <input type="text" id="edit-description" class="form-control" value="${programType.description}" placeholder="e.g. National Diploma" required />
            </div>
          </form>
        `,
        width: '600px',
        showCancelButton: true,
        confirmButtonText: 'Update Program Type',
        preConfirm: () => {
          const type = document.getElementById('edit-type').value;
          const description = document.getElementById('edit-description').value;

          if (!type || !description) {
            Swal.showValidationMessage('Please fill all required fields');
            return false;
          }

          return {
            _id: programType._id,
            type,
            description
          };
        }
      }).then((result) => {
        if (result.isConfirmed) {
          this.updateProgramType(result.value);
        }
      });
    },

    // Update program type
    async updateProgramType(programTypeData) {
      try {
        this.loading = true;
        const response = await axios.put(
          `${process.env.VUE_APP_API_BASE_URL}/programtypes/${programTypeData._id}`,
          {
            type: programTypeData.type,
            description: programTypeData.description
          },
          // Headers not needed - cookies are automatic
        );

        if (response.data) {
          Swal.fire('Success', 'Program type updated successfully', 'success');
          this.fetchProgramTypes();
        } else {
          Swal.fire('Error', 'Failed to update program type', 'error');
        }
      } catch (error) {
        this.logger.error('Error updating program type:', error);
        Swal.fire('Error', error.response?.data?.msg || 'Failed to update program type', 'error');
      } finally {
        this.loading = false;
        this.editMode = false;
        this.editingItem = null;
      }
    },
    // Delete program type
    async deleteProgramType(programTypeId) {
      try {
        const result = await Swal.fire({
          title: 'Are you sure?',
          text: "This will permanently remove the program type from the system. You won't be able to recover it!",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#d33',
          cancelButtonColor: '#3085d6',
          confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
          this.loading = true;
          const response = await axios.delete(
            `${process.env.VUE_APP_API_BASE_URL}/programtypes/${programTypeId}`,
            // Headers not needed - cookies are automatic
          );

          if (response.data) {
            Swal.fire('Deleted!', 'Program type has been permanently deleted.', 'success');
            this.fetchProgramTypes();
          } else {
            Swal.fire('Error', 'Failed to delete program type', 'error');
          }
        }
      } catch (error) {
        this.logger.error('Error deleting program type:', error);
        Swal.fire('Error', error.response?.data?.msg || 'Failed to delete program type', 'error');
      } finally {
        this.loading = false;
      }
    },

    // Toggle program type active status
    async toggleProgramTypeStatus(programType) {
      try {
        this.loading = true;
        const response = await axios.put(
          `${process.env.VUE_APP_API_BASE_URL}/programtypes/${programType._id}/toggle-status`,
          {},
          // Headers not needed - cookies are automatic
        );

        if (response.data) {
          const status = response.data.programType.active ? 'activated' : 'deactivated';
          Swal.fire('Success', `Program type has been ${status}.`, 'success');
          this.fetchProgramTypes();
        } else {
          Swal.fire('Error', 'Failed to update program type status', 'error');
        }
      } catch (error) {
        this.logger.error('Error updating program type status:', error);
        Swal.fire('Error', error.response?.data?.msg || 'Failed to update program type status', 'error');
      } finally {
        this.loading = false;
      }
    },

    // Add new program mode
    openAddProgramModeModal() {
      this.editMode = false;
      this.newProgramMode = {
        mode: "",
        description: ""
      };

      Swal.fire({
        title: 'Add New Program Mode',
        html: `
          <form id="addProgramModeForm" class="text-start">
            <div class="mb-3">
              <label class="form-label">Mode Code <span class="text-danger">*</span></label>
              <input type="text" id="mode" class="form-control" placeholder="e.g. FT" maxlength="5" required />
            </div>
            <div class="mb-3">
              <label class="form-label">Description <span class="text-danger">*</span></label>
              <input type="text" id="description" class="form-control" placeholder="e.g. Full Time" required />
            </div>
          </form>
        `,
        width: '500px',
        showCancelButton: true,
        confirmButtonText: 'Add Program Mode',
        preConfirm: () => {
          const mode = document.getElementById('mode').value;
          const description = document.getElementById('description').value;

          if (!mode || !description) {
            Swal.showValidationMessage('Please fill all required fields');
            return false;
          }

          return {
            mode,
            description
          };
        }
      }).then((result) => {
        if (result.isConfirmed) {
          this.createProgramMode(result.value);
        }
      });
    },

    // Create new program mode
    async createProgramMode(programModeData) {
      try {
        this.loading = true;
        const response = await axios.post(
          `${process.env.VUE_APP_API_BASE_URL}/programmodes`,
          programModeData,
          // Headers not needed - cookies are automatic
        );

        if (response.data) {
          Swal.fire('Success', 'Program mode added successfully', 'success');
          this.fetchProgramModes();
        } else {
          Swal.fire('Error', 'Failed to add program mode', 'error');
        }
      } catch (error) {
        this.logger.error('Error creating program mode:', error);
        Swal.fire('Error', error.response?.data?.msg || 'Failed to add program mode', 'error');
      } finally {
        this.loading = false;
      }
    },

    // Edit program mode
    editProgramMode(programMode) {
      this.editMode = true;
      this.editingItem = { ...programMode };

      Swal.fire({
        title: 'Edit Program Mode',
        html: `
          <form id="editProgramModeForm" class="text-start">
            <div class="mb-3">
              <label class="form-label">Mode Code <span class="text-danger">*</span></label>
              <input type="text" id="edit-mode" class="form-control" value="${programMode.mode}" placeholder="e.g. FT" maxlength="5" required />
            </div>
            <div class="mb-3">
              <label class="form-label">Description <span class="text-danger">*</span></label>
              <input type="text" id="edit-description" class="form-control" value="${programMode.description}" placeholder="e.g. Full Time" required />
            </div>
          </form>
        `,
        width: '500px',
        showCancelButton: true,
        confirmButtonText: 'Update Program Mode',
        preConfirm: () => {
          const mode = document.getElementById('edit-mode').value;
          const description = document.getElementById('edit-description').value;

          if (!mode || !description) {
            Swal.showValidationMessage('Please fill all required fields');
            return false;
          }

          return {
            _id: programMode._id,
            mode,
            description
          };
        }
      }).then((result) => {
        if (result.isConfirmed) {
          this.updateProgramMode(result.value);
        }
      });
    },

    // Update program mode
    async updateProgramMode(programModeData) {
      try {
        this.loading = true;
        const response = await axios.put(
          `${process.env.VUE_APP_API_BASE_URL}/programmodes/${programModeData._id}`,
          programModeData
        );
        // Headers not needed - cookies are automatic

        if (response.data) {
          Swal.fire('Success', 'Program mode updated successfully', 'success');
          this.fetchProgramModes();
        } else {
          Swal.fire('Error', 'Failed to update program mode', 'error');
        }
      } catch (error) {
        this.logger.error('Error updating program mode:', error);
        Swal.fire('Error', error.response?.data?.msg || 'Failed to update program mode', 'error');
      } finally {
        this.loading = false;
        this.editMode = false;
        this.editingItem = null;
      }
    },

    // Delete program mode
    async deleteProgramMode(programModeId) {
      try {
        const result = await Swal.fire({
          title: 'Are you sure?',
          text: "This will permanently remove the program mode from the system. You won't be able to recover it!",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#d33',
          cancelButtonColor: '#3085d6',
          confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
          this.loading = true;
          const response = await axios.delete(
            `${process.env.VUE_APP_API_BASE_URL}/programmodes/${programModeId}`
          );
          // Headers not needed - cookies are automatic

          if (response.data) {
            Swal.fire('Deleted!', 'Program mode has been permanently deleted.', 'success');
            this.fetchProgramModes();
          } else {
            Swal.fire('Error', 'Failed to delete program mode', 'error');
          }
        }
      } catch (error) {
        this.logger.error('Error deleting program mode:', error);
        Swal.fire('Error', error.response?.data?.msg || 'Failed to delete program mode', 'error');
      } finally {
        this.loading = false;
      }
    },

    // Toggle program mode active status
    async toggleProgramModeStatus(programMode) {
      try {
        this.loading = true;
        const response = await axios.put(
          `${process.env.VUE_APP_API_BASE_URL}/programmodes/${programMode._id}/toggle-status`,
          {}
        );
        // Headers not needed - cookies are automatic

        if (response.data) {
          const status = response.data.programMode.active ? 'activated' : 'deactivated';
          Swal.fire('Success', `Program mode has been ${status}.`, 'success');
          this.fetchProgramModes();
        } else {
          Swal.fire('Error', 'Failed to update program mode status', 'error');
        }
      } catch (error) {
        this.logger.error('Error updating program mode status:', error);
        Swal.fire('Error', error.response?.data?.msg || 'Failed to update program mode status', 'error');
      } finally {
        this.loading = false;
      }
    },

    // Add new academic session
    openAddSessionModal() {
      this.editMode = false;
      this.newAcademicSession = {
        sessionYear: "",
        startDate: "",
        endDate: "",
        status: "open",
        description: ""
      };

      // Check if there's already an open session
      const hasOpenSession = this.academicSessions.some(session => session.status === 'open');

      Swal.fire({
        title: 'Add New Academic Session',
        html: `
          <form id="addSessionForm" class="text-start">
            <div class="mb-3">
              <label class="form-label">Session Year <span class="text-danger">*</span></label>
              <input type="text" id="sessionYear" class="form-control" placeholder="e.g. 2023/2024" required readonly />
            </div>
            <div class="mb-3">
              <label class="form-label">Start Date <span class="text-danger">*</span></label>
              <input type="date" id="startDate" class="form-control" required onchange="updateEndDateMin(); generateSessionYear()" />
            </div>
            <div class="mb-3">
              <label class="form-label">End Date <span class="text-danger">*</span></label>
              <input type="date" id="endDate" class="form-control" required onchange="generateSessionYear()" />
            </div>
            <div class="mb-3">
              <label class="form-label">Status</label>
              <select id="status" class="form-select">
                <option value="open">Open</option>
                <option value="closed">Closed</option>
                <option value="archived">Archived</option>
              </select>
            </div>
            ${hasOpenSession ? `
            <div class="alert alert-warning mb-3">
              <i class="bi bi-exclamation-triangle-fill me-2"></i>
              <small>Note: There is already an open academic session. If you set this session to "Open", the existing open session will be automatically archived.</small>
            </div>` : ''}
            <div class="mb-3">
              <label class="form-label">Description</label>
              <textarea id="description" class="form-control" rows="3"></textarea>
            </div>
          </form>
        `,
        showCancelButton: true,
        confirmButtonText: 'Add Session',
        didOpen: () => {
          // Function to update end date minimum value based on start date
          window.updateEndDateMin = () => {
            const startDateEl = document.getElementById('startDate');
            const endDateEl = document.getElementById('endDate');

            if (startDateEl.value) {
              // Set minimum date for end date input to be the start date
              endDateEl.min = startDateEl.value;

              // If end date is already selected and is before start date, reset it
              if (endDateEl.value && endDateEl.value < startDateEl.value) {
                endDateEl.value = startDateEl.value;
                // Trigger the session year generation
                window.generateSessionYear();
              }
            }
          };

          // Add function to generate session year based on dates
          window.generateSessionYear = () => {
            const startDateEl = document.getElementById('startDate');
            const endDateEl = document.getElementById('endDate');
            const sessionYearEl = document.getElementById('sessionYear');

            if (startDateEl.value && endDateEl.value) {
              const startYear = new Date(startDateEl.value).getFullYear();
              const endYear = new Date(endDateEl.value).getFullYear();
              sessionYearEl.value = `${startYear}/${endYear}`;
            }
          };

          // Initialize end date min value
          window.updateEndDateMin();
        },
        preConfirm: () => {
          const sessionYear = document.getElementById('sessionYear').value;
          const startDate = document.getElementById('startDate').value;
          const endDate = document.getElementById('endDate').value;
          const status = document.getElementById('status').value;
          const description = document.getElementById('description').value;

          if (!sessionYear || !startDate || !endDate) {
            Swal.showValidationMessage('Please fill all required fields');
            return false;
          }

          return {
            sessionYear,
            startDate,
            endDate,
            status,
            description
          };
        }
      }).then((result) => {
        if (result.isConfirmed) {
          this.createAcademicSession(result.value);
        }
      });
    },

    // Create new academic session
    async createAcademicSession(sessionData) {
      try {
        this.loading = true;

        // Validate required fields
        if (!sessionData.sessionYear || !sessionData.startDate || !sessionData.endDate) {
          Swal.fire('Validation Error', 'Session year, start date, and end date are required', 'error');
          return;
        }

        // Check for duplicate session year before submitting
        const isDuplicate = this.academicSessions.some(
          session => session.sessionYear === sessionData.sessionYear
        );

        if (isDuplicate) {
          Swal.fire('Validation Error', 'Duplicate session year. Please use a unique session year.', 'error');
          return;
        }

        // Check if there's an open session and the new session is set to open
        const hasOpenSession = this.academicSessions.some(session => session.status === 'open');
        let statusChanged = false;

        if (hasOpenSession && sessionData.status === 'open') {
          // Inform the user that the existing open session will be archived
          statusChanged = true;
        }

        const response = await axios.post(
          `${process.env.VUE_APP_API_BASE_URL}/academic-sessions`,
          sessionData
          // Headers not needed - cookies are automatic
        );

        // Check if the response status is 201 (Created)
        if (response.status === 201) {
          let successMessage = 'Academic session added successfully';
          if (statusChanged && response.data.status === 'archived') {
            successMessage = 'Academic session added with status "archived" because another session is already open';
          }

          Swal.fire('Success', successMessage, 'success');
          this.fetchAcademicSessions();
          // Reset the form
          this.newAcademicSession = {
            sessionYear: "",
            startDate: "",
            endDate: "",
            status: "open",
            description: ""
          };
        } else {
          Swal.fire('Error', response.data.message || 'Failed to add academic session', 'error');
        }
      } catch (error) {
        this.logger.error('Error creating academic session:', error);
        Swal.fire('Error', error.response?.data?.message || 'Failed to add academic session', 'error');
      } finally {
        this.loading = false;
      }
    },

    async deleteAcademicSession(sessionId) {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: 'This will delete the academic session!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!'
      });

      if (result.isConfirmed) {
        try {
          await axios.delete(`${process.env.VUE_APP_API_BASE_URL}/academic-sessions/${sessionId}`);
          // Headers not needed - cookies are automatic
          await this.fetchAcademicSessions();

          Swal.fire(
            'Deleted!',
            'The academic session has been deleted.',
            'success'
          );
        } catch (err) {
          this.error = 'Failed to delete academic session';
          this.logger.error(err);

          Swal.fire(
            'Error!',
            'Failed to delete academic session.',
            'error'
          );
        }
      }
    },
    // Edit academic session
    editSession(session) {
      this.editMode = true;

      // Check if there's already an open session (excluding the current session)
      const hasOpenSession = this.academicSessions.some(s =>
        s.status === 'open' && s._id !== session._id
      );

      Swal.fire({
        title: 'Edit Academic Session',
        html: `
          <form id="editSessionForm" class="text-start">
            <div class="mb-3">
              <label class="form-label">Session Year <span class="text-danger">*</span></label>
              <input type="text" id="sessionYear" class="form-control" value="${session.sessionYear || ''}" placeholder="e.g. 2023/2024" required readonly />
            </div>
            <div class="mb-3">
              <label class="form-label">Start Date <span class="text-danger">*</span></label>
              <input type="date" id="startDate" class="form-control" value="${session.startDate ? session.startDate.substring(0, 10) : ''}" required onchange="updateEndDateMin(); generateSessionYear()" />
            </div>
            <div class="mb-3">
              <label class="form-label">End Date <span class="text-danger">*</span></label>
              <input type="date" id="endDate" class="form-control" value="${session.endDate ? session.endDate.substring(0, 10) : ''}" required onchange="generateSessionYear()" />
            </div>
            <div class="mb-3">
              <label class="form-label">Status</label>
              <select id="status" class="form-select">
                <option value="open" ${session.status === 'open' ? 'selected' : ''}>Open</option>
                <option value="closed" ${session.status === 'closed' ? 'selected' : ''}>Closed</option>
                <option value="archived" ${session.status === 'archived' ? 'selected' : ''}>Archived</option>
              </select>
            </div>
            ${hasOpenSession && session.status !== 'open' ? `
            <div class="alert alert-warning mb-3">
              <i class="bi bi-exclamation-triangle-fill me-2"></i>
              <small>Note: There is already an open academic session. If you change this session's status to "Open", the existing open session will be automatically archived.</small>
            </div>` : ''}
            <div class="mb-3">
              <label class="form-label">Description</label>
              <textarea id="description" class="form-control" rows="3">${session.description || ''}</textarea>
            </div>
          </form>
        `,
        showCancelButton: true,
        confirmButtonText: 'Update Session',
        didOpen: () => {
          // Function to update end date minimum value based on start date
          window.updateEndDateMin = () => {
            const startDateEl = document.getElementById('startDate');
            const endDateEl = document.getElementById('endDate');

            if (startDateEl.value) {
              // Set minimum date for end date input to be the start date
              endDateEl.min = startDateEl.value;

              // If end date is already selected and is before start date, reset it
              if (endDateEl.value && endDateEl.value < startDateEl.value) {
                endDateEl.value = startDateEl.value;
                // Trigger the session year generation
                window.generateSessionYear();
              }
            }
          };

          // Add function to generate session year based on dates
          window.generateSessionYear = () => {
            const startDateEl = document.getElementById('startDate');
            const endDateEl = document.getElementById('endDate');
            const sessionYearEl = document.getElementById('sessionYear');

            if (startDateEl.value && endDateEl.value) {
              const startYear = new Date(startDateEl.value).getFullYear();
              const endYear = new Date(endDateEl.value).getFullYear();
              sessionYearEl.value = `${startYear}/${endYear}`;
            }
          };

          // Initialize end date min value
          window.updateEndDateMin();

          // Generate session year if needed
          if (!document.getElementById('sessionYear').value &&
            document.getElementById('startDate').value &&
            document.getElementById('endDate').value) {
            window.generateSessionYear();
          }
        },
        preConfirm: () => {
          const sessionYear = document.getElementById('sessionYear').value;
          const startDate = document.getElementById('startDate').value;
          const endDate = document.getElementById('endDate').value;
          const status = document.getElementById('status').value;
          const description = document.getElementById('description').value;

          if (!sessionYear || !startDate || !endDate) {
            Swal.showValidationMessage('Please fill all required fields');
            return false;
          }

          return {
            _id: session._id,
            sessionYear,
            startDate,
            endDate,
            status,
            description
          };
        }
      }).then((result) => {
        if (result.isConfirmed) {
          this.updateAcademicSession(result.value);
        }
      });
    },

    // Update academic session
    async updateAcademicSession(sessionData) {
      try {
        this.loading = true;

        // Validate required fields
        if (!sessionData.sessionYear || !sessionData.startDate || !sessionData.endDate) {
          Swal.fire('Validation Error', 'Session year, start date, and end date are required', 'error');
          return;
        }

        // Remove any fields that might be null to prevent duplicate key errors
        const cleanedData = {};
        Object.keys(sessionData).forEach(key => {
          if (sessionData[key] !== null && sessionData[key] !== undefined) {
            cleanedData[key] = sessionData[key];
          }
        });

        // Automatic active management based on status
        // "open" or "running" -> active remains as sent
        // "closed" or "archived" -> force active to false
        if (cleanedData.status && (cleanedData.status === 'closed' || cleanedData.status === 'archived')) {
          cleanedData.active = false;
        }

        // Find the current session to check if status is changing to open
        const currentSession = this.academicSessions.find(s => s._id === sessionData._id);
        const changingToOpen = currentSession && currentSession.status !== 'open' && sessionData.status === 'open';
        const changingFromOpen = currentSession && currentSession.status === 'open' && sessionData.status !== 'open';

        // Check if there's another open session
        const hasAnotherOpenSession = this.academicSessions.some(
          s => s.status === 'open' && s._id !== sessionData._id
        );

        let statusChangeInfo = '';
        if (changingToOpen && hasAnotherOpenSession) {
          statusChangeInfo = 'The existing open session has been archived.';
        }
        if (changingFromOpen) {
          statusChangeInfo = 'Session has been set to inactive since status is no longer "open".';
        }

        const response = await axios.put(
          `${process.env.VUE_APP_API_BASE_URL}/academic-sessions/${sessionData._id}`,
          cleanedData
          // Headers not needed - cookies are automatic
        );

        // Check if the response status is 200 (OK)
        if (response.status === 200) {
          let successMessage = 'Academic session updated successfully';
          if (statusChangeInfo) {
            successMessage += '. ' + statusChangeInfo;
          }

          Swal.fire('Success', successMessage, 'success');
          this.fetchAcademicSessions();
        } else {
          Swal.fire('Error', response.data.message || 'Failed to update academic session', 'error');
        }
      } catch (error) {
        this.logger.error('Error updating academic session:', error);
        Swal.fire('Error', error.response?.data?.message || 'Failed to update academic session', 'error');
      } finally {
        this.loading = false;
      }
    },

    async changeSessionStatus(session, newStatus) {
      try {
        // Prepare updated session data
        const updatedSession = {
          ...session,
          status: newStatus
        };

        // Manage active flag based on status
        // "open" -> set active to true (activate the session)
        // "running" -> keep active unchanged (session remains active)
        // "closed" or "archived" -> set active to false (deactivate)
        if (newStatus === 'open') {
          updatedSession.active = true;
        } else if (newStatus === 'running') {
          // Keep the current active state - don't change it
          updatedSession.active = session.active;
        } else {
          // closed or archived - deactivate
          updatedSession.active = false;
        }

        const response = await axios.put(
          `${process.env.VUE_APP_API_BASE_URL}/academic-sessions/${session._id}`,
          updatedSession
          // Headers not needed - cookies are automatic
        );

        if (response.status === 200) {
          await this.fetchAcademicSessions();

          let statusMessage = '';
          if (newStatus === 'open') {
            statusMessage = 'Academic session opened successfully and set to active';
          } else if (newStatus === 'running') {
            statusMessage = 'Academic session set to running';
          } else if (newStatus === 'closed') {
            statusMessage = 'Academic session closed successfully and set to inactive';
          } else if (newStatus === 'archived') {
            statusMessage = 'Academic session archived successfully and set to inactive';
          }

          Swal.fire({
            title: 'Success!',
            text: statusMessage,
            icon: 'success',
            timer: 2000
          });
        }
      } catch (err) {
        this.error = 'Failed to update academic session status';
        this.logger.error(err);

        Swal.fire({
          title: 'Error!',
          text: 'Failed to update academic session status',
          icon: 'error'
        });
      }
    },

    // Add new course
    openAddCourseModal() {
      this.editMode = false;
      this.newCourse = {
        code: "",
        title: "",
        description: "",
        units: 0,
        hours: 0,
        session: "",
        department: "",
        programType: "",
        programMode: "",
        program: "",
        year: "",
        semester: "",
        lecturer: ""
      };

      Swal.fire({
        title: 'Add New Course',
        html: `
          <form id="addCourseForm" class="text-start">
            <div class="row mb-3">
              <div class="col-md-3">
                <label class="form-label">Academic Session <span class="text-danger">*</span></label>
                <select id="session" class="form-select" required>
                  <option value="" selected disabled>Select academic session</option>
                </select>
              </div>
              <div class="col-md-3">
                <label class="form-label">School <span class="text-danger">*</span></label>
                <select id="school" class="form-select" required>
                  <option value="" selected disabled>Select School</option>
                  ${(this.schools || []).filter(school => school && school._id).map(school => `<option value="${school._id}">${school.name}</option>`).join('')}
                </select>
              </div>
              <div class="col-md-6">
                <label class="form-label">Department <span class="text-danger">*</span></label>
                <select id="department" class="form-select" required disabled>
                  <option value="" selected disabled>Select Department</option>
                </select>
              </div>
            </div>
            <div class="row mb-3">
              <div class="col-md-3">
                <label class="form-label">Program Type <span class="text-danger">*</span></label>
                <select id="programType" class="form-select" required disabled>
                  <option value="" selected disabled>Select Program Type</option>
                </select>
              </div>
              <div class="col-md-3">
                <label class="form-label">Program Mode <span class="text-danger">*</span></label>
                <select id="programMode" class="form-select" required disabled>
                  <option value="" selected disabled>Select Program Mode</option>
                </select>
              </div>
              <div class="col-md-6">
                <label class="form-label">Program <span class="text-danger">*</span></label>
                <select id="program" class="form-select" required disabled>
                  <option value="" selected disabled>Select Program</option>
                </select>
              </div>
            </div>
            <div class="row mb-3">
              <div class="col-md-3">
                <label class="form-label">Course Code <span class="text-danger">*</span></label>
                <input type="text" id="code" class="form-control" placeholder="e.g. CSC101" required />
              </div>
              <div class="col-md-5">
                <label class="form-label">Course Title <span class="text-danger">*</span></label>
                <input type="text" id="title" class="form-control" placeholder="e.g. Introduction to Computing" required />
              </div>
              <div class="col-md-2">
                <label class="form-label">Units <span class="text-danger">*</span></label>
                <input type="number" id="units" class="form-control" min="1" max="6" required />
              </div>
              <div class="col-md-2">
                <label class="form-label">Hours <span class="text-danger">*</span></label>
                <input type="number" id="hours" class="form-control" min="1" required />
              </div>
            </div>
            <div class="row mb-3">
              <div class="col-md-3">
                <label class="form-label">Year <span class="text-danger">*</span></label>
                <select id="year" class="form-select" required disabled>
                  <option value="" selected disabled>Select Year</option>
                </select>
              </div>
              <div class="col-md-3">
                <label class="form-label">Semester <span class="text-danger">*</span></label>
                <select id="semester" class="form-select" required disabled>
                  <option value="" selected disabled>Select Semester</option>
                </select>
              </div>
              <div class="col-md-6">
                <label class="form-label">Lecturer <span class="text-danger">*</span></label>
                <select id="lecturer" class="form-select" required>
                  <option value="" selected disabled>Select Lecturer</option>
                </select>
              </div>
            </div>
            <div class="mb-3">
              <label class="form-label">Description</label>
              <textarea id="description" class="form-control" rows="3"></textarea>
            </div>
          </form>
        `,
        width: '800px',
        showCancelButton: true,
        confirmButtonText: 'Add Course',
        didOpen: async () => {
          // Populate academic sessions dropdown
          const sessionSelect = document.getElementById('session');
          try {
            const response = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/academic-sessions`);
            // Headers not needed - cookies are automatic
            if (response.data && response.data.length > 0) {
              response.data.forEach(session => {
                const option = document.createElement('option');
                option.value = session._id;
                option.textContent = session.sessionYear;
                sessionSelect.appendChild(option);
              });
            }
          } catch (error) {
            this.logger.error('Error fetching academic sessions:', error);
          }

          // Populate lecturers dropdown (academic staff only)
          const lecturerSelect = document.getElementById('lecturer');
          try {
            const response = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/staff?staffType=academic`);
            if (response.data && response.data.data && response.data.data.staff) {
              response.data.data.staff.forEach(staff => {
                const option = document.createElement('option');
                option.value = staff._id;
                option.textContent = `${staff.userId?.firstName || ''} ${staff.userId?.lastName || ''}`.trim();
                lecturerSelect.appendChild(option);
              });
            }
          } catch (error) {
            this.logger.error('Error fetching academic staff:', error);
          }

          // Populate program types dropdown
          const programTypeSelect = document.getElementById('programType');
          try {
            const response = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/programtypes`);
            if (response.data && response.data.length > 0) {
              programTypeSelect.innerHTML = '<option value="" selected disabled>Select Program Type</option>';
              response.data.forEach(type => {
                const option = document.createElement('option');
                option.value = type._id;
                option.textContent = type.type;
                programTypeSelect.appendChild(option);
              });
              programTypeSelect.disabled = false;
            }
          } catch (error) {
            this.logger.error('Error fetching program types:', error);
          }

          // Add event listener to school select
          const schoolSelect = document.getElementById('school');
          const departmentSelect = document.getElementById('department');

          schoolSelect.addEventListener('change', async () => {
            const schoolId = schoolSelect.value;
            if (schoolId) {
              departmentSelect.disabled = true;
              departmentSelect.innerHTML = '<option value="">Loading...</option>';

              try {
                const departments = await this.fetchDepartmentsBySchool(schoolId);

                departmentSelect.innerHTML = '<option value="" selected disabled>Select Department</option>';
                departments.forEach(dept => {
                  const option = document.createElement('option');
                  option.value = dept._id;
                  option.textContent = dept.name;
                  departmentSelect.appendChild(option);
                });

                departmentSelect.disabled = false;
              } catch (error) {
                this.logger.error('Error fetching departments:', error);
                departmentSelect.innerHTML = '<option value="" selected disabled>Error loading departments</option>';
              }
            } else {
              departmentSelect.innerHTML = '<option value="" selected disabled>Select Department</option>';
              departmentSelect.disabled = true;
            }
          });

          // Add event listener to program type select
          const programModeSelect = document.getElementById('programMode');
          programTypeSelect.addEventListener('change', async () => {
            const typeId = programTypeSelect.value;
            if (typeId) {
              programModeSelect.disabled = true;
              programModeSelect.innerHTML = '<option value="">Loading...</option>';

              try {
                const response = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/programmodes`);

                programModeSelect.innerHTML = '<option value="" selected disabled>Select Program Mode</option>';
                if (response.data && response.data.length > 0) {
                  response.data.forEach(mode => {
                    const option = document.createElement('option');
                    option.value = mode._id;
                    option.textContent = mode.mode;
                    programModeSelect.appendChild(option);
                  });
                }
                programModeSelect.disabled = false;
              } catch (error) {
                this.logger.error('Error fetching program modes:', error);
                programModeSelect.innerHTML = '<option value="" selected disabled>Error loading modes</option>';
              }
            } else {
              programModeSelect.innerHTML = '<option value="" selected disabled>Select Program Mode</option>';
              programModeSelect.disabled = true;
            }
          });

          // Add event listener to department, program type, and program mode selects
          const programSelect = document.getElementById('program');
          const updateProgramOfferings = async () => {
            const departmentId = departmentSelect.value;
            const typeId = programTypeSelect.value;
            const modeId = programModeSelect.value;
            const sessionId = sessionSelect.value;

            if (departmentId && typeId && modeId && sessionId) {
              programSelect.disabled = true;
              programSelect.innerHTML = '<option value="">Loading...</option>';

              try {
                // Fetch program offerings filtered by department, type, and mode
                const response = await axios.get(
                  `${process.env.VUE_APP_API_BASE_URL}/program-offerings/department/${departmentId}`
                );
                // Headers not needed - cookies are automatic
                programSelect.innerHTML = '<option value="" selected disabled>Select Program</option>';
                if (response.data && response.data.length > 0) {
                  // Filter program offerings by type and mode
                  const filteredOfferings = response.data.filter(offering =>
                    offering.structure &&
                    offering.structure.type &&
                    offering.structure.mode &&
                    offering.structure.type._id === typeId &&
                    offering.structure.mode._id === modeId
                  );

                  filteredOfferings.forEach(offering => {
                    const option = document.createElement('option');
                    option.value = offering._id;
                    option.textContent = offering.program ? offering.program.name : 'Unknown Program';
                    option.dataset.structure = JSON.stringify(offering.structure);
                    programSelect.appendChild(option);
                  });
                }
                programSelect.disabled = false;
              } catch (error) {
                this.logger.error('Error fetching program offerings:', error);
                programSelect.innerHTML = '<option value="" selected disabled>Error loading programs</option>';
              }
            } else {
              programSelect.innerHTML = '<option value="" selected disabled>Select Program</option>';
              programSelect.disabled = true;
            }
          };

          departmentSelect.addEventListener('change', updateProgramOfferings);
          programTypeSelect.addEventListener('change', updateProgramOfferings);
          programModeSelect.addEventListener('change', updateProgramOfferings);
          sessionSelect.addEventListener('change', updateProgramOfferings);

          // Add event listener to program select to populate years and semesters
          const yearSelect = document.getElementById('year');
          const semesterSelect = document.getElementById('semester');

          programSelect.addEventListener('change', () => {
            const selectedOption = programSelect.options[programSelect.selectedIndex];
            if (selectedOption && selectedOption.dataset.structure) {
              try {
                const structure = JSON.parse(selectedOption.dataset.structure);
                // Populate years
                yearSelect.innerHTML = '<option value="" selected disabled>Select Year</option>';
                if (structure.years && structure.years.length > 0) {
                  structure.years.forEach(yearObj => {
                    const option = document.createElement('option');
                    option.value = yearObj.year;
                    option.textContent = `Year ${yearObj.year}`;
                    option.dataset.semesters = JSON.stringify(yearObj.semesters);
                    yearSelect.appendChild(option);
                  });
                  yearSelect.disabled = false;
                } else {
                  yearSelect.disabled = true;
                }

                // Reset semesters
                semesterSelect.innerHTML = '<option value="" selected disabled>Select Semester</option>';
                semesterSelect.disabled = true;
              } catch (error) {
                this.logger.error('Error parsing structure data:', error);
              }
            } else {
              yearSelect.innerHTML = '<option value="" selected disabled>Select Year</option>';
              yearSelect.disabled = true;
              semesterSelect.innerHTML = '<option value="" selected disabled>Select Semester</option>';
              semesterSelect.disabled = true;
            }
          });

          // Add event listener to year select to populate semesters
          yearSelect.addEventListener('change', () => {
            const selectedOption = yearSelect.options[yearSelect.selectedIndex];
            if (selectedOption && selectedOption.dataset.semesters) {
              try {
                const semesters = JSON.parse(selectedOption.dataset.semesters);
                // Populate semesters
                semesterSelect.innerHTML = '<option value="" selected disabled>Select Semester</option>';
                if (semesters && semesters.length > 0) {
                  semesters.forEach(sem => {
                    const option = document.createElement('option');
                    option.value = sem;
                    option.textContent = `Semester ${sem}`;
                    semesterSelect.appendChild(option);
                  });
                  semesterSelect.disabled = false;
                } else {
                  semesterSelect.disabled = true;
                }
              } catch (error) {
                this.logger.error('Error parsing semesters data:', error);
              }
            } else {
              semesterSelect.innerHTML = '<option value="" selected disabled>Select Semester</option>';
              semesterSelect.disabled = true;
            }
          });
        },
        preConfirm: () => {
          const code = document.getElementById('code').value;
          const title = document.getElementById('title').value;
          const description = document.getElementById('description').value;
          const units = document.getElementById('units').value;
          const hours = document.getElementById('hours').value;
          const session = document.getElementById('session').value;
          const program = document.getElementById('program').value;
          const year = document.getElementById('year').value;
          const semester = document.getElementById('semester').value;
          const lecturer = document.getElementById('lecturer').value;

          if (!code || !title || !units || !hours || !session || !program || !year || !semester || !lecturer) {
            Swal.showValidationMessage('Please fill all required fields');
            return false;
          }

          return {
            code,
            title,
            description,
            units: parseInt(units),
            hours: parseInt(hours),
            session,
            program,
            year: parseInt(year),
            semester: parseInt(semester),
            lecturer
          };
        }
      }).then((result) => {
        if (result.isConfirmed) {
          this.createCourseOffering(result.value);
        }
      });
    },

    // Create new course
    async createCourse(courseData) {
      try {
        this.loading = true;
        // First, create the course record
        const courseResponse = await axios.post(
          `${process.env.VUE_APP_API_BASE_URL}/courses-new`,
          {
            code: courseData.code,
            title: courseData.title,
            description: courseData.description,
            active: true
          }
          // Headers not needed - cookies are automatic
        );

        if (courseResponse.data && courseResponse.data._id) {
          // Then, create the program course record
          const programCourseResponse = await axios.post(
            `${process.env.VUE_APP_API_BASE_URL}/program-courses`,
            {
              program: courseData.program,
              course: courseResponse.data._id,
              units: courseData.units,
              hours: courseData.hours,
              lecturer: courseData.lecturer,
              session: courseData.session,
              year: courseData.year,
              semester: courseData.semester,
              active: true
            }
            // Headers not needed - cookies are automatic
          );

          if (programCourseResponse.data) {
            Swal.fire('Success', 'Course added successfully', 'success');
            this.fetchCourses();
          } else {
            Swal.fire('Error', 'Failed to add course to program', 'error');
          }
        } else {
          Swal.fire('Error', courseResponse.data?.message || 'Failed to create course', 'error');
        }
      } catch (error) {
        this.logger.error('Error creating course:', error);
        Swal.fire('Error', error.response?.data?.message || 'Failed to add course', 'error');
      } finally {
        this.loading = false;
      }
    },

    // Create only a base course (for manage courses modal)
    async createBaseCourse(courseData) {
      try {
        this.loading = true;
        const response = await axios.post(
          `${process.env.VUE_APP_API_BASE_URL}/courses-new`,
          {
            code: courseData.code,
            title: courseData.title,
            description: courseData.description,
            active: true
          }
        );

        if (response.data && response.data._id) {
          Swal.fire('Success', 'Course created successfully', 'success');
          this.loadCoursesInModal(); // Refresh the modal table
        } else {
          Swal.fire('Error', response.data?.message || 'Failed to create course', 'error');
        }
      } catch (error) {
        this.logger.error('Error creating base course:', error);
        Swal.fire('Error', error.response?.data?.message || 'Failed to create course', 'error');
      } finally {
        this.loading = false;
      }
    },

    // Create only a program course offering (using existing course)
    async createCourseOffering(offeringData) {
      try {
        this.loading = true;
        const response = await axios.post(
          `${process.env.VUE_APP_API_BASE_URL}/program-courses`,
          {
            program: offeringData.program,
            course: offeringData.course,
            units: offeringData.units,
            hours: offeringData.hours,
            lecturer: offeringData.lecturer,
            session: offeringData.session,
            year: offeringData.year,
            semester: offeringData.semester,
            category: offeringData.category,
            active: true
          }
        );

        if (response.data) {
          Swal.fire('Success', 'Course offering added successfully', 'success');
          this.fetchCourses(); // Refresh the main course offerings table
        } else {
          Swal.fire('Error', 'Failed to add course offering', 'error');
        }
      } catch (error) {
        this.logger.error('Error creating course offering:', error);
        Swal.fire('Error', error.response?.data?.message || 'Failed to add course offering', 'error');
      } finally {
        this.loading = false;
      }
    },

    // Edit course offering
    editCourse(course) {
      this.editMode = true;
      this.editingItem = course;

      Swal.fire({
        title: 'Edit Course Offering',
        html: `
          <form id="editCourseOfferingForm" class="text-start">
            <div class="row mb-3">
              <div class="col-md-3">
                <label class="form-label">Academic Session <span class="text-danger">*</span></label>
                <select id="session" class="form-select" required>
                  <option value="" selected disabled>Select academic session</option>
                </select>
              </div>
              <div class="col-md-3">
                <label class="form-label">School <span class="text-danger">*</span></label>
                <select id="school" class="form-select" required>
                  <option value="" selected disabled>Select School</option>
                  ${(this.schools || []).filter(school => school && school._id).map(school => `<option value="${school._id}">${school.name}</option>`).join('')}
                </select>
              </div>
              <div class="col-md-6">
                <label class="form-label">Department <span class="text-danger">*</span></label>
                <select id="department" class="form-select" required disabled>
                  <option value="" selected disabled>Select Department</option>
                </select>
              </div>
            </div>
            <div class="row mb-3">
              <div class="col-md-3">
                <label class="form-label">Program Type <span class="text-danger">*</span></label>
                <select id="programType" class="form-select" required disabled>
                  <option value="" selected disabled>Select Program Type</option>
                </select>
              </div>
              <div class="col-md-3">
                <label class="form-label">Program Mode <span class="text-danger">*</span></label>
                <select id="programMode" class="form-select" required disabled>
                  <option value="" selected disabled>Select Program Mode</option>
                </select>
              </div>
              <div class="col-md-6">
                <label class="form-label">Program <span class="text-danger">*</span></label>
                <select id="program" class="form-select" required disabled>
                  <option value="" selected disabled>Select Program</option>
                </select>
              </div>
            </div>
            <div class="row mb-3">
              <div class="col-md-6">
                <label class="form-label">Course <span class="text-danger">*</span></label>
                <select id="course" class="form-select" required>
                  <option value="" selected disabled>Select Course</option>
                </select>
              </div>
              <div class="col-md-2">
                <label class="form-label">Units <span class="text-danger">*</span></label>
                <input type="number" id="units" class="form-control" value="${course.units || ''}" min="1" max="6" required />
              </div>
              <div class="col-md-2">
                <label class="form-label">Hours <span class="text-danger">*</span></label>
                <input type="number" id="hours" class="form-control" value="${course.hours || ''}" min="1" required />
              </div>
              <div class="col-md-2">
                <label class="form-label">Year <span class="text-danger">*</span></label>
                <select id="year" class="form-select" required disabled>
                  <option value="" selected disabled>Select Year</option>
                </select>
              </div>
            </div>
            <div class="row mb-3">
              <div class="col-md-3">
                <label class="form-label">Semester <span class="text-danger">*</span></label>
                <select id="semester" class="form-select" required disabled>
                  <option value="" selected disabled>Select Semester</option>
                </select>
              </div>
              <div class="col-md-3">
                <label class="form-label">Category <span class="text-danger">*</span></label>
                <select id="category" class="form-select" required>
                  <option value="" selected disabled>Select Category</option>
                  <option value="compulsory" ${course.category === 'compulsory' ? 'selected' : ''}>Compulsory</option>
                  <option value="elective" ${course.category === 'elective' ? 'selected' : ''}>Elective</option>
                </select>
              </div>
              <div class="col-md-6">
                <label class="form-label">Lecturer(s) <span class="text-danger">*</span> <small class="text-body-tertiary fw-light">At least one lecturer is required.</small></label>
                <select id="lecturer" class="form-select" multiple required style="min-height: 120px;">
                  <option value="" disabled>Select Lecturer(s)</option>
                </select>
                <small class="form-text text-muted">Hold Ctrl (Cmd on Mac) to select multiple lecturers.</small>
              </div>
            </div>
          </form>
        `,
        width: '900px',
        showCancelButton: true,
        confirmButtonText: 'Update Course Offering',
        didOpen: async () => {
          // Get all select elements
          const courseSelect = document.getElementById('course');
          const sessionSelect = document.getElementById('session');
          const lecturerSelect = document.getElementById('lecturer');
          const schoolSelect = document.getElementById('school');
          const departmentSelect = document.getElementById('department');
          const programTypeSelect = document.getElementById('programType');
          const programModeSelect = document.getElementById('programMode');
          const programSelect = document.getElementById('program');
          const yearSelect = document.getElementById('year');
          const semesterSelect = document.getElementById('semester');

          // STEP 1: Set up all event listeners first (before populating or triggering)

          // School change event
          schoolSelect.addEventListener('change', async () => {
            const schoolId = schoolSelect.value;
            if (schoolId) {
              departmentSelect.disabled = true;
              departmentSelect.innerHTML = '<option value="">Loading...</option>';

              try {
                const departments = await this.fetchDepartmentsBySchool(schoolId);

                departmentSelect.innerHTML = '<option value="" selected disabled>Select Department</option>';
                departments.forEach(dept => {
                  const option = document.createElement('option');
                  option.value = dept._id;
                  option.textContent = dept.name;
                  if (course.departmentId === dept._id) {
                    option.selected = true;
                  }
                  departmentSelect.appendChild(option);
                });

                departmentSelect.disabled = false;

                // Trigger department change if pre-selected
                if (course.departmentId) {
                  const event = new Event('change');
                  departmentSelect.dispatchEvent(event);
                }
              } catch (error) {
                this.logger.error('Error fetching departments:', error);
                departmentSelect.innerHTML = '<option value="" selected disabled>Error loading departments</option>';
              }
            } else {
              departmentSelect.innerHTML = '<option value="" selected disabled>Select Department</option>';
              departmentSelect.disabled = true;
            }
          });

          // Program Type change event
          programTypeSelect.addEventListener('change', async () => {
            const typeId = programTypeSelect.value;
            if (typeId) {
              programModeSelect.disabled = true;
              programModeSelect.innerHTML = '<option value="">Loading...</option>';

              try {
                const response = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/programmodes`);

                programModeSelect.innerHTML = '<option value="" selected disabled>Select Program Mode</option>';
                if (response.data && response.data.length > 0) {
                  response.data.forEach(mode => {
                    const option = document.createElement('option');
                    option.value = mode._id;
                    option.textContent = mode.mode;
                    if (course.programModeId === mode._id) {
                      option.selected = true;
                    }
                    programModeSelect.appendChild(option);
                  });
                }
                programModeSelect.disabled = false;

                // Trigger mode change if pre-selected
                if (course.programModeId) {
                  const event = new Event('change');
                  programModeSelect.dispatchEvent(event);
                }
              } catch (error) {
                this.logger.error('Error fetching program modes:', error);
                programModeSelect.innerHTML = '<option value="" selected disabled>Error loading modes</option>';
              }
            } else {
              programModeSelect.innerHTML = '<option value="" selected disabled>Select Program Mode</option>';
              programModeSelect.disabled = true;
            }
          });

          // Update program offerings when dependencies change
          const updateProgramOfferings = async () => {
            const departmentId = departmentSelect.value;
            const typeId = programTypeSelect.value;
            const modeId = programModeSelect.value;
            const sessionId = sessionSelect.value;

            if (departmentId && typeId && modeId && sessionId) {
              programSelect.disabled = true;
              programSelect.innerHTML = '<option value="">Loading...</option>';

              try {
                const response = await axios.get(
                  `${process.env.VUE_APP_API_BASE_URL}/program-offerings/department/${departmentId}`
                );
                programSelect.innerHTML = '<option value="" selected disabled>Select Program</option>';
                if (response.data && response.data.length > 0) {
                  const filteredOfferings = response.data.filter(offering =>
                    offering.structure &&
                    offering.structure.type &&
                    offering.structure.mode &&
                    offering.structure.type._id === typeId &&
                    offering.structure.mode._id === modeId
                  );

                  filteredOfferings.forEach(offering => {
                    const option = document.createElement('option');
                    option.value = offering._id;
                    option.textContent = offering.program ? offering.program.name : 'Unknown Program';
                    option.dataset.structure = JSON.stringify(offering.structure);
                    if (course.programOfferingId === offering._id) {
                      option.selected = true;
                    }
                    programSelect.appendChild(option);
                  });
                }
                programSelect.disabled = false;

                // Trigger change if program is pre-selected
                if (course.programOfferingId) {
                  const event = new Event('change');
                  programSelect.dispatchEvent(event);
                }
              } catch (error) {
                this.logger.error('Error fetching program offerings:', error);
                programSelect.innerHTML = '<option value="" selected disabled>Error loading programs</option>';
              }
            } else {
              programSelect.innerHTML = '<option value="" selected disabled>Select Program</option>';
              programSelect.disabled = true;
            }
          };

          departmentSelect.addEventListener('change', updateProgramOfferings);
          programTypeSelect.addEventListener('change', updateProgramOfferings);
          programModeSelect.addEventListener('change', updateProgramOfferings);
          sessionSelect.addEventListener('change', updateProgramOfferings);

          // Program select change - populate years and semesters
          programSelect.addEventListener('change', () => {
            const selectedOption = programSelect.options[programSelect.selectedIndex];
            if (selectedOption && selectedOption.dataset.structure) {
              try {
                const structure = JSON.parse(selectedOption.dataset.structure);

                // Clear semester first (before populating years and triggering year change)
                semesterSelect.innerHTML = '<option value="" selected disabled>Select Semester</option>';
                semesterSelect.disabled = true;

                yearSelect.innerHTML = '<option value="" selected disabled>Select Year</option>';
                if (structure.years && structure.years.length > 0) {
                  structure.years.forEach(yearObj => {
                    const option = document.createElement('option');
                    option.value = yearObj.year;
                    option.textContent = `Year ${yearObj.year}`;
                    option.dataset.semesters = JSON.stringify(yearObj.semesters);
                    if (course.year === yearObj.year) {
                      option.selected = true;
                    }
                    yearSelect.appendChild(option);
                  });
                  yearSelect.disabled = false;

                  // Trigger change if year is pre-selected
                  if (course.year) {
                    const event = new Event('change');
                    yearSelect.dispatchEvent(event);
                  }
                } else {
                  yearSelect.disabled = true;
                }
              } catch (error) {
                this.logger.error('Error parsing structure data:', error);
              }
            } else {
              yearSelect.innerHTML = '<option value="" selected disabled>Select Year</option>';
              yearSelect.disabled = true;
              semesterSelect.innerHTML = '<option value="" selected disabled>Select Semester</option>';
              semesterSelect.disabled = true;
            }
          });

          // Year select change - populate semesters
          yearSelect.addEventListener('change', () => {
            const selectedOption = yearSelect.options[yearSelect.selectedIndex];
            if (selectedOption && selectedOption.dataset.semesters) {
              try {
                const semesters = JSON.parse(selectedOption.dataset.semesters);
                semesterSelect.innerHTML = '<option value="" selected disabled>Select Semester</option>';
                if (semesters && semesters.length > 0) {
                  semesters.forEach(sem => {
                    const option = document.createElement('option');
                    option.value = sem;
                    option.textContent = `Semester ${sem}`;
                    if (course.semester === sem) {
                      option.selected = true;
                    }
                    semesterSelect.appendChild(option);
                  });
                  semesterSelect.disabled = false;
                } else {
                  semesterSelect.disabled = true;
                }
              } catch (error) {
                this.logger.error('Error parsing semesters data:', error);
              }
            } else {
              semesterSelect.innerHTML = '<option value="" selected disabled>Select Semester</option>';
              semesterSelect.disabled = true;
            }
          });

          // STEP 2: Now populate all dropdowns with data

          // Load courses
          try {
            const response = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/courses-new`);
            if (response.data && response.data.length > 0) {
              response.data.forEach(c => {
                const option = document.createElement('option');
                option.value = c._id;
                option.textContent = `${c.code} - ${c.title}`;
                if (course.courseId === c._id) {
                  option.selected = true;
                }
                courseSelect.appendChild(option);
              });
            }
          } catch (error) {
            this.logger.error('Error fetching courses:', error);
          }

          // Load academic sessions
          try {
            const response = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/academic-sessions`);
            if (response.data && response.data.length > 0) {
              response.data.forEach(session => {
                const option = document.createElement('option');
                option.value = session._id;
                option.textContent = session.sessionYear;
                if (course.sessionId === session._id) {
                  option.selected = true;
                }
                sessionSelect.appendChild(option);
              });
            }
          } catch (error) {
            this.logger.error('Error fetching academic sessions:', error);
          }

          // Load lecturers
          try {
            const response = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/staff?staffType=academic`);
            if (response.data && response.data.data && response.data.data.staff) {
              response.data.data.staff.forEach(staff => {
                const option = document.createElement('option');
                option.value = staff._id;
                option.textContent = `${staff.userId?.firstName || ''} ${staff.userId?.lastName || ''}`.trim();
                // Check if this staff member is in the lecturerIds array
                if (course.lecturerIds && course.lecturerIds.includes(staff._id)) {
                  option.selected = true;
                }
                lecturerSelect.appendChild(option);
              });
            }
          } catch (error) {
            this.logger.error('Error fetching academic staff:', error);
          }

          // Load program types and pre-select
          try {
            const response = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/programtypes`);
            if (response.data && response.data.length > 0) {
              programTypeSelect.innerHTML = '<option value="" selected disabled>Select Program Type</option>';
              response.data.forEach(type => {
                const option = document.createElement('option');
                option.value = type._id;
                option.textContent = type.type;
                if (course.programTypeId === type._id) {
                  option.selected = true;
                }
                programTypeSelect.appendChild(option);
              });
              programTypeSelect.disabled = false;
            }
          } catch (error) {
            this.logger.error('Error fetching program types:', error);
          }

          // STEP 3: Trigger the cascade by setting school value
          if (course.schoolId) {
            schoolSelect.value = course.schoolId;
            // Trigger school change which will cascade to department, then to program offerings
            const event = new Event('change');
            schoolSelect.dispatchEvent(event);
          }

          // Also trigger program type change if we have it (for mode loading)
          if (course.programTypeId) {
            // Small delay to ensure school cascade has started
            setTimeout(() => {
              const event = new Event('change');
              programTypeSelect.dispatchEvent(event);
            }, 100);
          }
        },
        preConfirm: () => {
          const courseId = document.getElementById('course').value;
          const units = document.getElementById('units').value;
          const hours = document.getElementById('hours').value;
          const session = document.getElementById('session').value;
          const program = document.getElementById('program').value;
          const year = document.getElementById('year').value;
          const semester = document.getElementById('semester').value;
          const category = document.getElementById('category').value;
          const lecturerSelect = document.getElementById('lecturer');
          const selectedLecturers = Array.from(lecturerSelect.selectedOptions).map(option => option.value);

          if (!courseId || !units || !hours || !session || !program || !year || !semester || !category || selectedLecturers.length === 0) {
            Swal.showValidationMessage('Please fill all required fields and select at least one lecturer');
            return false;
          }

          return {
            course: courseId,
            units: parseInt(units),
            hours: parseInt(hours),
            session,
            program,
            year: parseInt(year),
            semester: parseInt(semester),
            category,
            lecturer: selectedLecturers
          };
        }
      }).then((result) => {
        if (result.isConfirmed) {
          this.updateCourseOffering(course._id, result.value);
        }
      });
    },

    // Update course offering
    async updateCourseOffering(offeringId, offeringData) {
      try {
        this.loading = true;

        const response = await axios.put(
          `${process.env.VUE_APP_API_BASE_URL}/program-courses/${offeringId}`,
          {
            course: offeringData.course,
            units: offeringData.units,
            hours: offeringData.hours,
            session: offeringData.session,
            program: offeringData.program,
            year: offeringData.year,
            semester: offeringData.semester,
            category: offeringData.category,
            lecturer: offeringData.lecturer
          }
        );

        if (response.data) {
          Swal.fire('Success', 'Course offering updated successfully', 'success');
          this.fetchCourses();
        } else {
          Swal.fire('Error', 'Failed to update course offering', 'error');
        }
      } catch (error) {
        this.logger.error('Error updating course offering:', error);
        Swal.fire('Error', error.response?.data?.message || 'Failed to update course offering', 'error');
      } finally {
        this.loading = false;
      }
    },

    // Delete course
    deleteCourse(course) {
      Swal.fire({
        title: 'Delete Course',
        text: `Are you sure you want to delete ${course.code} - ${course.title}? This action cannot be undone.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!'
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            this.loading = true;

            // Delete the program course record
            const response = await axios.delete(
              `${process.env.VUE_APP_API_BASE_URL}/program-courses/${course._id}`
            );

            if (response.data) {
              Swal.fire('Deleted!', 'Course has been deleted successfully.', 'success');
              this.fetchCourses();
            } else {
              Swal.fire('Error', 'Failed to delete course', 'error');
            }
          } catch (error) {
            this.logger.error('Error deleting course:', error);
            Swal.fire('Error', error.response?.data?.message || 'Failed to delete course', 'error');
          } finally {
            this.loading = false;
          }
        }
      });
    },

    // Manage Courses Modal - for pure course CRUD
    openManageCoursesModal() {
      // Define window functions first, before modal opens
      const self = this;

      window.addNewCourse = async () => {
        const code = document.getElementById('courseCode').value.trim();
        const title = document.getElementById('courseTitle').value.trim();
        const description = document.getElementById('courseDescription').value.trim();
        const active = document.getElementById('courseActive').checked;

        if (!code || !title) {
          Swal.fire({
            icon: 'warning',
            title: 'Validation Error',
            text: 'Course code and title are required'
          });
          return;
        }

        try {
          const response = await axios.post(`${process.env.VUE_APP_API_BASE_URL}/courses-new`, {
            code, title, description, active
          });

          if (response.data) {
            // Clear form
            document.getElementById('courseCode').value = '';
            document.getElementById('courseTitle').value = '';
            document.getElementById('courseDescription').value = '';
            document.getElementById('courseActive').checked = true;

            // Reload table using captured context
            try {
              await self.loadCoursesInModal();
            } catch (reloadError) {
              self.logger.warn('Could not reload courses table:', reloadError);
            }

            Swal.fire({
              icon: 'success',
              title: 'Success!',
              text: 'Course added successfully',
              timer: 2000,
              showConfirmButton: false
            });
          }
        } catch (error) {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: error.response?.data?.message || 'Failed to add course'
          });
        }
      };

      window.editCourseInModal = async (id, code, title, description, active) => {
        const { value: formValues } = await Swal.fire({
          title: 'Edit Course',
          html: `
            <div class="row g-3">
              <div class="col-md-6">
                <label class="form-label">Course Code</label>
                <input id="editCourseCode" class="form-control" value="${code}" required>
              </div>
              <div class="col-md-6">
                <div class="form-check mt-4">
                  <input class="form-check-input" type="checkbox" id="editCourseActive" ${active ? 'checked' : ''}>
                  <label class="form-check-label">Active</label>
                </div>
              </div>
              <div class="col-12">
                <label class="form-label">Course Title</label>
                <input id="editCourseTitle" class="form-control" value="${title}" required>
              </div>
              <div class="col-12">
                <label class="form-label">Description</label>
                <textarea id="editCourseDescription" class="form-control" rows="3">${description}</textarea>
              </div>
            </div>
          `,
          showCancelButton: true,
          confirmButtonText: 'Update Course',
          preConfirm: () => {
            const code = document.getElementById('editCourseCode').value.trim();
            const title = document.getElementById('editCourseTitle').value.trim();

            if (!code || !title) {
              Swal.showValidationMessage('Course code and title are required');
              return false;
            }

            return {
              code,
              title,
              description: document.getElementById('editCourseDescription').value.trim(),
              active: document.getElementById('editCourseActive').checked
            };
          }
        });

        if (formValues) {
          try {
            const response = await axios.put(`${process.env.VUE_APP_API_BASE_URL}/courses-new/${id}`, formValues);
            if (response.data) {
              // Update the row in the table directly instead of reloading
              const row = document.querySelector(`tr[data-course-id="${id}"]`);
              if (row) {
                const cells = row.querySelectorAll('td');
                cells[0].innerHTML = `<code>${formValues.code}</code>`;
                cells[1].textContent = formValues.title;
                cells[2].innerHTML = `<span class="text-truncate" style="max-width: 200px;" title="${formValues.description || 'No description'}">${formValues.description || '<em class="text-muted">No description</em>'}</span>`;
                cells[3].innerHTML = `<span class="badge ${formValues.active ? 'bg-success' : 'bg-secondary'}">${formValues.active ? 'Active' : 'Inactive'}</span>`;

                // Update the onclick attributes with new values
                const editBtn = cells[4].querySelector('.btn-outline-primary');
                if (editBtn) {
                  editBtn.setAttribute('onclick', `editCourseInModal('${id}', '${formValues.code}', '${formValues.title}', '${formValues.description || ''}', ${formValues.active})`);
                }
              }

              // Show success toast
              Swal.fire({
                icon: 'success',
                title: 'Updated!',
                text: 'Course updated successfully',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true
              });
            }
          } catch (error) {
            Swal.fire('Error', error.response?.data?.message || 'Failed to update course', 'error');
          }
        }
      };

      window.deleteCourseInModal = async (id, code) => {
        const result = await Swal.fire({
          title: 'Delete Course',
          text: `Are you sure you want to delete course "${code}"?`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#d33',
          confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
          try {
            const response = await axios.delete(`${process.env.VUE_APP_API_BASE_URL}/courses-new/${id}`);
            if (response.data) {
              // Remove the row from the table directly
              const row = document.querySelector(`tr[data-course-id="${id}"]`);
              if (row) {
                row.remove();

                // Check if table is empty now
                const remainingRows = document.querySelectorAll('#coursesTableBody tr[data-course-id]');
                if (remainingRows.length === 0) {
                  const tableBody = document.getElementById('coursesTableBody');
                  if (tableBody) {
                    tableBody.innerHTML = `
                      <tr>
                        <td colspan="5" class="text-center py-3 text-muted">
                          <i class="bi bi-inbox me-2"></i>No courses found
                        </td>
                      </tr>
                    `;
                  }
                }
              }

              // Show success toast
              Swal.fire({
                icon: 'success',
                title: 'Deleted!',
                text: 'Course deleted successfully',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true
              });
            }
          } catch (error) {
            Swal.fire('Error', error.response?.data?.message || 'Failed to delete course', 'error');
          }
        }
      };

      Swal.fire({
        title: 'Manage Courses',
        html: `
          <div class="container-fluid">
            <!-- Add New Course Form -->
            <div class="card mb-4">
              <div class="card-header bg-primary text-white">
                <h6 class="mb-0"><i class="bi bi-plus-circle me-2"></i>Add New Course</h6>
              </div>
              <div class="card-body">
                <form id="newCourseForm" class="row g-3">
                  <div class="col-md-4">
                    <label class="form-label">Course Code <span class="text-danger">*</span></label>
                    <input type="text" id="courseCode" class="form-control" placeholder="e.g. CSC101" required />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Course Title <span class="text-danger">*</span></label>
                    <input type="text" id="courseTitle" class="form-control" placeholder="e.g. Introduction to Computing" required />
                  </div>
                  <div class="col-md-2">
                    <label class="form-label">Status</label>
                    <div class="form-check mt-2">
                      <input class="form-check-input" type="checkbox" id="courseActive" checked>
                      <label class="form-check-label" for="courseActive">Active</label>
                    </div>
                  </div>
                  <div class="col-12">
                    <label class="form-label">Description</label>
                    <textarea id="courseDescription" class="form-control" rows="2" placeholder="Course description (optional)"></textarea>
                  </div>
                  <div class="col-12">
                    <button type="button" class="btn btn-primary" onclick="addNewCourse()">
                      <i class="bi bi-plus-circle me-2"></i>Add Course
                    </button>
                  </div>
                </form>
              </div>
            </div>

            <!-- Existing Courses Table -->
            <div class="card">
              <div class="card-header bg-light d-flex justify-content-between align-items-center">
                <h6 class="mb-0"><i class="bi bi-list me-2"></i>Existing Courses</h6>
                <div class="input-group" style="max-width: 250px;">
                  <input type="text" id="courseSearchInput" class="form-control form-control-sm" placeholder="Search courses...">
                  <span class="input-group-text"><i class="bi bi-search"></i></span>
                </div>
              </div>
              <div class="card-body p-0" style="max-height: 400px; overflow-y: auto;">
                <table class="table table-sm table-hover mb-0" id="coursesTable">
                  <thead class="table-light sticky-top">
                    <tr>
                      <th>Code</th>
                      <th>Title</th>
                      <th>Description</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody id="coursesTableBody">
                    <tr>
                      <td colspan="5" class="text-center py-3">
                        <div class="spinner-border spinner-border-sm" role="status"></div>
                        <span class="ms-2">Loading courses...</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        `,
        width: '90%',
        showCloseButton: true,
        showConfirmButton: false,
        didOpen: async () => {
          await this.loadCoursesInModal();
          this.setupCourseSearch();
        }
      });
    },

    // Load courses in the manage courses modal
    async loadCoursesInModal() {
      try {
        const response = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/courses-new`);
        const courses = response.data || [];

        const tableBody = document.getElementById('coursesTableBody');

        // Check if the table body element exists (modal might be closed)
        if (!tableBody) {
          // Silently return - this is normal when modal is closed
          return;
        }        if (courses.length === 0) {
          tableBody.innerHTML = `
            <tr>
              <td colspan="5" class="text-center py-3 text-muted">
                <i class="bi bi-inbox me-2"></i>No courses found
              </td>
            </tr>
          `;
          return;
        }

        tableBody.innerHTML = courses.map(course => `
          <tr data-course-id="${course._id}">
            <td><code>${course.code}</code></td>
            <td>${course.title}</td>
            <td class="text-truncate" style="max-width: 200px;" title="${course.description || 'No description'}">${course.description || '<em class="text-muted">No description</em>'}</td>
            <td>
              <span class="badge ${course.active ? 'bg-success' : 'bg-secondary'}">
                ${course.active ? 'Active' : 'Inactive'}
              </span>
            </td>
            <td>
              <div class="btn-group btn-group-sm">
                <button class="btn btn-outline-primary btn-sm" onclick="editCourseInModal('${course._id}', '${course.code}', '${course.title}', '${course.description || ''}', ${course.active})" title="Edit">
                  <i class="bi bi-pencil"></i>
                </button>
                <button class="btn btn-outline-danger btn-sm" onclick="deleteCourseInModal('${course._id}', '${course.code}')" title="Delete">
                  <i class="bi bi-trash"></i>
                </button>
              </div>
            </td>
          </tr>
        `).join('');

      } catch (error) {
        this.logger.error('Error loading courses:', error);
        const tableBody = document.getElementById('coursesTableBody');
        if (tableBody) {
          tableBody.innerHTML = `
            <tr>
              <td colspan="5" class="text-center py-3 text-danger">
                <i class="bi bi-exclamation-triangle me-2"></i>Error loading courses: ${error.message || 'Unknown error'}
              </td>
            </tr>
          `;
        }
      }
    },

    // Setup search functionality for courses
    setupCourseSearch() {
      const searchInput = document.getElementById('courseSearchInput');
      if (searchInput) {
        searchInput.addEventListener('input', (e) => {
          const query = e.target.value.toLowerCase();
          const rows = document.querySelectorAll('#coursesTableBody tr[data-course-id]');

          rows.forEach(row => {
            const text = row.textContent.toLowerCase();
            row.style.display = text.includes(query) ? '' : 'none';
          });
        });
      }
    },

    // Rename the existing method to openAddCourseOfferingModal
    openAddCourseOfferingModal() {
      this.editMode = false;
      this.newCourse = {
        code: "",
        title: "",
        description: "",
        units: 0,
        hours: 0,
        session: "",
        department: "",
        programType: "",
        programMode: "",
        program: "",
        year: "",
        semester: "",
        lecturer: "",
        course: "" // New field for selecting existing course
      };

      Swal.fire({
        title: 'Add New Course Offering',
        html: `
          <form id="addCourseOfferingForm" class="text-start">
            <div class="row mb-3">
              <div class="col-md-3">
                <label class="form-label">Academic Session <span class="text-danger">*</span></label>
                <select id="session" class="form-select" required>
                  <option value="" selected disabled>Select academic session</option>
                </select>
              </div>
              <div class="col-md-3">
                <label class="form-label">School <span class="text-danger">*</span></label>
                <select id="school" class="form-select" required>
                  <option value="" selected disabled>Select School</option>
                  ${(this.schools || []).filter(school => school && school._id).map(school => `<option value="${school._id}">${school.name}</option>`).join('')}
                </select>
              </div>
              <div class="col-md-6">
                <label class="form-label">Department <span class="text-danger">*</span></label>
                <select id="department" class="form-select" required disabled>
                  <option value="" selected disabled>Select Department</option>
                </select>
              </div>
            </div>
            <div class="row mb-3">
              <div class="col-md-3">
                <label class="form-label">Program Type <span class="text-danger">*</span></label>
                <select id="programType" class="form-select" required disabled>
                  <option value="" selected disabled>Select Program Type</option>
                </select>
              </div>
              <div class="col-md-3">
                <label class="form-label">Program Mode <span class="text-danger">*</span></label>
                <select id="programMode" class="form-select" required disabled>
                  <option value="" selected disabled>Select Program Mode</option>
                </select>
              </div>
              <div class="col-md-6">
                <label class="form-label">Program <span class="text-danger">*</span></label>
                <select id="program" class="form-select" required disabled>
                  <option value="" selected disabled>Select Program</option>
                </select>
              </div>
            </div>
            <div class="row mb-3">
                            <div class="col-md-3">
                <label class="form-label">Year <span class="text-danger">*</span></label>
                <select id="year" class="form-select" required disabled>
                  <option value="" selected disabled>Select Year</option>
                </select>
              </div>
                            <div class="col-md-3">
                <label class="form-label">Semester <span class="text-danger">*</span></label>
                <select id="semester" class="form-select" required disabled>
                  <option value="" selected disabled>Select Semester</option>
                </select>
              </div>
              <div class="col-md-6">
                <label class="form-label">Course <span class="text-danger">*</span></label>
                <select id="course" class="form-select" required disabled>
                  <option value="" selected disabled>Select Course</option>
                </select>
              </div>
            </div>
            <div class="row mb-3">
                            <div class="col-md-2">
                <label class="form-label">Units <span class="text-danger">*</span></label>
                <input type="number" id="units" class="form-control" min="1" max="6" required />
              </div>
                            <div class="col-md-2">
                <label class="form-label">Hours <span class="text-danger">*</span></label>
                <input type="number" id="hours" class="form-control" min="1" required />
              </div>
                            <div class="col-md-3">
                <label class="form-label">Category <span class="text-danger">*</span></label>
                <select id="category" class="form-select" required>
                  <option value="" selected disabled>Select Category</option>
                  <option value="compulsory">Compulsory</option>
                  <option value="elective">Elective</option>
                </select>
              </div>
              <div class="col-md-5">
                <label class="form-label">Lecturer(s) <span class="text-danger">*</span> <small class="text-body-tertiary fw-light">At least one lecturer is required.</small></label>
                <select id="lecturer" class="form-select" multiple required  size="3">
                  <option value="" disabled>Select Lecturer(s)</option>
                </select>
                <small class="form-text text-muted">Hold Ctrl (Cmd on Mac) to select multiple lecturers.</small>
              </div>
            </div>
          </form>
        `,
        width: '900px',
        showCancelButton: true,
        confirmButtonText: 'Add Course Offering',
        didOpen: async () => {
          // Get references to select elements
          const courseSelect = document.getElementById('course');

          // Populate academic sessions dropdown
          const sessionSelect = document.getElementById('session');
          try {
            const response = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/academic-sessions`);
            if (response.data && response.data.length > 0) {
              response.data.forEach(session => {
                const option = document.createElement('option');
                option.value = session._id;
                option.textContent = session.sessionYear;
                sessionSelect.appendChild(option);
              });
            }
          } catch (error) {
            this.logger.error('Error fetching academic sessions:', error);
          }

          // Populate lecturers dropdown (academic staff only)
          const lecturerSelect = document.getElementById('lecturer');
          try {
            const response = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/staff?staffType=academic`);
            if (response.data && response.data.data && response.data.data.staff) {
              response.data.data.staff.forEach(staff => {
                const option = document.createElement('option');
                option.value = staff._id;
                option.textContent = `${staff.userId?.firstName || ''} ${staff.userId?.lastName || ''}`.trim();
                lecturerSelect.appendChild(option);
              });
            }
          } catch (error) {
            this.logger.error('Error fetching academic staff:', error);
          }

          // Populate program types dropdown
          const programTypeSelect = document.getElementById('programType');
          try {
            const response = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/programtypes`);
            if (response.data && response.data.length > 0) {
              programTypeSelect.innerHTML = '<option value="" selected disabled>Select Program Type</option>';
              response.data.forEach(type => {
                const option = document.createElement('option');
                option.value = type._id;
                option.textContent = type.type;
                programTypeSelect.appendChild(option);
              });
              programTypeSelect.disabled = false;
            }
          } catch (error) {
            this.logger.error('Error fetching program types:', error);
          }

          // Add event listener to school select
          const schoolSelect = document.getElementById('school');
          const departmentSelect = document.getElementById('department');

          schoolSelect.addEventListener('change', async () => {
            const schoolId = schoolSelect.value;
            if (schoolId) {
              departmentSelect.disabled = true;
              departmentSelect.innerHTML = '<option value="">Loading...</option>';

              try {
                const departments = await this.fetchDepartmentsBySchool(schoolId);

                departmentSelect.innerHTML = '<option value="" selected disabled>Select Department</option>';
                departments.forEach(dept => {
                  const option = document.createElement('option');
                  option.value = dept._id;
                  option.textContent = dept.name;
                  departmentSelect.appendChild(option);
                });

                departmentSelect.disabled = false;
              } catch (error) {
                this.logger.error('Error fetching departments:', error);
                departmentSelect.innerHTML = '<option value="" selected disabled>Error loading departments</option>';
              }
            } else {
              departmentSelect.innerHTML = '<option value="" selected disabled>Select Department</option>';
              departmentSelect.disabled = true;
            }
          });

          // Add event listener to program type select
          const programModeSelect = document.getElementById('programMode');
          programTypeSelect.addEventListener('change', async () => {
            const typeId = programTypeSelect.value;
            if (typeId) {
              programModeSelect.disabled = true;
              programModeSelect.innerHTML = '<option value="">Loading...</option>';

              try {
                const response = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/programmodes`);

                programModeSelect.innerHTML = '<option value="" selected disabled>Select Program Mode</option>';
                if (response.data && response.data.length > 0) {
                  response.data.forEach(mode => {
                    const option = document.createElement('option');
                    option.value = mode._id;
                    option.textContent = mode.mode;
                    programModeSelect.appendChild(option);
                  });
                }
                programModeSelect.disabled = false;
              } catch (error) {
                this.logger.error('Error fetching program modes:', error);
                programModeSelect.innerHTML = '<option value="" selected disabled>Error loading modes</option>';
              }
            } else {
              programModeSelect.innerHTML = '<option value="" selected disabled>Select Program Mode</option>';
              programModeSelect.disabled = true;
            }
          });

          // Add event listener to department, program type, and program mode selects
          const programSelect = document.getElementById('program');
          const updateProgramOfferings = async () => {
            const departmentId = departmentSelect.value;
            const typeId = programTypeSelect.value;
            const modeId = programModeSelect.value;
            const sessionId = sessionSelect.value;

            if (departmentId && typeId && modeId && sessionId) {
              programSelect.disabled = true;
              programSelect.innerHTML = '<option value="">Loading...</option>';

              try {
                const response = await axios.get(
                  `${process.env.VUE_APP_API_BASE_URL}/program-offerings/department/${departmentId}`
                );
                programSelect.innerHTML = '<option value="" selected disabled>Select Program</option>';
                if (response.data && response.data.length > 0) {
                  const filteredOfferings = response.data.filter(offering =>
                    offering.structure &&
                    offering.structure.type &&
                    offering.structure.mode &&
                    offering.structure.type._id === typeId &&
                    offering.structure.mode._id === modeId
                  );

                  filteredOfferings.forEach(offering => {
                    const option = document.createElement('option');
                    option.value = offering._id;
                    option.textContent = offering.program ? offering.program.name : 'Unknown Program';
                    option.dataset.structure = JSON.stringify(offering.structure);
                    programSelect.appendChild(option);
                  });
                }
                programSelect.disabled = false;
              } catch (error) {
                this.logger.error('Error fetching program offerings:', error);
                programSelect.innerHTML = '<option value="" selected disabled>Error loading programs</option>';
              }
            } else {
              programSelect.innerHTML = '<option value="" selected disabled>Select Program</option>';
              programSelect.disabled = true;
            }
          };

          departmentSelect.addEventListener('change', updateProgramOfferings);
          programTypeSelect.addEventListener('change', updateProgramOfferings);
          programModeSelect.addEventListener('change', updateProgramOfferings);
          sessionSelect.addEventListener('change', updateProgramOfferings);

          // Add event listener to program select to populate years and semesters
          const yearSelect = document.getElementById('year');
          const semesterSelect = document.getElementById('semester');

          programSelect.addEventListener('change', () => {
            const selectedOption = programSelect.options[programSelect.selectedIndex];
            if (selectedOption && selectedOption.dataset.structure) {
              try {
                const structure = JSON.parse(selectedOption.dataset.structure);
                yearSelect.innerHTML = '<option value="" selected disabled>Select Year</option>';
                if (structure.years && structure.years.length > 0) {
                  structure.years.forEach(yearObj => {
                    const option = document.createElement('option');
                    option.value = yearObj.year;
                    option.textContent = `Year ${yearObj.year}`;
                    option.dataset.semesters = JSON.stringify(yearObj.semesters);
                    yearSelect.appendChild(option);
                  });
                  yearSelect.disabled = false;
                } else {
                  yearSelect.disabled = true;
                }

                semesterSelect.innerHTML = '<option value="" selected disabled>Select Semester</option>';
                semesterSelect.disabled = true;
              } catch (error) {
                this.logger.error('Error parsing structure data:', error);
              }
            } else {
              yearSelect.innerHTML = '<option value="" selected disabled>Select Year</option>';
              yearSelect.disabled = true;
              semesterSelect.innerHTML = '<option value="" selected disabled>Select Semester</option>';
              semesterSelect.disabled = true;
            }
          });

          // Add event listener to year select to populate semesters
          yearSelect.addEventListener('change', () => {
            const selectedOption = yearSelect.options[yearSelect.selectedIndex];
            if (selectedOption && selectedOption.dataset.semesters) {
              try {
                const semesters = JSON.parse(selectedOption.dataset.semesters);
                semesterSelect.innerHTML = '<option value="" selected disabled>Select Semester</option>';
                if (semesters && semesters.length > 0) {
                  semesters.forEach(sem => {
                    const option = document.createElement('option');
                    option.value = sem;
                    option.textContent = `Semester ${sem}`;
                    semesterSelect.appendChild(option);
                  });
                  semesterSelect.disabled = false;
                } else {
                  semesterSelect.disabled = true;
                }
              } catch (error) {
                this.logger.error('Error parsing semesters data:', error);
              }
            } else {
              semesterSelect.innerHTML = '<option value="" selected disabled>Select Semester</option>';
              semesterSelect.disabled = true;
            }
          });

          // Function to load available courses and enable course select
          const updateAvailableCourses = async () => {
            const programId = programSelect.value;
            const sessionId = sessionSelect.value;
            const year = yearSelect.value;
            const semester = semesterSelect.value;

            if (programId && sessionId && year && semester) {
              courseSelect.disabled = true;
              courseSelect.innerHTML = '<option value="">Loading courses...</option>';

              try {
                const response = await axios.get(
                  `${process.env.VUE_APP_API_BASE_URL}/program-courses/available-courses`,
                  {
                    params: {
                      program: programId,
                      session: sessionId,
                      year: year,
                      semester: semester
                    }
                  }
                );

                courseSelect.innerHTML = '<option value="" selected disabled>Select Course</option>';
                if (response.data && response.data.length > 0) {
                  response.data.forEach(course => {
                    const option = document.createElement('option');
                    option.value = course._id;
                    option.textContent = `${course.code} - ${course.title}`;
                    courseSelect.appendChild(option);
                  });
                  courseSelect.disabled = false;
                } else {
                  courseSelect.innerHTML = '<option value="" disabled>No available courses for this combination</option>';
                  courseSelect.disabled = true;
                }
              } catch (error) {
                this.logger.error('Error fetching available courses:', error);
                courseSelect.innerHTML = '<option value="" disabled>Error loading courses</option>';
                courseSelect.disabled = true;
              }
            } else {
              courseSelect.innerHTML = '<option value="" selected disabled>Select Course</option>';
              courseSelect.disabled = true;
            }
          };

          // Add event listener to semester select to update available courses
          semesterSelect.addEventListener('change', updateAvailableCourses);
        },
        preConfirm: () => {
          const course = document.getElementById('course').value;
          const units = document.getElementById('units').value;
          const hours = document.getElementById('hours').value;
          const session = document.getElementById('session').value;
          const program = document.getElementById('program').value;
          const year = document.getElementById('year').value;
          const semester = document.getElementById('semester').value;
          const category = document.getElementById('category').value;
          const lecturerSelect = document.getElementById('lecturer');
          const selectedLecturers = Array.from(lecturerSelect.selectedOptions).map(option => option.value);

          if (!course || !units || !hours || !session || !program || !year || !semester || !category || selectedLecturers.length === 0) {
            Swal.showValidationMessage('Please fill all required fields and select at least one lecturer');
            return false;
          }

          return {
            course,
            units: parseInt(units),
            hours: parseInt(hours),
            session,
            program,
            year: parseInt(year),
            semester: parseInt(semester),
            category,
            lecturer: selectedLecturers
          };
        }
      }).then((result) => {
        if (result.isConfirmed) {
          this.createCourseOffering(result.value);
        }
      });
    },

    // Edit school
    editSchool(school) {
      this.editMode = true;
      this.editingItem = school;

      Swal.fire({
        title: 'Edit School',
        html: `
          <form id="editSchoolForm" class="text-start">
            <div class="mb-3">
              <label class="form-label">School Code <span class="text-danger">*</span></label>
              <input type="text" id="code" class="form-control" placeholder="e.g. SCI" maxlength="3" required value="${school.code}" />
            </div>
            <div class="mb-3">
              <label class="form-label">School Name <span class="text-danger">*</span></label>
              <input type="text" id="name" class="form-control" placeholder="e.g. School of Science" required value="${school.name}" />
            </div>
            <div class="mb-3">
              <label class="form-label">Description</label>
              <textarea id="description" class="form-control" rows="3">${school.description || ''}</textarea>
            </div>
          </form>
        `,
        showCancelButton: true,
        confirmButtonText: 'Update School',
        preConfirm: () => {
          const code = document.getElementById('code').value;
          const name = document.getElementById('name').value;
          const description = document.getElementById('description').value;

          if (!code || !name) {
            Swal.showValidationMessage('Please fill all required fields');
            return false;
          }

          return {
            _id: school._id,
            code,
            name,
            description
          };
        }
      }).then((result) => {
        if (result.isConfirmed) {
          this.updateSchool(result.value);
        }
      });
    },

    // Api request to update school
    async updateSchool(schoolData) {
      try {
        this.loading = true;
        const response = await axios.put(
          `${process.env.VUE_APP_API_BASE_URL}/schools/${schoolData._id}`,
          schoolData
          // Headers not needed - cookies are automatic
        );

        if (response.data) {
          Swal.fire('Success', 'School updated successfully', 'success');
          this.fetchSchools();
          this.fetchAllDepartments(); // Refresh departments to update counts
        } else {
          Swal.fire('Error', 'Failed to update school', 'error');
        }
      } catch (error) {
        this.logger.error('Error updating school:', error);
        Swal.fire('Error', error.response?.data?.message || 'Failed to update school', 'error');
      } finally {
        this.loading = false;
        this.editMode = false;
        this.editingItem = null;
      }
    },

    // Add new school
    openAddSchoolModal() {
      this.editMode = false;
      this.newSchool = {
        name: "",
        code: "",
        description: ""
      };

      Swal.fire({
        title: 'Add New School',
        html: `
          <form id="addSchoolForm" class="text-start">
            <div class="mb-3">
              <label class="form-label">School Code <span class="text-danger">*</span></label>
              <input type="text" id="code" class="form-control" maxlength="3" placeholder="e.g. SCI" required />
            </div>
            <div class="mb-3">
              <label class="form-label">School Name <span class="text-danger">*</span></label>
              <input type="text" id="name" class="form-control" placeholder="e.g. School of Science" required />
            </div>
            <div class="mb-3">
              <label class="form-label">Description</label>
              <textarea id="description" class="form-control" rows="3"></textarea>
            </div>
          </form>
        `,
        showCancelButton: true,
        confirmButtonText: 'Add School',
        preConfirm: () => {
          const code = document.getElementById('code').value;
          const name = document.getElementById('name').value;
          const description = document.getElementById('description').value;

          if (!code || !name) {
            Swal.showValidationMessage('Please fill all required fields');
            return false;
          }

          return {
            code,
            name,
            description
          };
        }
      }).then((result) => {
        if (result.isConfirmed) {
          this.createSchool(result.value);
        }
      });
    },

    // Create new school
    async createSchool(schoolData) {
      try {
        this.loading = true;
        const response = await axios.post(
          `${process.env.VUE_APP_API_BASE_URL}/schools`,
          schoolData
        );
        // Headers not needed - cookies are automatic

        // The API is successfully adding the school but might not return success flag
        // Consider any non-error response as success
        if (response.data) {
          // Add the new school to the schools array to update UI immediately
          if (response.data._id) {
            // If API returns the created school object
            this.schools.push(response.data);
          } else if (response.data.data && response.data.data.school) {
            // If API returns nested school object
            this.schools.push(response.data.data.school);
          }

          Swal.fire('Success', 'School added successfully', 'success');
          // Fetch schools to ensure data is in sync with server
          this.fetchSchools();
        } else {
          Swal.fire('Error', response.data?.message || 'Failed to add school', 'error');
        }
      } catch (error) {
        this.logger.error('Error creating school:', error);
        Swal.fire('Error', error.response?.data?.message || 'Failed to add school', 'error');
      } finally {
        this.loading = false;
      }
    },
    // Delete school
    async deleteSchool(schoolId) {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: 'This will delete the school and all associated departments and programs!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!'
      });

      if (result.isConfirmed) {
        try {
          await axios.delete(`${process.env.VUE_APP_API_BASE_URL}/schools/${schoolId}`);
          // Headers not needed - cookies are automatic
          await this.fetchSchools();
          await this.fetchAllDepartments();;
          await this.fetchPrograms();

          Swal.fire(
            'Deleted!',
            'The school has been deleted.',
            'success'
          );
        } catch (err) {
          this.error = 'Failed to delete school';
          this.logger.error(err);

          Swal.fire(
            'Error!',
            'Failed to delete school.',
            'error'
          );
        }
      }
    },

    // Add new department
    openAddDepartmentModal() {
      this.editMode = false;
      this.newDepartment = {
        name: "",
        code: "",
        description: "",
        school: ""
      };

      Swal.fire({
        title: 'Add New Department',
        html: `
          <form id="addDepartmentForm" class="text-start">
            <div class="mb-3">
              <label class="form-label">Department Code <span class="text-danger">*</span></label>
              <input type="text" id="code" class="form-control" placeholder="e.g. CSC" maxlength="3" required>
            </div>
            <div class="mb-3">
              <label class="form-label">Department Name <span class="text-danger">*</span></label>
              <input type="text" id="name" class="form-control" placeholder="e.g. Computer Science" required>
            </div>
            <div class="mb-3">
              <label class="form-label">School <span class="text-danger">*</span></label>
              <select id="school" class="form-select" required>
                <option value="">Select School</option>
                ${(this.schools || []).filter(school => school && school._id).map(school => `<option value="${school._id}">${school.name}</option>`).join('')}
              </select>
            </div>
            <div class="mb-3">
              <label class="form-label">Description</label>
              <textarea id="description" class="form-control" rows="3"></textarea>
            </div>
          </form>
        `,
        showCancelButton: true,
        confirmButtonText: 'Add Department',
        preConfirm: () => {
          const code = document.getElementById('code').value;
          const name = document.getElementById('name').value;
          const school = document.getElementById('school').value;
          const description = document.getElementById('description').value;

          if (!code || !name || !school) {
            Swal.showValidationMessage('Please fill all required fields');
            return false;
          }

          return {
            code,
            name,
            school,
            description
          };
        }
      }).then((result) => {
        if (result.isConfirmed) {
          this.createDepartment(result.value);
        }
      });
    },

    // Create new department
    async createDepartment(departmentData) {
      try {
        this.loading = true;
        const response = await axios.post(
          `${process.env.VUE_APP_API_BASE_URL}/departments`,
          departmentData
          // Headers not needed - cookies are automatic
        );

        // The API is successfully adding the department but might not return success flag
        // Consider any non-error response as success, similar to createSchool
        if (response.data) {
          // Add the new department to the departments array to update UI immediately
          if (response.data._id) {
            // If API returns the created department object
            this.departments.push(response.data);
          } else if (response.data.data && response.data.data.department) {
            // If API returns nested department object
            this.departments.push(response.data.data.department);
          }

          Swal.fire('Success', 'Department added successfully', 'success');
          // Fetch departments to ensure data is in sync with server
          this.fetchAllDepartments();
        } else {
          Swal.fire('Error', response.data?.message || 'Failed to add department', 'error');
        }
      } catch (error) {
        this.logger.error('Error creating department:', error);
        Swal.fire('Error', error.response?.data?.message || 'Failed to add department', 'error');
      } finally {
        this.loading = false;
      }
    },

    // Edit department
    editDepartment(department) {
      this.editMode = true;
      this.editingItem = department;

      Swal.fire({
        title: 'Edit Department',
        html: `
          <form id="editDepartmentForm" class="text-start">
            <div class="mb-3">
              <label class="form-label">Department Code <span class="text-danger">*</span></label>
              <input type="text" id="code" class="form-control" placeholder="e.g. CSC" maxlength="3" required value="${department.code}">
            </div>
            <div class="mb-3">
              <label class="form-label">Department Name <span class="text-danger">*</span></label>
              <input type="text" id="name" class="form-control" placeholder="e.g. Computer Science" required value="${department.name}">
            </div>
            <div class="mb-3">
              <label class="form-label">School <span class="text-danger">*</span></label>
              <select id="school" class="form-select" required>
                <option value="">Select School</option>
                ${(this.schools || []).filter(school => school && school._id).map(school => `<option value="${school._id}" ${department.school && department.school._id === school._id ? 'selected' : ''}>${school.name}</option>`).join('')}
              </select>
            </div>
            <div class="mb-3">
              <label class="form-label">Description</label>
              <textarea id="description" class="form-control" rows="3">${department.description || ''}</textarea>
            </div>
          </form>
        `,
        showCancelButton: true,
        confirmButtonText: 'Update Department',
        preConfirm: () => {
          const code = document.getElementById('code').value;
          const name = document.getElementById('name').value;
          const school = document.getElementById('school').value;
          const description = document.getElementById('description').value;

          if (!code || !name || !school) {
            Swal.showValidationMessage('Please fill all required fields');
            return false;
          }

          return {
            _id: department._id,
            code,
            name,
            school,
            description
          };
        }
      }).then((result) => {
        if (result.isConfirmed) {
          this.updateDepartment(result.value);
        }
      });
    },

    // Api request to update department
    async updateDepartment(departmentData) {
      try {
        this.loading = true;
        const response = await axios.put(
          `${process.env.VUE_APP_API_BASE_URL}/departments/${departmentData._id}`,
          departmentData
          // Headers not needed - cookies are automatic
        );

        if (response.data) {
          Swal.fire('Success', 'Department updated successfully', 'success');
          this.fetchAllDepartments();
          this.fetchPrograms(); // Refresh programs to update counts
        } else {
          Swal.fire('Error', 'Failed to update department', 'error');
        }
      } catch (error) {
        this.logger.error('Error updating department:', error);
        Swal.fire('Error', error.response?.data?.message || 'Failed to update department', 'error');
      } finally {
        this.loading = false;
        this.editMode = false;
        this.editingItem = null;
      }
    },

    // Delete department
    async deleteDepartment(departmentId) {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: 'This will delete the department and all associated programs!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!'
      });

      if (result.isConfirmed) {
        try {
          await axios.delete(`${process.env.VUE_APP_API_BASE_URL}/departments/${departmentId}`);
          // Headers not needed - cookies are automatic
          await this.fetchAllDepartments();
          await this.fetchPrograms();

          Swal.fire(
            'Deleted!',
            'The department has been deleted.',
            'success'
          );
        } catch (err) {
          this.error = 'Failed to delete department';
          this.logger.error(err);

          Swal.fire(
            'Error!',
            'Failed to delete department.',
            'error'
          );
        }
      }
    },

    // Add new program
    openAddProgramModal() {
      this.editMode = false;
      this.newProgram = {
        name: "",
        code: "",
        description: "",
        department: "",
        minUnits: 15,
        maxUnits: 24,
        active: true,
        isHND: false,
        isND: false,
        isPartTime: false,
        isFullTime: false,
        programTypes: []
      };

      // Initialize with one program type by default
      this.addProgramType();

      // Generate HTML for program types section
      const generateProgramTypesHtml = () => {
        let html = '';

        this.newProgram.programTypes.forEach((pt, index) => {
          html += `
            <div class="program-type-item border rounded p-3 mb-3" style="background-color: #f8f9fa;">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h6 class="mb-0 fw-bold">Program Type ${index + 1}</h6>
                ${index > 0 ? `<button type="button" class="btn btn-sm btn-danger" onclick="window.removeType(${index})"><i class="bi bi-trash"></i> Remove</button>` : ''}
              </div>
              <div class="row mb-3">
                <div class="col-md-6">
                  <label class="form-label">Type <span class="text-danger">*</span></label>
                  <select id="type-${index}" class="form-select" onchange="window.handleTypeChange(${index})">
                    <option value="">Select Type</option>
                    ${(this.programTypes || []).filter(type => type && type._id).map(type => {
                      return `<option value="${type.type}" ${pt.type === type.type ? 'selected' : ''}>${type.type} - ${type.description}</option>`;
                    }).join('')}
                  </select>
                </div>
                <div class="col-md-6">
                  <label class="form-label">Mode <span class="text-danger">*</span></label>
                  <select id="mode-${index}" class="form-select" onchange="window.handleModeChange(${index})">
                    <option value="">Select Mode</option>
                    ${(this.programModes || []).filter(mode => mode && mode._id).map(mode => {
                      const currentType = pt.type;
                      const isDisabled = currentType && this.isTypeAndModeDisabled(currentType, mode.mode, index);
                      return `<option value="${mode.mode}" ${pt.mode === mode.mode ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}>${mode.mode} - ${mode.description}${isDisabled ? ' (Already used)' : ''}</option>`;
                    }).join('')}
                  </select>
                </div>
              </div>
              <div id="validation-message-${index}" class="alert alert-warning d-none mb-3">
                This combination of Type and Mode is already selected for another program type.
              </div>
              <div class="row mb-3">
                <div class="col-md-6">
                  <label class="form-label">Number of Academic Years <span class="text-danger">*</span></label>
                  <input type="number" id="sessions-${index}" class="form-control" min="1" max="6" value="${pt.academicSessions || 2}" onchange="window.generateSessionFields(${index})">
                </div>
                <div class="col-md-6">
                  <label class="form-label">Active Status</label>
                  <div class="form-check form-switch mt-2">
                    <input class="form-check-input" type="checkbox" role="switch" id="active-${index}" ${pt.active !== false ? 'checked' : ''} onchange="window.handleActiveToggle(${index})">
                    <label class="form-check-label" for="active-${index}">
                      <span id="activeLabel-${index}">${pt.active !== false ? 'Active' : 'Inactive'}</span>
                    </label>
                  </div>
                  <small class="text-muted">Inactive offerings cannot be selected by students</small>
                </div>
              </div>
              <div id="sessions-container-${index}">
                ${pt.sessions.map((session, sessionIndex) => `
                  <div class="session-item border-top pt-2 mt-2">
                    <h6 class="mb-2 text-muted">Session ${sessionIndex + 1}</h6>
                    <div class="mb-3">
                      <label class="form-label">Number of Semesters</label>
                      <input type="number" id="semesters-${index}-${sessionIndex}" class="form-control" min="1" max="3" value="${session.semesters || 2}">
                    </div>
                  </div>
                `).join('')}
              </div>
            </div>
          `;
        });

        return html;
      };

      Swal.fire({
        title: 'Add New Program',
        html: `
          <div style="max-height: 500px;" class="text-start py-3 overflow-y-scroll">
            <form id="addProgramForm">
              <!-- Basic Information Section -->
              <div class="mb-4">
                <h5 class="border-bottom pb-2 mb-3">Basic Information</h5>
                <div class="mb-3">
                  <label class="form-label">Program Code <span class="text-danger">*</span></label>
                  <input type="text" id="code" class="form-control" placeholder="e.g. CSC" maxlength="3" required>
                </div>
                <div class="mb-3">
                  <label class="form-label">Program Name <span class="text-danger">*</span></label>
                  <input type="text" id="name" class="form-control" placeholder="e.g. Computer Science" required>
                </div>
                <div class="row mb-3">
                  <div class="col-md-6">
                    <label class="form-label">School <span class="text-danger">*</span></label>
                    <select id="school" class="form-select" required onchange="window.fetchDepartmentsForSchool(this.value)">
                      <option value="">Select School</option>
                      ${(this.schools || []).filter(school => school && school._id).map(school => {
                        return `<option value="${school._id}">${school.name}</option>`;
                      }).join('')}
                    </select>
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Department <span class="text-danger">*</span></label>
                    <select id="department" class="form-select" required>
                      <option value="">Select Department</option>
                    </select>
                  </div>
                </div>
                <div class="mb-3">
                  <label class="form-label">Description</label>
                  <textarea id="description" class="form-control" rows="2" placeholder="Enter program description"></textarea>
                </div>
                <div class="row mb-3">
                  <div class="col-md-6">
                    <label class="form-label">Minimum Units <span class="text-danger">*</span></label>
                    <input type="number" id="minUnits" class="form-control" min="1" value="15" required>
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Maximum Units <span class="text-danger">*</span></label>
                    <input type="number" id="maxUnits" class="form-control" min="1" value="24" required>
                  </div>
                </div>
                <div class="mb-3">
                  <label class="form-label">Program Active Status</label>
                  <div class="form-check form-switch">
                    <input class="form-check-input" type="checkbox" role="switch" id="active" checked>
                    <label class="form-check-label" for="active">Active</label>
                  </div>
                  <small class="text-muted">Master switch for entire program</small>
                </div>
              </div>

              <!-- Program Types Section -->
              <div class="mb-4">
                <div class="d-flex justify-content-between align-items-center border-bottom pb-2 mb-3">
                  <h5 class="mb-0">Program Types Configuration</h5>
                  <button type="button" class="btn btn-sm btn-primary" onclick="window.addType()">
                    <i class="bi bi-plus-circle"></i> Add Type
                  </button>
                </div>
                <div id="program-types-container">
                  ${generateProgramTypesHtml()}
                </div>
              </div>
            </form>
          </div>
        `,
        width: '900px',
        showCancelButton: true,
        confirmButtonText: 'Create Program',
        cancelButtonText: 'Cancel',
        customClass: {
          popup: 'swal-wide'
        },
        didOpen: () => {
          // Define function to fetch departments for a school
          window.fetchDepartmentsForSchool = async (schoolId) => {
            if (!schoolId) return;

            try {
              const departmentSelect = document.getElementById('department');
              departmentSelect.innerHTML = '<option value="">Select Department</option>';
              departmentSelect.disabled = true;

              const response = await axios.get(
                `${process.env.VUE_APP_API_BASE_URL}/departments/school/${schoolId}`
              );

              if (response.data && response.data.length > 0) {
                response.data.forEach(dept => {
                  const option = document.createElement('option');
                  option.value = dept._id;
                  option.textContent = dept.name;
                  departmentSelect.appendChild(option);
                });
                departmentSelect.disabled = false;
              } else {
                Swal.showValidationMessage('No departments found for this school');
              }
            } catch (error) {
              this.logger.error('Error fetching departments:', error);
              Swal.showValidationMessage('Failed to load departments');
            }
          };

          // Define functions for program types management
          window.addType = () => {
            this.addProgramType();
            document.getElementById('program-types-container').innerHTML = generateProgramTypesHtml();
          };

          window.removeType = (index) => {
            this.removeProgramType(index);
            document.getElementById('program-types-container').innerHTML = generateProgramTypesHtml();
          };

          window.handleTypeChange = (index) => {
            const typeValue = document.getElementById(`type-${index}`).value;
            const modeValue = document.getElementById(`mode-${index}`).value;

            this.newProgram.programTypes[index].type = typeValue;

            // Check if this type/mode combination already exists
            if (typeValue && modeValue) {
              const isDuplicate = this.isTypeAndModeDisabled(typeValue, modeValue, index);
              const validationMessage = document.getElementById(`validation-message-${index}`);
              if (isDuplicate) {
                validationMessage.classList.remove('d-none');
              } else {
                validationMessage.classList.add('d-none');
              }
            }

            // Refresh the mode options for this index
            document.getElementById('program-types-container').innerHTML = generateProgramTypesHtml();
          };

          window.handleModeChange = (index) => {
            const modeValue = document.getElementById(`mode-${index}`).value;
            const typeValue = document.getElementById(`type-${index}`).value;

            this.newProgram.programTypes[index].mode = modeValue;

            // Check if this type/mode combination already exists
            if (typeValue && modeValue) {
              const isDuplicate = this.isTypeAndModeDisabled(typeValue, modeValue, index);
              const validationMessage = document.getElementById(`validation-message-${index}`);
              if (isDuplicate) {
                validationMessage.classList.remove('d-none');
              } else {
                validationMessage.classList.add('d-none');
              }
            }
          };

          window.generateSessionFields = (index) => {
            const sessionCount = parseInt(document.getElementById(`sessions-${index}`).value) || 0;
            this.newProgram.programTypes[index].academicSessions = sessionCount;
            this.newProgram.programTypes[index].sessions = Array.from({ length: sessionCount }, () => ({ semesters: 2 }));
            document.getElementById('program-types-container').innerHTML = generateProgramTypesHtml();
          };

          window.handleActiveToggle = (index) => {
            const isActive = document.getElementById(`active-${index}`).checked;
            this.newProgram.programTypes[index].active = isActive;
            const label = document.getElementById(`activeLabel-${index}`);
            if (label) {
              label.textContent = isActive ? 'Active' : 'Inactive';
            }
          };
        },
        preConfirm: () => {
          // Validate basic fields
          const code = document.getElementById('code').value.trim();
          const name = document.getElementById('name').value.trim();
          const department = document.getElementById('department').value;
          const description = document.getElementById('description').value.trim();
          const minUnits = parseInt(document.getElementById('minUnits').value);
          const maxUnits = parseInt(document.getElementById('maxUnits').value);
          const active = document.getElementById('active').checked;

          if (!code || !name || !department) {
            Swal.showValidationMessage('Please fill all required fields');
            return false;
          }

          if (!minUnits || minUnits < 1) {
            Swal.showValidationMessage('Minimum units must be at least 1');
            return false;
          }

          if (!maxUnits || maxUnits < 1) {
            Swal.showValidationMessage('Maximum units must be at least 1');
            return false;
          }

          if (maxUnits < minUnits) {
            Swal.showValidationMessage('Maximum units must be greater than or equal to minimum units');
            return false;
          }

          // Collect program types data
          const programTypes = [];
          for (let i = 0; i < this.newProgram.programTypes.length; i++) {
            const type = document.getElementById(`type-${i}`)?.value;
            const mode = document.getElementById(`mode-${i}`)?.value;
            const academicSessions = parseInt(document.getElementById(`sessions-${i}`)?.value) || 2;
            const offeringActive = document.getElementById(`active-${i}`)?.checked;

            if (!type || !mode) {
              Swal.showValidationMessage(`Please select both Type and Mode for Program Type ${i + 1}`);
              return false;
            }

            // Check for duplicate type/mode combinations
            if (this.isTypeAndModeDisabled(type, mode, i)) {
              Swal.showValidationMessage(`Program Type ${i + 1} has a duplicate Type/Mode combination`);
              return false;
            }

            const sessions = [];
            for (let j = 0; j < academicSessions; j++) {
              const semesters = parseInt(document.getElementById(`semesters-${i}-${j}`)?.value) || 2;
              sessions.push({ semesters });
            }

            programTypes.push({
              type,
              mode,
              academicSessions,
              sessions,
              active: offeringActive
            });
          }

          if (programTypes.length === 0) {
            Swal.showValidationMessage('Please add at least one program type');
            return false;
          }

          return {
            code,
            name,
            department,
            description,
            minUnits,
            maxUnits,
            active,
            programTypes
          };
        }
      }).then((result) => {
        if (result.isConfirmed) {
          // Update all program data
          this.newProgram.code = result.value.code;
          this.newProgram.name = result.value.name;
          this.newProgram.department = result.value.department;
          this.newProgram.description = result.value.description;
          this.newProgram.minUnits = result.value.minUnits;
          this.newProgram.maxUnits = result.value.maxUnits;
          this.newProgram.active = result.value.active;
          this.newProgram.programTypes = result.value.programTypes;

          // Call the add method
          this.addProgram();
        }
      });
    },

    // Create new program
    async createProgram(programData) {
      try {
        this.loading = true;
        const response = await axios.post(
          `${process.env.VUE_APP_API_BASE_URL}/programs`,
          programData
          // Headers not needed - cookies are automatic
        );

        if (response.data && response.data.success) {
          Swal.fire('Success', 'Program added successfully', 'success');
          this.fetchPrograms();
        } else {
          Swal.fire('Error', response.data.message || 'Failed to add program', 'error');
        }
      } catch (error) {
        this.logger.error('Error creating program:', error);
        Swal.fire('Error', error.response?.data?.message || 'Failed to add program', 'error');
      } finally {
        this.loading = false;
      }
    },

    // Fetch complete program data with program types
    async fetchProgramById(programId) {
      try {
        const response = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/programs/${programId}`);
        return response.data;
      } catch (error) {
        this.logger.error('Error fetching program by ID:', error);
        throw error;
      }
    },

    // Fetch program structures for a specific program
    async fetchProgramStructures(programId) {
      try {
        // Get program offerings for this program, which contain references to program structures
        const response = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/program-offerings/program/${programId}`);

        if (response.data && response.data.length > 0) {
          // Extract unique program structures from the offerings
          const structureIds = [...new Set(response.data.map(offering => offering.structure._id))];

          // Get complete structure details for each unique structure
          const structuresPromises = structureIds.map(id =>
            axios.get(`${process.env.VUE_APP_API_BASE_URL}/program-structures/${id}`)
          );

          const structuresResponses = await Promise.all(structuresPromises);
          return structuresResponses.map(res => res.data);
        }

        return [];
      } catch (error) {
        this.logger.error('Error fetching program structures:', error);
        return [];
      }
    },

    // Edit program
    editProgram(program) {
      this.editMode = true;
      this.editingItem = program;

      // First, fetch the complete program data, program structures, and program offerings
      Promise.all([
        this.fetchProgramById(program._id),
        this.fetchProgramStructures(program._id),
        this.fetchProgramOfferings(program._id)
      ])
        .then(([completeProgram, programStructures, programOfferings]) => {
          // Get minUnits and maxUnits from the first program offering (they should all be the same for a program)
          let minUnits = 15; // Default
          let maxUnits = 24; // Default

          if (programOfferings && programOfferings.length > 0) {
            minUnits = programOfferings[0].minUnits || 15;
            maxUnits = programOfferings[0].maxUnits || 24;
          }

          // Set up the program data for editing with complete data
          this.newProgram = {
            name: completeProgram.name,
            code: completeProgram.code,
            description: completeProgram.description || '',
            department: completeProgram.department || program.department?._id || '',
            minUnits: minUnits,
            maxUnits: maxUnits,
            active: completeProgram.active !== undefined ? completeProgram.active : true,
            programTypes: this.transformProgramStructuresForEdit(programStructures, programOfferings)
          };

          // If no program types exist, initialize with default
          if (this.newProgram.programTypes.length === 0) {
            this.addProgramType();
          }

          // Use the program data from the list which contains department info
          // Combine it with the fetched program data for a complete picture
          const enhancedProgram = {
            ...completeProgram,
            department: program.department, // Use department from program list data
            minUnits: minUnits,
            maxUnits: maxUnits,
            active: completeProgram.active !== undefined ? completeProgram.active : true
          };

          // Store enhanced program for back navigation
          this.cachedEnhancedProgram = enhancedProgram;

          this.showEditProgramModal(program, enhancedProgram);
        })
        .catch(error => {
          this.logger.error('Error fetching complete program data:', error);
          Swal.fire('Error', 'Failed to load program data for editing', 'error');
        });
    },

    // Fetch program offerings for a specific program
    async fetchProgramOfferings(programId) {
      try {
        const response = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/program-offerings/program/${programId}`);
        return response.data || [];
      } catch (error) {
        this.logger.warn('Error fetching program offerings:', error);
        return [];
      }
    },

    // Transform program types data from API format to modal format
    transformProgramTypesForEdit(programTypes) {
      if (!programTypes || !Array.isArray(programTypes)) {
        return [];
      }

      return programTypes.map(pt => ({
        type: pt.type || '',
        mode: pt.mode || '',
        academicSessions: pt.academicSessions || pt.sessions?.length || 2,
        sessions: pt.sessions || Array.from({ length: pt.academicSessions || 2 }, () => ({ semesters: 2 }))
      }));
    },

    // Transform program structures data from API format to modal format for editing
    transformProgramStructuresForEdit(programStructures, programOfferings = []) {
      if (!programStructures || !Array.isArray(programStructures)) {
        return [];
      }

      return programStructures.map(structure => {
        const structureId = structure._id;

        // Find the corresponding program offering to get the active status
        const offering = programOfferings.find(po => {
          const poStructureId = po.structure?._id || po.structure;
          return poStructureId === structureId;
        });

        return {
          type: structure.type?.type || structure.type || '',
          mode: structure.mode?.mode || structure.mode || '',
          academicSessions: structure.years?.length || 2,
          sessions: structure.years?.map(year => ({
            semesters: year.semesters?.length || 2
          })) || Array.from({ length: structure.years?.length || 2 }, () => ({ semesters: 2 })),
          active: offering ? (offering.active !== false) : true // Get active status from offering
        };
      });
    },

    // Show the edit program modal with complete data
    showEditProgramModal(program, completeProgram) {
      // Generate HTML for program types section
      const generateProgramTypesHtml = () => {
        let html = '';

        this.newProgram.programTypes.forEach((pt, index) => {
          html += `
            <div class="program-type-item border rounded p-3 mb-3" style="background-color: #f8f9fa;">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h6 class="mb-0 fw-bold">Program Type ${index + 1}</h6>
                ${index > 0 ? `<button type="button" class="btn btn-sm btn-danger" onclick="window.removeType(${index})"><i class="bi bi-trash"></i> Remove</button>` : ''}
              </div>
              <div class="row mb-3">
                <div class="col-md-6">
                  <label class="form-label">Type <span class="text-danger">*</span></label>
                  <select id="type-${index}" class="form-select" onchange="window.handleTypeChange(${index})">
                    <option value="">Select Type</option>
                    ${(this.programTypes || []).filter(type => type && type._id).map(type => {
                      return `<option value="${type.type}" ${pt.type === type.type ? 'selected' : ''}>${type.type} - ${type.description}</option>`;
                    }).join('')}
                  </select>
                </div>
                <div class="col-md-6">
                  <label class="form-label">Mode <span class="text-danger">*</span></label>
                  <select id="mode-${index}" class="form-select" onchange="window.handleModeChange(${index})">
                    <option value="">Select Mode</option>
                    ${(this.programModes || []).filter(mode => mode && mode._id).map(mode => {
                      const currentType = pt.type;
                      const isDisabled = currentType && this.isTypeAndModeDisabled(currentType, mode.mode, index);
                      return `<option value="${mode.mode}" ${pt.mode === mode.mode ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}>${mode.mode} - ${mode.description}${isDisabled ? ' (Already used)' : ''}</option>`;
                    }).join('')}
                  </select>
                </div>
              </div>
              <div id="validation-message-${index}" class="alert alert-warning d-none mb-3">
                This combination of Type and Mode is already selected for another program type.
              </div>
              <div class="row mb-3">
                <div class="col-md-6">
                  <label class="form-label">Number of Academic Years <span class="text-danger">*</span></label>
                  <input type="number" id="sessions-${index}" class="form-control" min="1" max="6" value="${pt.academicSessions || 2}" onchange="window.generateSessionFields(${index})">
                </div>
                <div class="col-md-6">
                  <label class="form-label">Active Status</label>
                  <div class="form-check form-switch mt-2">
                    <input class="form-check-input" type="checkbox" role="switch" id="active-${index}" ${pt.active !== false ? 'checked' : ''} onchange="window.handleActiveToggle(${index})">
                    <label class="form-check-label" for="active-${index}">
                      <span id="activeLabel-${index}">${pt.active !== false ? 'Active' : 'Inactive'}</span>
                    </label>
                  </div>
                  <small class="text-muted">Inactive offerings cannot be selected by students</small>
                </div>
              </div>
              <div id="sessions-container-${index}">
                ${pt.sessions.map((session, sessionIndex) => `
                  <div class="session-item border-top pt-2 mt-2">
                    <h6 class="mb-2 text-muted">Session ${sessionIndex + 1}</h6>
                    <div class="mb-3">
                      <label class="form-label">Number of Semesters</label>
                      <input type="number" id="semesters-${index}-${sessionIndex}" class="form-control" min="1" max="3" value="${session.semesters || 2}">
                    </div>
                  </div>
                `).join('')}
              </div>
            </div>
          `;
        });

        return html;
      };

      Swal.fire({
        title: 'Edit Program',
        html: `
          <div style="max-height: 500px;" class="text-start py-3 overflow-y-scroll">
            <form id="editProgramForm">
              <!-- Basic Information Section -->
              <div class="mb-4">
                <h5 class="border-bottom pb-2 mb-3">Basic Information</h5>
                <div class="mb-3">
                  <label class="form-label">Program Code <span class="text-danger">*</span></label>
                  <input type="text" id="code" class="form-control" placeholder="e.g. CSC" maxlength="3" required value="${completeProgram.code}">
                </div>
                <div class="mb-3">
                  <label class="form-label">Program Name <span class="text-danger">*</span></label>
                  <input type="text" id="name" class="form-control" placeholder="e.g. Computer Science" required value="${completeProgram.name}">
                </div>
                <div class="row mb-3">
                  <div class="col-md-6">
                    <label class="form-label">School <span class="text-danger">*</span></label>
                    <select id="school" class="form-select" required onchange="window.fetchDepartmentsForSchool(this.value)">
                      <option value="">Select School</option>
                      ${(this.schools || []).filter(school => school && school._id).map(school => {
                        const isSelected = completeProgram.department &&
                                         completeProgram.department.school &&
                                         (completeProgram.department.school._id === school._id ||
                                          completeProgram.department.school === school._id);
                        return `<option value="${school._id}" ${isSelected ? 'selected' : ''}>${school.name}</option>`;
                      }).join('')}
                    </select>
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Department <span class="text-danger">*</span></label>
                    <select id="department" class="form-select" required>
                      <option value="">Select Department</option>
                    </select>
                  </div>
                </div>
                <div class="mb-3">
                  <label class="form-label">Description</label>
                  <textarea id="description" class="form-control" rows="3">${completeProgram.description || ''}</textarea>
                </div>
                <div class="row mb-3">
                  <div class="col-md-6">
                    <label class="form-label">Minimum Units <span class="text-danger">*</span></label>
                    <input type="number" id="minUnits" class="form-control" min="1" max="50" required value="${completeProgram.minUnits || 15}" placeholder="e.g. 15">
                    <small class="text-muted">Minimum credit units per semester</small>
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Maximum Units <span class="text-danger">*</span></label>
                    <input type="number" id="maxUnits" class="form-control" min="1" max="50" required value="${completeProgram.maxUnits || 24}" placeholder="e.g. 24">
                    <small class="text-muted">Maximum credit units per semester</small>
                  </div>
                </div>
                <div class="mb-3">
                  <label class="form-label">Status</label>
                  <div class="form-check form-switch">
                    <input class="form-check-input" type="checkbox" role="switch" id="active" ${completeProgram.active !== false ? 'checked' : ''}>
                    <label class="form-check-label" for="active">
                      <span id="activeLabel">${completeProgram.active !== false ? 'Active' : 'Inactive'}</span>
                    </label>
                  </div>
                  <small class="text-muted">Inactive programs cannot be selected for new admissions</small>
                </div>
              </div>

              <!-- Program Types Section -->
              <div class="mb-4">
                <h5 class="border-bottom pb-2 mb-3">Program Types Configuration</h5>
                <div id="program-types-container">
                  ${generateProgramTypesHtml()}
                </div>
                <button type="button" class="btn btn-sm btn-outline-primary" onclick="window.addType()">
                  <i class="bi bi-plus-circle"></i> Add Program Type
                </button>
              </div>
            </form>
          </div>
        `,
        width: '900px',
        showCancelButton: true,
        cancelButtonText: 'Cancel',
        confirmButtonText: 'Update Program',
        confirmButtonColor: '#28a745',
        didOpen: () => {
          // Setup active status toggle label
          const activeCheckbox = document.getElementById('active');
          const activeLabel = document.getElementById('activeLabel');
          if (activeCheckbox && activeLabel) {
            activeCheckbox.addEventListener('change', () => {
              activeLabel.textContent = activeCheckbox.checked ? 'Active' : 'Inactive';
            });
          }

          // Setup department population function
          window.fetchDepartmentsForSchool = async (schoolId) => {
            if (!schoolId) {
              document.getElementById('department').innerHTML = '<option value="">Select Department</option>';
              return;
            }

            try {
              const departments = await this.fetchDepartmentsBySchool(schoolId);
              const departmentSelect = document.getElementById('department');
              departmentSelect.innerHTML = '<option value="">Select Department</option>' +
                departments.map(dept => `<option value="${dept._id}">${dept.name}</option>`).join('');
            } catch (error) {
              this.logger.error('Error fetching departments:', error);
            }
          };

          // Pre-populate departments for the selected school
          if (completeProgram.department) {
            let schoolId = null;
            if (completeProgram.department.school) {
              schoolId = completeProgram.department.school._id || completeProgram.department.school;
            }

            if (schoolId) {
              this.fetchDepartmentsBySchool(schoolId)
                .then(departments => {
                  const departmentSelect = document.getElementById('department');
                  departmentSelect.innerHTML = '<option value="">Select Department</option>' +
                    departments.map(dept => {
                      const isSelected = dept._id === completeProgram.department._id;
                      return `<option value="${dept._id}" ${isSelected ? 'selected' : ''}>${dept.name}</option>`;
                    }).join('');
                })
                .catch(error => {
                  this.logger.error('Error pre-populating departments:', error);
                });
            }
          }

          // Define functions for program types management
          window.addType = () => {
            this.addProgramType();
            document.getElementById('program-types-container').innerHTML = generateProgramTypesHtml();
          };

          window.removeType = (index) => {
            this.removeProgramType(index);
            document.getElementById('program-types-container').innerHTML = generateProgramTypesHtml();
          };

          window.handleTypeChange = (index) => {
            const typeValue = document.getElementById(`type-${index}`).value;
            const modeValue = document.getElementById(`mode-${index}`).value;

            this.newProgram.programTypes[index].type = typeValue;

            // Check if this type/mode combination already exists
            if (typeValue && modeValue) {
              const isDuplicate = this.isTypeAndModeDisabled(typeValue, modeValue, index);
              const validationMessage = document.getElementById(`validation-message-${index}`);
              if (isDuplicate) {
                validationMessage.classList.remove('d-none');
              } else {
                validationMessage.classList.add('d-none');
              }
            }

            // Refresh the mode options for this index
            document.getElementById('program-types-container').innerHTML = generateProgramTypesHtml();
          };

          window.handleModeChange = (index) => {
            const modeValue = document.getElementById(`mode-${index}`).value;
            const typeValue = document.getElementById(`type-${index}`).value;

            this.newProgram.programTypes[index].mode = modeValue;

            // Check if this type/mode combination already exists
            if (typeValue && modeValue) {
              const isDuplicate = this.isTypeAndModeDisabled(typeValue, modeValue, index);
              const validationMessage = document.getElementById(`validation-message-${index}`);
              if (isDuplicate) {
                validationMessage.classList.remove('d-none');
              } else {
                validationMessage.classList.add('d-none');
              }
            }
          };

          window.generateSessionFields = (index) => {
            const sessionCount = parseInt(document.getElementById(`sessions-${index}`).value) || 0;
            this.newProgram.programTypes[index].academicSessions = sessionCount;
            this.newProgram.programTypes[index].sessions = Array.from({ length: sessionCount }, () => ({ semesters: 2 }));
            document.getElementById('program-types-container').innerHTML = generateProgramTypesHtml();
          };

          window.handleActiveToggle = (index) => {
            const isActive = document.getElementById(`active-${index}`).checked;
            this.newProgram.programTypes[index].active = isActive;
            const label = document.getElementById(`activeLabel-${index}`);
            if (label) {
              label.textContent = isActive ? 'Active' : 'Inactive';
            }
          };
        },
        preConfirm: () => {
          // Validate basic fields
          const code = document.getElementById('code').value.trim();
          const name = document.getElementById('name').value.trim();
          const department = document.getElementById('department').value;
          const description = document.getElementById('description').value.trim();
          const minUnits = parseInt(document.getElementById('minUnits').value);
          const maxUnits = parseInt(document.getElementById('maxUnits').value);
          const active = document.getElementById('active').checked;

          if (!code || !name || !department) {
            Swal.showValidationMessage('Please fill all required fields');
            return false;
          }

          if (!minUnits || minUnits < 1) {
            Swal.showValidationMessage('Minimum units must be at least 1');
            return false;
          }

          if (!maxUnits || maxUnits < 1) {
            Swal.showValidationMessage('Maximum units must be at least 1');
            return false;
          }

          if (maxUnits < minUnits) {
            Swal.showValidationMessage('Maximum units must be greater than or equal to minimum units');
            return false;
          }

          // Collect program types data
          const programTypes = [];
          for (let i = 0; i < this.newProgram.programTypes.length; i++) {
            const type = document.getElementById(`type-${i}`)?.value;
            const mode = document.getElementById(`mode-${i}`)?.value;
            const academicSessions = parseInt(document.getElementById(`sessions-${i}`)?.value) || 2;
            const active = document.getElementById(`active-${i}`)?.checked;

            if (!type || !mode) {
              Swal.showValidationMessage(`Please select both Type and Mode for Program Type ${i + 1}`);
              return false;
            }

            // Check for duplicate type/mode combinations
            if (this.isTypeAndModeDisabled(type, mode, i)) {
              Swal.showValidationMessage(`Program Type ${i + 1} has a duplicate Type/Mode combination`);
              return false;
            }

            const sessions = [];
            for (let j = 0; j < academicSessions; j++) {
              const semesters = parseInt(document.getElementById(`semesters-${i}-${j}`)?.value) || 2;
              sessions.push({ semesters });
            }

            programTypes.push({
              type,
              mode,
              academicSessions,
              sessions,
              active
            });
          }

          if (programTypes.length === 0) {
            Swal.showValidationMessage('Please add at least one program type');
            return false;
          }

          return {
            code,
            name,
            department,
            description,
            minUnits,
            maxUnits,
            active,
            programTypes
          };
        }
      }).then((result) => {
        if (result.isConfirmed) {
          // Update all program data
          this.newProgram.code = result.value.code;
          this.newProgram.name = result.value.name;
          this.newProgram.department = result.value.department;
          this.newProgram.description = result.value.description;
          this.newProgram.minUnits = result.value.minUnits;
          this.newProgram.maxUnits = result.value.maxUnits;
          this.newProgram.active = result.value.active;
          this.newProgram.programTypes = result.value.programTypes;

          // Call the update method
          this.updateExistingProgram();
        }
      });
    },

    // Delete program
    async deleteProgram(programId) {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: 'This will permanently delete the program and all associated data (program offerings and structures)!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete everything!'
      });

      if (result.isConfirmed) {
        try {
          this.loading = true;

          // Step 1: Get all program offerings for this program
          let programOfferings = [];
          try {
            const offeringsResponse = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/program-offerings/program/${programId}`);
            programOfferings = offeringsResponse.data || [];
          } catch (error) {
            this.logger.warn('No program offerings found or error fetching:', error);
          }

          // Step 2: Delete all program offerings for this program
          for (const offering of programOfferings) {
            try {
              await axios.delete(`${process.env.VUE_APP_API_BASE_URL}/program-offerings/${offering._id}`);
              this.logger.info(`Deleted program offering: ${offering._id}`);
            } catch (error) {
              this.logger.warn(`Failed to delete program offering ${offering._id}:`, error);
            }
          }

          // Step 3: Get unique program structure IDs from the offerings
          const structureIds = [...new Set(programOfferings
            .filter(offering => offering.structure && offering.structure._id)
            .map(offering => offering.structure._id))];

          // Step 4: Delete program structures (only if they're not used by other programs)
          for (const structureId of structureIds) {
            try {
              // Check if this structure is used by other programs
              const otherOfferingsResponse = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/program-offerings`);
              const otherOfferings = otherOfferingsResponse.data || [];

              const isUsedByOtherPrograms = otherOfferings.some(offering =>
                offering.structure &&
                offering.structure._id === structureId &&
                offering.program &&
                offering.program._id !== programId
              );

              if (!isUsedByOtherPrograms) {
                await axios.delete(`${process.env.VUE_APP_API_BASE_URL}/program-structures/${structureId}`);
                this.logger.info(`Deleted program structure: ${structureId}`);
              } else {
                this.logger.info(`Program structure ${structureId} kept (used by other programs)`);
              }
            } catch (error) {
              this.logger.warn(`Failed to delete program structure ${structureId}:`, error);
            }
          }

          // Step 5: Finally, delete the program itself
          await axios.delete(`${process.env.VUE_APP_API_BASE_URL}/programs/${programId}`);
          this.logger.info(`Deleted program: ${programId}`);

          // Step 6: Refresh the programs list
          await this.fetchPrograms();

          Swal.fire({
            title: 'Deleted!',
            text: `The program and all associated data have been successfully deleted.`,
            icon: 'success',
            timer: 2000
          });

        } catch (err) {
          this.logger.error('Error deleting program:', err);

          Swal.fire({
            title: 'Error!',
            text: `Failed to delete program: ${err.response?.data?.message || err.message}`,
            icon: 'error'
          });
        } finally {
          this.loading = false;
        }
      }
    },

    async addProgram() {
      try {
        if (!this.newProgram.department) {
          this.error = 'Please select a department';
          return;
        }

        // Check if a program with the same code already exists
        const existingProgram = this.programs.find(program => program.code === this.newProgram.code);
        if (existingProgram && !this.editMode) {
          this.error = `A program with code "${this.newProgram.code}" already exists. Please use a different code.`;
          Swal.fire({
            title: 'Duplicate Code',
            text: `A program with code "${this.newProgram.code}" already exists. Please use a different code.`,
            icon: 'error'
          });
          return;
        }

        // Validate program types
        if (this.newProgram.programTypes.length === 0) {
          this.error = 'Please add at least one program type configuration';
          Swal.fire({
            title: 'Missing Program Type',
            text: 'Please add at least one program type configuration',
            icon: 'error'
          });
          return;
        }

        // Validate each program type has required fields
        for (const pt of this.newProgram.programTypes) {
          if (!pt.type || !pt.mode || !pt.academicSessions) {
            this.error = 'Please complete all program type fields';
            Swal.fire({
              title: 'Incomplete Program Type',
              text: 'Please complete all program type fields',
              icon: 'error'
            });
            return;
          }
        }

        // Check for duplicate program type and mode combinations
        const typeModeMap = new Map();
        for (let i = 0; i < this.newProgram.programTypes.length; i++) {
          const pt = this.newProgram.programTypes[i];
          const key = `${pt.type}-${pt.mode}`;

          if (typeModeMap.has(key)) {
            // Get the actual descriptions from the database arrays
            const typeObj = this.programTypes.find(t => t.type === pt.type);
            const modeObj = this.programModes.find(m => m.mode === pt.mode);
            const typeDesc = typeObj ? typeObj.description : pt.type;
            const modeDesc = modeObj ? modeObj.description : pt.mode;

            this.error = `Duplicate program type found: ${typeDesc} (${modeDesc})`;
            Swal.fire({
              title: 'Duplicate Program Type',
              text: `You have already added ${typeDesc} (${modeDesc}). Please use unique type and mode combinations.`,
              icon: 'error'
            });
            return;
          }

          typeModeMap.set(key, i);
        }

        // Set programme type flags from the configured programme types.
        // These flags are more generic now and can work with any program types/modes from DB
        this.newProgram.isHND = this.newProgram.programTypes.some(pt => {
          const typeObj = this.programTypes.find(t => t.type === pt.type);
          return typeObj && typeObj.type.toUpperCase().includes('HND');
        });
        this.newProgram.isND = this.newProgram.programTypes.some(pt => {
          const typeObj = this.programTypes.find(t => t.type === pt.type);
          return typeObj && typeObj.type.toUpperCase().includes('ND') && !typeObj.type.toUpperCase().includes('HND');
        });
        this.newProgram.isPartTime = this.newProgram.programTypes.some(pt => {
          const modeObj = this.programModes.find(m => m.mode === pt.mode);
          return modeObj && (modeObj.mode.toUpperCase().includes('PT') || modeObj.description.toLowerCase().includes('part'));
        });
        this.newProgram.isFullTime = this.newProgram.programTypes.some(pt => {
          const modeObj = this.programModes.find(m => m.mode === pt.mode);
          return modeObj && (modeObj.mode.toUpperCase().includes('FT') || modeObj.description.toLowerCase().includes('full'));
        });

        if (this.editMode && this.editingItem) {
          // Update existing program
          await this.updateExistingProgram();
        } else {
          // Validate required fields
          if (!this.newProgram.name || !this.newProgram.code || !this.newProgram.department) {
            throw new Error('Program name, code, and department are required');
          }

          // Validate program types
          if (!this.newProgram.programTypes.length) {
            throw new Error('At least one program type is required');
          }

          // Validate each program type
          for (const pt of this.newProgram.programTypes) {
            if (!pt.type || !pt.mode) {
              throw new Error('Program type and mode are required for all configurations');
            }
            if (!pt.academicSessions || pt.academicSessions < 1) {
              throw new Error('Number of academic sessions must be at least 1');
            }
          }

          // Create new program
          const programResponse = await axios.post(
            `${process.env.VUE_APP_API_BASE_URL}/programs`,
            {
              name: this.newProgram.name.trim(),
              code: this.newProgram.code.trim(),
              description: this.newProgram.description,
              department: this.newProgram.department,
              active: this.newProgram.active !== undefined ? this.newProgram.active : true
            }
          ).catch(error => {
            // Headers not needed - cookies are automatic
            if (error.response && error.response.data && error.response.data.msg) {
              throw new Error(error.response.data.msg);
            }
            throw error;
          });

          // Get min/max units from the form data
          const minUnits = this.newProgram.minUnits || 15;
          const maxUnits = this.newProgram.maxUnits || 24;
          const programActive = this.newProgram.active !== undefined ? this.newProgram.active : true;

          // Create program structures and offerings for each program type
          for (const pt of this.newProgram.programTypes) {
            // Get program type ID first
            const typeResponse = await axios.get(
              `${process.env.VUE_APP_API_BASE_URL}/programtypes?type=${pt.type}`
            );
            // Headers not needed - cookies are automatic

            // Get program mode ID
            const modeResponse = await axios.get(
              `${process.env.VUE_APP_API_BASE_URL}/programmodes?mode=${pt.mode}`
            );
            // Headers not needed - cookies are automatic

            this.logger.info('Type response:', typeResponse.data);
            this.logger.info('Mode response:', modeResponse.data);

            if (!typeResponse.data.length || !modeResponse.data.length) {
              throw new Error('Program type or mode not found');
            }

            // Create program structure with ObjectIds instead of strings
            const structureData = {
              type: typeResponse.data[0]._id, // Use ObjectId instead of string
              mode: modeResponse.data[0]._id, // Use ObjectId instead of string
              years: Array.from({ length: pt.academicSessions }, (_, i) => ({
                year: i + 1,
                semesters: Array.from({ length: pt.sessions[i]?.semesters || 2 }, (_, j) => String(j + 1))
              }))
            };

            const structureResponse = await axios.post(
              `${process.env.VUE_APP_API_BASE_URL}/program-structures`,
              structureData
              // Headers not needed - cookies are automatic
            );

            if (!typeResponse.data.length || !modeResponse.data.length) {
              throw new Error('Program type or mode not found');
            }

            // Create program offering with per-offering active status
            const offeringData = {
              program: programResponse.data._id,
              department: this.newProgram.department,
              type: typeResponse.data[0]._id,
              mode: modeResponse.data[0]._id,
              structure: structureResponse.data._id,
              minUnits: minUnits,
              maxUnits: maxUnits,
              active: pt.active !== undefined ? pt.active : programActive
            };

            await axios.post(
              `${process.env.VUE_APP_API_BASE_URL}/program-offerings`,
              offeringData
            );
            // Headers not needed - cookies are automatic
          }

          Swal.fire({
            title: 'Success!',
            text: 'Program added successfully',
            icon: 'success',
            timer: 1500
          });
        }

        await this.fetchPrograms();

        // Reset form but keep department
        this.newProgram = {
          name: '',
          description: '',
          code: '',
          department: this.newProgram.department,
          isHND: false,
          isND: false,
          isPartTime: false,
          isFullTime: false,
          programTypes: []
        };

        // Initialize with one program type by default
        this.addProgramType();
        this.editMode = false;
        this.editingItem = null;
      } catch (err) {
        this.logger.error(err);
        let errorMessage = 'Failed to add/update program';
        let errorTitle = 'Error';

        // Handle specific error cases
        if (err.response && err.response.data) {
          if (err.response.data.message && err.response.data.message.includes('duplicate key error')) {
            errorMessage = 'A program with this code already exists. Please use a different code.';
            errorTitle = 'Duplicate Code Error';
          } else if (err.response.data.msg) {
            errorMessage = err.response.data.msg;
          }
        } else if (err.message) {
          errorMessage = err.message;
        }

        this.error = errorMessage;
        Swal.fire({
          title: errorTitle,
          text: errorMessage,
          icon: 'error'
        });
      }
    },

    // Update existing program with proper handling of program types
    async updateExistingProgram() {
      try {
        // Ensure program types and modes are loaded
        if (!this.programTypes.length) {
          await this.fetchProgramTypes();
        }
        if (!this.programModes.length) {
          await this.fetchProgramModes();
        }

        // 1. Update the basic program information including active status
        await axios.put(
          `${process.env.VUE_APP_API_BASE_URL}/programs/${this.editingItem._id}`,
          {
            name: this.newProgram.name,
            code: this.newProgram.code,
            description: this.newProgram.description,
            department: this.newProgram.department,
            active: this.newProgram.active !== undefined ? this.newProgram.active : true,
            programTypes: this.newProgram.programTypes
          }
        );

        // 2. Get existing program offerings for this program
        const existingOfferingsResponse = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/program-offerings/program/${this.editingItem._id}`
        );
        const existingOfferings = existingOfferingsResponse.data || [];

        // Get form values
        const minUnits = this.newProgram.minUnits || 15;
        const maxUnits = this.newProgram.maxUnits || 24;
        const programActive = this.newProgram.active !== undefined ? this.newProgram.active : true;

        // 3. Build a map of new program types/modes that should exist
        const newOfferingsMap = new Map(); // Key: structureId, Value: offering data

        for (const pt of this.newProgram.programTypes) {
          // Get program type ObjectId
          const typeObj = this.programTypes.find(type => type.type === pt.type);
          if (!typeObj) {
            throw new Error(`Program type "${pt.type}" not found`);
          }

          // Get program mode ObjectId
          const modeObj = this.programModes.find(mode => mode.mode === pt.mode);
          if (!modeObj) {
            throw new Error(`Program mode "${pt.mode}" not found`);
          }

          // Find or create program structure
          let structureId;
          const structureData = {
            type: typeObj._id,
            mode: modeObj._id,
            years: Array.from({ length: pt.academicSessions }, (_, i) => ({
              year: i + 1,
              semesters: Array.from({ length: pt.sessions[i]?.semesters || 2 }, (_, j) => String(j + 1))
            }))
          };

          try {
            const existingStructureResponse = await axios.get(
              `${process.env.VUE_APP_API_BASE_URL}/program-structures/type/${typeObj._id}/mode/${modeObj._id}`
            );

            if (existingStructureResponse.data && existingStructureResponse.data.length > 0) {
              const existingStructure = existingStructureResponse.data[0];
              structureId = existingStructure._id;

              // Check if years configuration changed
              const yearsChanged =
                existingStructure.years.length !== structureData.years.length ||
                existingStructure.years.some((year, idx) => {
                  const newYear = structureData.years[idx];
                  if (!newYear) return true;
                  return year.semesters.length !== newYear.semesters.length;
                });

              // Update structure if years/semesters changed
              if (yearsChanged) {
                this.logger.info(`Updating program structure ${structureId} with new years configuration`);
                await axios.put(
                  `${process.env.VUE_APP_API_BASE_URL}/program-structures/${structureId}`,
                  structureData
                );
              }
            } else {
              // Create new structure if none exists
              const structureResponse = await axios.post(
                `${process.env.VUE_APP_API_BASE_URL}/program-structures`,
                structureData
              );

              if (!structureResponse.data || !structureResponse.data._id) {
                throw new Error('Failed to create program structure - no ID returned');
              }

              structureId = structureResponse.data._id;
            }
          } catch (error) {
            // Create new structure if lookup fails
            const structureResponse = await axios.post(
              `${process.env.VUE_APP_API_BASE_URL}/program-structures`,
              structureData
            );

            if (!structureResponse.data || !structureResponse.data._id) {
              throw new Error('Failed to create program structure - no ID returned');
            }

            structureId = structureResponse.data._id;
          }

          // Add to map of offerings that should exist
          newOfferingsMap.set(structureId, {
            program: this.editingItem._id,
            department: this.newProgram.department,
            structure: structureId,
            minUnits: minUnits,
            maxUnits: maxUnits,
            active: pt.active !== undefined ? pt.active : programActive
          });
        }

        // 4. Update or create offerings based on what exists
        const processedStructureIds = new Set();

        // Update existing offerings or mark them for deletion
        for (const existingOffering of existingOfferings) {
          const structureId = existingOffering.structure._id || existingOffering.structure;

          if (newOfferingsMap.has(structureId)) {
            // This offering should exist - update it if needed
            const newData = newOfferingsMap.get(structureId);

            // Check if any data changed
            const needsUpdate =
              existingOffering.minUnits !== newData.minUnits ||
              existingOffering.maxUnits !== newData.maxUnits ||
              existingOffering.active !== newData.active ||
              (existingOffering.department._id || existingOffering.department) !== newData.department;

            if (needsUpdate) {
              // Update the existing offering
              await axios.put(
                `${process.env.VUE_APP_API_BASE_URL}/program-offerings/${existingOffering._id}`,
                {
                  minUnits: newData.minUnits,
                  maxUnits: newData.maxUnits,
                  active: newData.active,
                  department: newData.department
                }
              );
              this.logger.info(`Updated program offering ${existingOffering._id}`);
            } else {
              this.logger.info(`Program offering ${existingOffering._id} unchanged, skipping update`);
            }

            processedStructureIds.add(structureId);
          } else {
            // This offering should no longer exist - delete it
            await axios.delete(`${process.env.VUE_APP_API_BASE_URL}/program-offerings/${existingOffering._id}`);
            this.logger.info(`Deleted obsolete program offering ${existingOffering._id}`);
          }
        }

        // 5. Create new offerings for program types that didn't exist before
        for (const [structureId, offeringData] of newOfferingsMap.entries()) {
          if (!processedStructureIds.has(structureId)) {
            // This is a new offering - create it
            try {
              await axios.post(
                `${process.env.VUE_APP_API_BASE_URL}/program-offerings`,
                offeringData
              );
              this.logger.info(`Created new program offering for structure ${structureId}`);
            } catch (offeringError) {
              this.logger.error('Error creating program offering. Data sent:', offeringData);
              this.logger.error('Offering creation error:', offeringError.response?.data || offeringError.message);
              throw offeringError;
            }
          }
        }

        Swal.fire({
          title: 'Success!',
          text: 'Program updated successfully',
          icon: 'success',
          timer: 1500
        });

      } catch (error) {
        this.logger.error('Error updating program:', error);
        throw error;
      }
    },

    // Program Type Management Methods
    addProgramType() {
      this.newProgram.programTypes.push({
        type: '',
        mode: '',
        academicSessions: 0,
        sessions: [],
        active: true // Default new program types to active
      });
    },
    removeProgramType(index) {
      this.newProgram.programTypes.splice(index, 1);
    },
    generateSessionFields(index) {
      const programType = this.newProgram.programTypes[index];
      const sessionCount = parseInt(programType.academicSessions) || 0;

      // Reset sessions array
      programType.sessions = [];

      // Generate session fields
      for (let i = 0; i < sessionCount; i++) {
        programType.sessions.push({
          semesters: 2 // Default value
        });
      }
    },
    isTypeAndModeDisabled(type, mode, currentIndex) {
      // If either type or mode is not selected, don't disable anything
      if (!type || !mode) return false;

      // Check if this combination already exists in another program type entry
      return this.newProgram.programTypes.some((pt, index) =>
        index !== currentIndex && // Skip the current entry being edited
        pt.type === type &&
        pt.mode === mode
      );
    },
    updateDefaultSessions(index) {
      const programType = this.newProgram.programTypes[index];
      if (programType.type && programType.mode) {
        // Set default number of sessions (can be customized based on type and mode)
        // For now, using a general default of 2 years
        programType.academicSessions = 2;

        // You can add logic here to set different defaults based on type and mode
        // from the database if needed

        this.generateSessionFields(index);
      }
    },

    // Update program in database
    async updateProgram(programData) {
      try {
        this.loading = true;

        // Check if another program with the same code already exists (excluding the current program)
        const existingProgram = this.programs.find(p => p.code === programData.code && p._id !== programData._id);
        if (existingProgram) {
          this.error = `Another program with code "${programData.code}" already exists. Please use a different code.`;
          Swal.fire({
            title: 'Duplicate Code',
            text: `Another program with code "${programData.code}" already exists. Please use a different code.`,
            icon: 'error'
          });
          return;
        }

        const response = await axios.put(
          `${process.env.VUE_APP_API_BASE_URL}/programs/${programData._id}`,
          programData
          // Headers not needed - cookies are automatic
        );

        if (response.data) {
          Swal.fire('Success', 'Program updated successfully', 'success');
          this.fetchPrograms();
        } else {
          Swal.fire('Error', 'Failed to update program', 'error');
        }
      } catch (error) {
        this.logger.error('Error updating program:', error);
        Swal.fire('Error', error.response?.data?.message || 'Failed to update program', 'error');
      } finally {
        this.loading = false;
        this.editMode = false;
        this.editingItem = null;
      }
    },

    // Change current tabmethods: {
    changeTab(tab) {
      this.currentTab = tab;
    },

    // Program Session Payments Methods
    fetchProgramSessionPayments() {
      this.loading = true;
      axios.get('/program-session-payments')
        // Headers not needed - cookies are automatic
        .then(response => {
          this.programSessionPayments = response.data;
          this.loading = false;
        })
        .catch((error) => {
          this.logger.error('Error fetching program session payments:', error);
          this.$toast.error('Failed to load program session payments');
          this.loading = false;
        });
    },

    fetchPaymentTypes() {
      axios.get('/payments')
        // Headers not needed - cookies are automatic
        .then(response => {
          // Filter out any undefined, null, or invalid objects
          this.paymentTypes = (response.data || []).filter(type => type && type._id);
        })
        .catch(error => {
          this.logger.error('Error fetching payment types:', error);
          this.$toast.error('Failed to load payment types');
        });
    },

    getDefaultPaymentForm() {
      return {
        _id: null,
        payment: "",
        academicSession: "",
        programType: "",
        programMode: "",
        applicableTo: "students",
        targetUserIds: [],
        targetPrograms: [],
        paymentOrder: null,
        amount: 0,
        dueDate: "",
        applyLateFee: false,
        lateFee: 0,
        active: true
      };
    },

    getPaymentModal() {
      if (!this.paymentModalInstance && this.$refs.paymentModal) {
        this.paymentModalInstance = new this.$bootstrap.Modal(this.$refs.paymentModal);
      }

      return this.paymentModalInstance;
    },

    normalizePaymentTargetProgramValues(targetPrograms = []) {
      return (Array.isArray(targetPrograms) ? targetPrograms : [])
        .map(target => {
          const programOffering = target?.programOffering?._id || target?.programOffering || '';
          const year = target?.year;
          return programOffering && year ? `${programOffering}:${year}` : '';
        })
        .filter(Boolean);
    },

    parsePaymentTargetProgramValues(values = []) {
      return (Array.isArray(values) ? values : [])
        .map(value => {
          const [programOffering, year] = String(value || '').split(':');
          return {
            programOffering,
            year: parseInt(year, 10)
          };
        })
        .filter(target => target.programOffering && !Number.isNaN(target.year));
    },

    async openPaymentModal(mode = 'add') {
      this.paymentModalMode = mode;
      this.paymentFormError = '';
      this.paymentFormSubmitting = false;

      if (this.paymentForm.academicSession) {
        await this.loadPaymentTargetOptionsForSession(this.paymentForm.academicSession);
      } else {
        this.paymentTargetOptions = {
          students: [],
          applicants: [],
          programTargets: []
        };
      }

      this.paymentTargetProgramValues = this.normalizePaymentTargetProgramValues(
        this.paymentForm.targetPrograms
      );

      await this.$nextTick();
      const modal = this.getPaymentModal();
      modal?.show();
    },

    closePaymentModal() {
      this.paymentFormError = '';
      this.paymentFormSubmitting = false;
      this.getPaymentModal()?.hide();
    },

    formatProgramSessionPaymentApplicableTo(payment) {
      const targetProgramsCount = Array.isArray(payment?.targetPrograms)
        ? payment.targetPrograms.length
        : 0;
      const targetAccountsCount = Array.isArray(payment?.targetUserIds)
        ? payment.targetUserIds.length
        : 0;

      switch (payment?.applicableTo) {
        case 'students':
          return 'Students';
        case 'applicants':
          return 'Applicants';
        case 'individual_students':
          return `Individual Accounts (${targetAccountsCount})`;
        case 'specific_program':
          return `Specific Program (${targetProgramsCount})`;
        case 'new':
          return 'New Students';
        case 'returning':
          return 'Returning Students';
        case 'all':
        default:
          return 'All Applicants and Students';
      }
    },

    getPaymentAudienceSummary(applicableTo) {
      const studentCount = this.paymentTargetOptions.students.length;
      const applicantCount = this.paymentTargetOptions.applicants.length;
      const programCount = this.paymentTargetOptions.programTargets.length;

      switch (applicableTo) {
        case 'students':
          return `Applies to all admitted students with matric numbers in the selected session (${studentCount} found).`;
        case 'applicants':
          return `Applies to all applicants without matric numbers in the selected session (${applicantCount} found).`;
        case 'individual_students':
          return studentCount
            ? `Select one or more admitted students from the selected session (${studentCount} available).`
            : 'Select an academic session to load admitted students.';
        case 'specific_program':
          return programCount
            ? `Select one or more specific program targets from the available program offerings (${programCount} targets available).`
            : 'Select an academic session to load specific program targets.';
        case 'new':
          return 'Applies to year-one applicants and students for the selected programme filters.';
        case 'returning':
          return 'Applies to returning students after year one for the selected programme filters.';
        case 'all':
        default:
          return 'Applies to applicants and students for the selected programme filters.';
      }
    },

    async loadPaymentTargetOptionsForSession(academicSession) {
      if (!academicSession) {
        this.paymentTargetOptions = {
          students: [],
          applicants: [],
          programTargets: []
        };
        return;
      }

      try {
        const response = await axios.get('/program-session-payments/target-options', {
          params: { academicSession }
        });

        this.paymentTargetOptions = {
          students: Array.isArray(response.data?.students) ? response.data.students : [],
          applicants: Array.isArray(response.data?.applicants) ? response.data.applicants : [],
          programTargets: Array.isArray(response.data?.programTargets) ? response.data.programTargets : []
        };
      } catch (error) {
        this.logger.error('Error fetching payment target options:', error);
        this.paymentTargetOptions = {
          students: [],
          applicants: [],
          programTargets: []
        };
      }
    },

    async addProgramSessionPayment() {
      this.paymentForm = this.getDefaultPaymentForm();
      this.paymentTargetOptions = {
        students: [],
        applicants: [],
        programTargets: []
      };
      this.paymentTargetProgramValues = [];
      await this.openPaymentModal('add');
    },

    async editProgramSessionPayment(payment) {
      const supportedApplicableAudiences = [
        'students',
        'applicants',
        'individual_students',
        'specific_program',
        'new',
        'returning',
        'all'
      ];

      // Set form data with null checks for nested objects
      this.paymentForm = {
        _id: payment._id,
        payment: payment.payment?._id || '',
        academicSession: payment.academicSession?._id || '',
        programType: payment.programType?._id || '',
        programMode: payment.programMode?._id || '',
        applicableTo: supportedApplicableAudiences.includes(payment.applicableTo)
          ? payment.applicableTo
          : '',
        targetUserIds: Array.isArray(payment.targetUserIds)
          ? payment.targetUserIds.map(user => user?._id || user).filter(Boolean)
          : [],
        targetPrograms: Array.isArray(payment.targetPrograms)
          ? payment.targetPrograms
            .map(target => ({
              programOffering: target?.programOffering?._id || target?.programOffering || '',
              year: target?.year
            }))
            .filter(target => target.programOffering && target.year)
          : [],
        paymentOrder: payment.paymentOrder,
        amount: payment.amount,
        dueDate: payment.dueDate ? new Date(payment.dueDate).toISOString().split('T')[0] : '',
        applyLateFee: Boolean(payment.applyLateFee),
        lateFee: payment.lateFee,
        active: payment.active
      };

      this.paymentTargetOptions = {
        students: [],
        applicants: [],
        programTargets: []
      };
      this.paymentTargetProgramValues = this.normalizePaymentTargetProgramValues(
        this.paymentForm.targetPrograms
      );

      await this.openPaymentModal('edit');
    },

    async handlePaymentAcademicSessionChange() {
      this.paymentForm.targetUserIds = [];
      this.paymentForm.targetPrograms = [];
      this.paymentTargetProgramValues = [];
      this.paymentFormError = '';

      if (!this.paymentForm.academicSession) {
        this.paymentTargetOptions = {
          students: [],
          applicants: [],
          programTargets: []
        };
        return;
      }

      await this.loadPaymentTargetOptionsForSession(this.paymentForm.academicSession);
    },

    handlePaymentApplicableToChange() {
      this.paymentFormError = '';
      this.paymentForm.programType = '';
      this.paymentForm.programMode = '';

      if (!this.shouldShowPaymentStudentTargets) {
        this.paymentForm.targetUserIds = [];
      }

      if (!this.shouldShowPaymentProgramTargets) {
        this.paymentTargetProgramValues = [];
        this.paymentForm.targetPrograms = [];
      }
    },

    handlePaymentTypeSelection() {
      const selectedPayment = this.paymentTypes.find(
        type => type._id === this.paymentForm.payment
      );

      if (selectedPayment && selectedPayment.amount) {
        this.paymentForm.amount = Number(selectedPayment.amount);
      }
    },

    syncSpecificProgramTargets() {
      this.paymentForm.targetPrograms = this.parsePaymentTargetProgramValues(
        this.paymentTargetProgramValues
      );
    },

    validatePaymentForm() {
      if (
        !this.paymentForm.payment ||
        !this.paymentForm.amount ||
        !this.paymentForm.academicSession ||
        !this.paymentForm.applicableTo
      ) {
        return 'Please fill all required fields';
      }

      if (this.shouldShowPaymentStudentTargets && this.paymentForm.targetUserIds.length === 0) {
        return 'Select at least one student for Individual Students';
      }

      if (this.shouldShowPaymentProgramTargets && this.paymentTargetProgramValues.length === 0) {
        return 'Select at least one program target for Specific Program';
      }

      return '';
    },

    buildPaymentPayload() {
      return {
        payment: this.paymentForm.payment,
        amount: Number(this.paymentForm.amount),
        academicSession: this.paymentForm.academicSession,
        programType: null,
        programMode: null,
        applicableTo: this.paymentForm.applicableTo,
        targetUserIds: this.shouldShowPaymentStudentTargets
          ? this.paymentForm.targetUserIds
          : [],
        targetPrograms: this.shouldShowPaymentProgramTargets
          ? this.parsePaymentTargetProgramValues(this.paymentTargetProgramValues)
          : [],
        paymentOrder: this.paymentForm.paymentOrder ? parseInt(this.paymentForm.paymentOrder, 10) : null,
        dueDate: this.paymentForm.dueDate || null,
        applyLateFee: Boolean(this.paymentForm.applyLateFee),
        lateFee: this.paymentForm.applyLateFee ? Number(this.paymentForm.lateFee || 0) : 0,
        active: this.paymentForm.active !== false
      };
    },

    async savePaymentFromModal() {
      this.paymentFormError = this.validatePaymentForm();
      if (this.paymentFormError) {
        return;
      }

      const paymentPayload = this.buildPaymentPayload();
      this.paymentFormSubmitting = true;

      try {
        await this.submitPaymentData(
          paymentPayload,
          this.paymentModalMode === 'edit'
        );

        this.closePaymentModal();
      } finally {
        this.paymentFormSubmitting = false;
      }
    },

    togglePaymentStatus(payment) {
      const newStatus = !payment.active;
      const action = newStatus ? 'activate' : 'deactivate';

      Swal.fire({
        title: `${action.charAt(0).toUpperCase() + action.slice(1)} Payment?`,
        text: `Are you sure you want to ${action} this payment?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes',
        cancelButtonText: 'No'
      }).then((result) => {
        if (result.isConfirmed) {
          axios.patch(`/program-session-payments/${payment._id}/toggle-status`, {})
            // Headers not needed - cookies are automatic
            .then(() => {
              Swal.fire({
                icon: 'success',
                title: 'Success',
                text: `Payment ${action}d successfully`
              });
              this.fetchProgramSessionPayments();
            })
            .catch(error => {
              this.logger.error(`Error ${action}ing payment:`, error);
              Swal.fire({
                icon: 'error',
                title: 'Error',
                text: `Failed to ${action} payment: ${error.response?.data?.message || error.message}`
              });
            });
        }
      });
    },

    deletePayment(payment) {
      Swal.fire({
        title: 'Delete Payment?',
        text: 'Are you sure you want to delete this payment? This action cannot be undone.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it',
        cancelButtonText: 'Cancel'
      }).then((result) => {
        if (result.isConfirmed) {
          axios.delete(`/program-session-payments/${payment._id}`)
            // Headers not needed - cookies are automatic
            .then(() => {
              Swal.fire({
                icon: 'success',
                title: 'Success',
                text: 'Payment deleted successfully'
              });
              this.fetchProgramSessionPayments();
            })
            .catch(error => {
              this.logger.error('Error deleting payment:', error);
              Swal.fire({
                icon: 'error',
                title: 'Error',
                text: `Failed to delete payment: ${error.response?.data?.message || error.message}`
              });
            });
        }
      });
    },

    async submitPaymentData(paymentData, isEdit = false) {
      this.loading = true;

      try {
        const request = isEdit && this.paymentForm._id
          ? axios.put(`/program-session-payments/${this.paymentForm._id}`, paymentData)
          // Headers not needed - cookies are automatic
          : axios.post('/program-session-payments', paymentData);
        // Headers not needed - cookies are automatic

        await request;

        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: `Payment ${isEdit ? 'updated' : 'added'} successfully`
        });

        this.fetchProgramSessionPayments();
      } catch (error) {
        this.logger.error(`Error ${isEdit ? 'updating' : 'creating'} program session payment:`, error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: `Failed to ${isEdit ? 'update' : 'add'} payment: ${error.response?.data?.message || error.message}`
        });
      } finally {
        this.loading = false;
      }
    },

    renderPaymentTypesTable() {
      const startIndex = (this.pagination.currentPage - 1) * this.pagination.itemsPerPage;
      const endIndex = startIndex + this.pagination.itemsPerPage;
      const validPaymentTypes = (this.paymentTypes || []).filter(type => type && type._id);
      const paginatedTypes = validPaymentTypes.slice(startIndex, endIndex);

      return `
  <div class="mb-4">
    <form id="addPaymentTypeForm" class="row g-3 align-items-end mt-2" onsubmit="event.preventDefault(); window.addPaymentType({
      name: document.getElementById('name').value,
      description: document.getElementById('description').value,
      amount: parseFloat(document.getElementById('amount').value),
      active: document.getElementById('active').checked
    });">
    <div class="row mb-3">
      <div class="col-md-6">
        <label for="name" class="form-label">Name</label>
        <input type="text" id="name" class="form-control" required>
      </div>
      <div class="col-md-6">
        <label for="amount" class="form-label">Amount</label>
        <input type="number" id="amount" class="form-control" required>
      </div>
      </div>
        <div class="row mb-3 d-flex align-items-end">
            <div class="col-md-8">
        <label for="description" class="form-label">Description</label>
        <input type="text" id="description" class="form-control">
      </div>
      <div class="col-auto">
        <div class="form-check form-switch">
          <input type="checkbox" id="active" class="form-check-input" checked>
          <label for="active" class="form-check-label">Active</label>
        </div>
      </div>
      <div class="col-auto">
        <button type="submit" class="btn btn-primary"><i class="bi bi-plus-lg"></i> Add</button>
      </div>
      </div>
    </form>
  </div>

  <div class="table-responsive">
    <table class="table table-hover">
      <thead>
        <tr>
          <th>Name</th>
          <th>Description</th>
          <th>Amount</th>
          <th>Status</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        ${paginatedTypes.map(type => `
          <tr>
            <td>${type.name}</td>
            <td>${type.description || 'N/A'}</td>
            <td>${this.formatCurrency(type.amount)}</td>
            <td>
              <span class="badge ${type.active ? 'bg-success' : 'bg-secondary'}">
                ${type.active ? 'Active' : 'Inactive'}
              </span>
            </td>
            <td>
              <div class="btn-group btn-group-sm">
                <button class="btn btn-outline-primary" onclick="window.editPaymentType('${type._id}')">
                  <i class="bi bi-pencil"></i>
                </button>
                <button class="btn btn-outline-danger" onclick="window.deletePaymentType('${type._id}')">
                  <i class="bi bi-trash"></i>
                </button>
              </div>
            </td>
          </tr>
        `).join('')}
      </tbody>
    </table>
  </div>

  ${this.paymentTypes.length > this.pagination.itemsPerPage ? `
  <nav class="mt-3">
    <ul class="pagination justify-content-center">
      <li class="page-item ${this.pagination.currentPage === 1 ? 'disabled' : ''}">
        <a class="page-link" href="#" onclick="window.changePage(${this.pagination.currentPage - 1}); return false;">
          <i class="bi bi-chevron-left"></i>
        </a>
      </li>
      ${Array.from({ length: Math.ceil(this.paymentTypes.length / this.pagination.itemsPerPage) }, (_, i) => i + 1)
            .map(page => `
          <li class="page-item ${page === this.pagination.currentPage ? 'active' : ''}">
            <a class="page-link" href="#" onclick="window.changePage(${page}); return false;">${page}</a>
          </li>
        `).join('')}
      <li class="page-item ${this.pagination.currentPage === Math.ceil(this.paymentTypes.length / this.pagination.itemsPerPage) ? 'disabled' : ''}">
        <a class="page-link" href="#" onclick="window.changePage(${this.pagination.currentPage + 1}); return false;">
          <i class="bi bi-chevron-right"></i>
        </a>
      </li>
    </ul>
  </nav>
  ` : ''}
    `;
    },

    managePaymentTypes() {
      this.paymentTypeForm = {
        name: '',
        amount: 0,
        active: true
      };

      // Define global handlers for the modal
      window.addPaymentType = (data) => {
        this.paymentTypeForm = data;
        this.handleAddPaymentType();
      };

      window.editPaymentType = (id) => {
        this.handleEditPaymentType(id);
      };

      window.deletePaymentType = (id) => {
        this.handleDeletePaymentType(id);
      };

      window.changePage = (page) => {
        this.handlePageChange(page);
      };

      Swal.fire({
        title: 'Manage Payment Types',
        html: this.renderPaymentTypesTable(),
        showConfirmButton: false,
        showCancelButton: true,
        cancelButtonText: 'Close',
        width: '800px',
        didOpen: () => {
          // Initialize form handlers
          const form = document.getElementById('addPaymentTypeForm');
          if (form) {
            form.addEventListener('submit', this.addPaymentType);
          }
        },
        willClose: () => {
          // Cleanup
          const form = document.getElementById('addPaymentTypeForm');
          if (form) {
            form.removeEventListener('submit', this.addPaymentType);
          }
        }
      });
    },

    async handleAddPaymentType() {
      try {
        const response = await axios.post('/payments', this.paymentTypeForm);
        // Headers not needed - cookies are automatic
        if (response.data.success) {
          await this.fetchPaymentTypes();
          this.paymentTypeForm = {
            name: '',
            description: '',
            amount: 0,
            active: true
          };

          // Update the SweetAlert modal with the refreshed data
          Swal.update({
            html: this.renderPaymentTypesTable()
          });

          // Show success message
          Swal.fire({
            icon: 'success',
            title: 'Success',
            text: 'Payment type added successfully'
          });
        }
      } catch (error) {
        this.logger.error('Error adding payment type:', error);
        const errorMessage = error.response?.data?.message || 'Failed to add payment type';
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: errorMessage
        });
      }
    },

    async handleEditPaymentType(id) {
      const type = this.paymentTypes.find(t => t._id === id);
      if (!type) return;

      const { value: formData } = await Swal.fire({
        title: 'Edit Payment Type',
        html: `
      <form id="editPaymentTypeForm" class="p-3">
        <div class="mb-3">
          <label for="editName" class="form-label">Name</label>
          <input type="text" id="editName" class="form-control" value="${type.name}" required>
        </div>
        <div class="mb-3">
          <label for="editDescription" class="form-label">Description</label>
          <input type="text" id="editDescription" class="form-control" value="${type.description || ''}">
        </div>
        <div class="mb-3">
          <label for="editAmount" class="form-label">Amount</label>
          <input type="number" id="editAmount" class="form-control" value="${type.amount}" required>
        </div>
        <div class="mb-3 form-check form-switch">
          <input type="checkbox" id="editActive" class="form-check-input" ${type.active ? 'checked' : ''}>
          <label for="editActive" class="form-check-label">Active</label>
        </div>
      </form>
    `,
        showCancelButton: true,
        confirmButtonText: 'Update',
        cancelButtonText: 'Cancel',
        focusConfirm: false,
        preConfirm: () => {
          return {
            name: document.getElementById('editName').value,
            description: document.getElementById('editDescription').value,
            amount: parseFloat(document.getElementById('editAmount').value),
            active: document.getElementById('editActive').checked
          };
        }
      });

      if (formData) {
        try {
          const response = await axios.put(`/payments/${id}`, formData);
          // Headers not needed - cookies are automatic
          if (response.data.success) {
            await this.fetchPaymentTypes();

            // Update the SweetAlert modal with the refreshed data
            Swal.update({
              html: this.renderPaymentTypesTable()
            });

            // Show success message
            Swal.fire({
              icon: 'success',
              title: 'Success',
              text: 'Payment type updated successfully'
            });
          }
        } catch (error) {
          this.logger.error('Error updating payment type:', error);
          const errorMessage = error.response?.data?.message || 'Failed to update payment type';
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: errorMessage
          });
        }
      }
    },

    async handleDeletePaymentType(id) {
      const result = await Swal.fire({
        title: 'Delete Payment Type',
        text: 'Are you sure you want to delete this payment type?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it',
        cancelButtonText: 'Cancel'
      });

      if (result.isConfirmed) {
        try {
          const response = await axios.delete(`/payments/${id}`);
          // Headers not needed - cookies are automatic
          if (response.data.success) {
            await this.fetchPaymentTypes();

            // Update the SweetAlert modal with the refreshed data
            Swal.update({
              html: this.renderPaymentTypesTable()
            });

            // Show success message
            Swal.fire({
              icon: 'success',
              title: 'Success',
              text: 'Payment type deleted successfully'
            });
          }
        } catch (error) {
          this.logger.error('Error deleting payment type:', error);
          const errorMessage = error.response?.data?.message || 'Failed to delete payment type';
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: errorMessage
          });
        }
      }
    },

    async handlePaymentTypesPagination(page) {
      if (page >= 1 && page <= Math.ceil(this.paymentTypes.length / this.pagination.itemsPerPage)) {
        this.pagination.currentPage = page;
        await this.$nextTick();
        Swal.update({ html: this.renderPaymentTypesTable() });
      }
    },

    // Handle page change for payment types modal pagination
    async handlePageChange(page) {
      // Redirect to handlePaymentTypesPagination for consistency
      await this.handlePaymentTypesPagination(page);
    },

    formatCurrency(amount) {
      return new Intl.NumberFormat('en-NG', {
        style: 'currency',
        currency: 'NGN'
      }).format(amount);
    },

    // Pagination methods
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.pagination.currentPage = page;
      }
    },

    prevPage() {
      if (this.pagination.currentPage > 1) {
        this.pagination.currentPage--;
      }
    },

    nextPage() {
      if (this.pagination.currentPage < this.totalPages) {
        this.pagination.currentPage++;
      }
    },

    // Session Controls Methods
    async viewSessionControls(session) {
      try {
        this.loading = true;
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/session-controls/session/${session._id}`
        );

        if (response.data) {
          this.showSessionControlsModal(session, response.data);
        } else {
          Swal.fire('Info', 'No session controls found for this academic session', 'info');
        }
      } catch (error) {
        this.logger.error('Error fetching session controls:', error);
        if (error.response?.status === 404) {
          Swal.fire('Info', 'No session controls found for this academic session', 'info');
        } else {
          Swal.fire('Error', 'Failed to fetch session controls', 'error');
        }
      } finally {
        this.loading = false;
      }
    },

    showSessionControlsModal(session, sessionControl) {
      const allControls = [];

      // Add default controls
      sessionControl.controls.forEach(control => {
        allControls.push({
          id: control._id,
          type: 'default',
          name: this.formatControlName(control.controlName),
          controlName: control.controlName,
          active: control.active,
          category: 'System Control'
        });
      });

      // Add payment controls
      sessionControl.paymentControls.forEach(paymentControl => {
        allControls.push({
          id: paymentControl._id,
          type: 'payment',
          name: paymentControl.payment?.payment?.name || 'Unknown Payment',
          amount: paymentControl.payment?.amount || 0,
          active: paymentControl.active,
          category: 'Payment Control'
        });
      });

      this.displayControlsModal(session, sessionControl._id, allControls);
    },

    displayControlsModal(session, sessionControlId, controls) {
      const controlsPerPage = 10;
      let currentPage = 1;
      let searchQuery = '';

      const getFilteredControls = () => {
        return controls.filter(control =>
          control.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          control.category.toLowerCase().includes(searchQuery.toLowerCase())
        );
      };

      const getPaginatedControls = (filteredControls) => {
        const startIndex = (currentPage - 1) * controlsPerPage;
        const endIndex = startIndex + controlsPerPage;
        return filteredControls.slice(startIndex, endIndex);
      };

      const getTotalPages = (filteredControls) => {
        return Math.ceil(filteredControls.length / controlsPerPage);
      };

      const generateControlsTable = () => {
        const filteredControls = getFilteredControls();
        const paginatedControls = getPaginatedControls(filteredControls);
        const totalPages = getTotalPages(filteredControls);

        let tableHTML = `
          <div class="mb-3">
            <input type="text" id="controlsSearch" class="form-control" placeholder="Search controls..." value="${searchQuery}">
          </div>
          <div class="table-responsive" style="max-height: 400px; overflow-y: auto;">
            <table class="table table-striped table-hover">
              <thead class="table-dark sticky-top">
                <tr>
                  <th>Control Name</th>
                  <th>Category</th>
                  <th>Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
        `;

        if (paginatedControls.length === 0) {
          tableHTML += `
            <tr>
              <td colspan="5" class="text-center py-3">No controls found</td>
            </tr>
          `;
        } else {
          paginatedControls.forEach(control => {
            const amountDisplay = control.type === 'payment' ? `₦${control.amount?.toLocaleString() || 0}` : 'N/A';

            tableHTML += `
              <tr>
                <td>${control.name}</td>
                <td>${control.category}</td>
                <td>${amountDisplay}</td>
                <td class="d-flex justify-content-center">
                  <div class="form-check form-switch">
  <input class="form-check-input" type="checkbox" id="controlSwitch-${control.id}"
         ${control.active ? 'checked' : ''}
         onchange="toggleControlStatus('${control.id}', '${control.type}', this.checked)">
         </div>
                </td>
              </tr>
            `;
          });
        }

        tableHTML += `
              </tbody>
            </table>
          </div>
        `;

        // Add pagination
        if (totalPages > 1) {
          tableHTML += `
            <div class="d-flex justify-content-between align-items-center mt-3">
              <div>
                <span class="text-muted">Showing ${paginatedControls.length} of ${filteredControls.length} controls</span>
              </div>
              <nav>
                <ul class="pagination pagination-sm mb-0">
                  <li class="page-item ${currentPage === 1 ? 'disabled' : ''}">
                    <button class="page-link" onclick="changePage(${currentPage - 1})" ${currentPage === 1 ? 'disabled' : ''}>Previous</button>
                  </li>
          `;

          for (let i = 1; i <= totalPages; i++) {
            tableHTML += `
              <li class="page-item ${i === currentPage ? 'active' : ''}">
                <button class="page-link" onclick="changePage(${i})">${i}</button>
              </li>
            `;
          }

          tableHTML += `
                  <li class="page-item ${currentPage === totalPages ? 'disabled' : ''}">
                    <button class="page-link" onclick="changePage(${currentPage + 1})" ${currentPage === totalPages ? 'disabled' : ''}>Next</button>
                  </li>
                </ul>
              </nav>
            </div>
          `;
        }

        return tableHTML;
      };

      const showModal = () => {
        Swal.fire({
          title: `Session Controls - ${session.sessionYear}`,
          html: generateControlsTable(),
          width: '900px',
          showCloseButton: true,
          showConfirmButton: false,
          didOpen: () => {
            // Function to attach search functionality
            const attachSearchListener = () => {
              const searchInput = document.getElementById('controlsSearch');
              if (searchInput) {
                searchInput.addEventListener('input', (e) => {
                  searchQuery = e.target.value;
                  currentPage = 1;
                  const cursorPosition = e.target.selectionStart;
                  Swal.update({
                    html: generateControlsTable()
                  });
                  // Restore focus and cursor position after update
                  setTimeout(() => {
                    const newSearchInput = document.getElementById('controlsSearch');
                    if (newSearchInput) {
                      newSearchInput.focus();
                      newSearchInput.setSelectionRange(cursorPosition, cursorPosition);
                      // Re-attach the event listener to the new input
                      attachSearchListener();
                    }
                  }, 0);
                });
              }
            };

            // Initial attachment of search functionality
            attachSearchListener();

            // Add global functions for pagination and toggle
            window.changePage = (page) => {
              currentPage = page;
              const searchInput = document.getElementById('controlsSearch');
              const cursorPosition = searchInput ? searchInput.selectionStart : 0;
              const searchValue = searchInput ? searchInput.value : searchQuery;
              Swal.update({
                html: generateControlsTable()
              });
              // Restore focus and cursor position after pagination
              setTimeout(() => {
                const newSearchInput = document.getElementById('controlsSearch');
                if (newSearchInput && searchValue) {
                  newSearchInput.focus();
                  newSearchInput.setSelectionRange(cursorPosition, cursorPosition);
                }
                // Re-attach the search listener
                attachSearchListener();
              }, 0);
            };

            window.toggleControlStatus = async (controlId, controlType, newStatus) => {
              try {
                await this.updateControlStatus(sessionControlId, controlId, controlType, newStatus);

                // Update the control in the local array
                const control = controls.find(c => c.id === controlId);
                if (control) {
                  control.active = newStatus;
                }

                // Preserve search input state before refresh
                const searchInput = document.getElementById('controlsSearch');
                const cursorPosition = searchInput ? searchInput.selectionStart : 0;
                const searchValue = searchInput ? searchInput.value : searchQuery;

                // Refresh the modal
                Swal.update({
                  html: generateControlsTable()
                });

                // Restore focus and cursor position after refresh
                setTimeout(() => {
                  const newSearchInput = document.getElementById('controlsSearch');
                  if (newSearchInput && searchValue) {
                    newSearchInput.focus();
                    newSearchInput.setSelectionRange(cursorPosition, cursorPosition);
                  }
                  // Re-attach the search listener
                  attachSearchListener();
                }, 0);

                Swal.fire({
                  icon: 'success',
                  title: 'Success',
                  text: `Control ${newStatus ? 'activated' : 'deactivated'} successfully`,
                  timer: 1500,
                  showConfirmButton: false
                }).then(() => {
                  // Show the controls modal again
                  showModal();
                });
              } catch (error) {
                this.logger.error('Error updating control status:', error);
                Swal.fire('Error', 'Failed to update control status', 'error');
              }
            };
          },
          willClose: () => {
            // Clean up global functions
            delete window.changePage;
            delete window.toggleControlStatus;
          }
        });
      };

      showModal();
    },

    async updateControlStatus(sessionControlId, controlId, controlType, active) {
      try {
        const response = await axios.put(
          `${process.env.VUE_APP_API_BASE_URL}/session-controls/${sessionControlId}/control`,
          {
            controlType,
            controlId,
            active
          }
          // Headers not needed - cookies are automatic
        );

        return response.data;
      } catch (error) {
        this.logger.error('Error updating control status:', error);
        throw error;
      }
    },

    formatControlName(controlName) {
      const nameMap = {
        'application': 'Application',
        'admissionProcessing': 'Admission Processing',
        'clearance': 'Clearance',
        'courseRegistration': 'Course Registration',
        'resultUpload': 'Result Upload',
        'resultRelease': 'Result Release'
      };

      return nameMap[controlName] || controlName;
    }
  }
};
</script>

<template>
  <div class="col-lg-12 col-md-12">
    <div class="p-4 text-start">
      <h4 class="fw-bold">Academic Management</h4>
      <p>
        Manage Academic Sessions, Schools, Departments, Programs, and Courses.
      </p>
      <hr class="mb-4" />

      <!-- Tabs -->
      <ul class="nav nav-tabs mb-4">
        <li class="nav-item">
          <a
            class="nav-link"
            :class="{ active: currentTab === 'sessions' }"
            href="#"
            @click.prevent="changeTab('sessions')"
          >
            <i class="bi bi-calendar-range me-2"></i>Academic Sessions
          </a>
        </li>
        <li class="nav-item">
          <a
            class="nav-link"
            :class="{ active: currentTab === 'schools' }"
            href="#"
            @click.prevent="changeTab('schools')"
          >
            <i class="bi bi-building me-2"></i>Schools
          </a>
        </li>
        <li class="nav-item">
          <a
            class="nav-link"
            :class="{ active: currentTab === 'departments' }"
            href="#"
            @click.prevent="changeTab('departments')"
          >
            <i class="bi bi-diagram-3 me-2"></i>Departments
          </a>
        </li>
        <li class="nav-item">
          <a
            class="nav-link"
            :class="{ active: currentTab === 'programs' }"
            href="#"
            @click.prevent="changeTab('programs')"
          >
            <i class="bi bi-mortarboard me-2"></i>Programs
          </a>
        </li>
        <li class="nav-item">
          <a
            class="nav-link"
            :class="{ active: currentTab === 'courses' }"
            href="#"
            @click.prevent="changeTab('courses')"
          >
            <i class="bi bi-book me-2"></i>Courses
          </a>
        </li>
        <li class="nav-item">
          <a
            class="nav-link"
            :class="{ active: currentTab === 'programTypes' }"
            href="#"
            @click.prevent="changeTab('programTypes')"
          >
            <i class="bi bi-tags me-2"></i>Program Types
          </a>
        </li>
        <li class="nav-item">
          <a
            class="nav-link"
            :class="{ active: currentTab === 'programModes' }"
            href="#"
            @click.prevent="changeTab('programModes')"
          >
            <i class="bi bi-tags me-2"></i>Program Modes
          </a>
        </li>
        <li class="nav-item">
          <a
            class="nav-link"
            :class="{ active: currentTab === 'programSessionPayments' }"
            href="#"
            @click.prevent="changeTab('programSessionPayments')"
          >
            <i class="bi bi-cash-coin me-2"></i>Payment Management
          </a>
        </li>
      </ul>

      <!-- Academic Sessions Tab -->
      <div v-if="currentTab === 'sessions'">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <div class="input-group" style="max-width: 300px">
            <span class="input-group-text"><i class="bi bi-search"></i></span>
            <input
              type="text"
              class="form-control"
              placeholder="Search sessions..."
              v-model="searchQuery"
            />
          </div>
          <button class="btn btn-primary" @click="openAddSessionModal">
            <i class="bi bi-plus-circle me-2"></i>Add New Session
          </button>
        </div>

        <div class="card shadow-sm">
          <div class="card-body p-0">
            <div class="table-responsive">
              <table class="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Session Year</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Status</th>
                    <th>Controls</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="loading">
                    <td colspan="6" class="text-center py-4">
                      <div class="spinner-border text-primary" role="status">
                        <span class="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                  <tr v-else-if="paginatedSessions.length === 0">
                    <td colspan="6" class="text-center py-4">
                      No academic sessions found.
                    </td>
                  </tr>
                  <tr v-for="session in paginatedSessions" :key="session._id">
                    <td>{{ session.sessionYear }}</td>
                    <td>
                      {{ new Date(session.startDate).toLocaleDateString() }}
                    </td>
                    <td>
                      {{ new Date(session.endDate).toLocaleDateString() }}
                    </td>
                    <td>
                      <span
                        class="badge"
                        :class="{
                          'bg-success': session.status === 'open',
                          'bg-warning': session.status === 'closed',
                          'bg-primary': session.status === 'running',
                          'bg-secondary': session.status === 'archived',
                        }"
                      >
                        {{ session.status }}
                      </span>
                    </td>
                    <td>
                      <button
                        @click="viewSessionControls(session)"
                        class="btn btn-sm btn-outline-info"
                        title="View Controls"
                      >
                        <i class="bi bi-gear"></i> View
                      </button>
                    </td>
                    <td>
                      <div class="btn-group">
                        <button
                          @click="editSession(session)"
                          class="btn btn-sm btn-outline-primary"
                        >
                          <i class="bi bi-pencil"></i>
                        </button>
                        <button
                          v-if="
                            session.status !== 'open' &&
                            session.status !== 'running'
                          "
                          @click="changeSessionStatus(session, 'open')"
                          class="btn btn-sm btn-outline-success"
                          title="Open Session"
                        >
                          <i class="bi bi-unlock"></i>
                        </button>
                        <button
                          v-if="session.status === 'open'"
                          @click="changeSessionStatus(session, 'running')"
                          class="btn btn-sm btn-outline-info"
                          title="Set to Running"
                        >
                          <i class="bi bi-play-circle"></i>
                        </button>
                        <button
                          v-if="session.status === 'running'"
                          @click="changeSessionStatus(session, 'closed')"
                          class="btn btn-sm btn-outline-warning"
                          title="Close Session"
                        >
                          <i class="bi bi-lock"></i>
                        </button>
                        <button
                          v-if="session.status === 'closed'"
                          @click="changeSessionStatus(session, 'archived')"
                          class="btn btn-sm btn-outline-secondary"
                          title="Archive Session"
                        >
                          <i class="bi bi-archive"></i>
                        </button>
                        <button
                          @click="deleteAcademicSession(session._id)"
                          class="btn btn-sm btn-outline-danger"
                        >
                          <i class="bi bi-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Pagination -->
            <div
              class="card-footer bg-white d-flex justify-content-between align-items-center"
            >
              <div>
                <span class="text-muted">
                  Showing {{ paginatedSessions.length }} of
                  {{ filteredSessions.length }} sessions
                </span>
              </div>
              <nav aria-label="Page navigation" v-if="totalPages > 1">
                <ul class="pagination mb-0">
                  <li
                    class="page-item"
                    :class="{ disabled: pagination.currentPage === 1 }"
                  >
                    <a class="page-link" href="#" @click.prevent="prevPage">
                      <i class="bi bi-chevron-left"></i>
                    </a>
                  </li>
                  <li
                    v-for="page in totalPages"
                    :key="page"
                    class="page-item"
                    :class="{ active: page === pagination.currentPage }"
                  >
                    <a
                      class="page-link"
                      href="#"
                      @click.prevent="goToPage(page)"
                    >
                      {{ page }}
                    </a>
                  </li>
                  <li
                    class="page-item"
                    :class="{ disabled: pagination.currentPage === totalPages }"
                  >
                    <a class="page-link" href="#" @click.prevent="nextPage">
                      <i class="bi bi-chevron-right"></i>
                    </a>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>
      </div>

      <!-- Schools Tab -->
      <div v-if="currentTab === 'schools'">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <div class="input-group" style="max-width: 300px">
            <span class="input-group-text"><i class="bi bi-search"></i></span>
            <input
              type="text"
              class="form-control"
              placeholder="Search schools..."
              v-model="searchQuery"
            />
          </div>
          <button class="btn btn-primary" @click="openAddSchoolModal">
            <i class="bi bi-plus-circle me-2"></i>Add New School
          </button>
        </div>

        <div class="card shadow-sm">
          <div class="card-body p-0">
            <div class="table-responsive">
              <table class="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Name</th>
                    <th>Description</th>
                    <th>Departments</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="loading">
                    <td colspan="5" class="text-center py-4">
                      <div class="spinner-border text-primary" role="status">
                        <span class="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                  <tr v-else-if="paginatedSchools.length === 0">
                    <td colspan="4" class="text-center py-4">
                      No schools found.
                    </td>
                  </tr>
                  <tr v-for="school in paginatedSchools" :key="school._id">
                    <td>{{ school.code }}</td>
                    <td>{{ school.name }}</td>
                    <td>{{ school.description }}</td>
                    <td>
                      <span class="badge bg-primary">
                        {{
                          departments.filter(
                            (dept) =>
                              dept.school && dept.school._id === school._id
                          ).length
                        }}
                      </span>
                    </td>
                    <td>
                      <div class="btn-group">
                        <button
                          @click="editSchool(school)"
                          class="btn btn-sm btn-outline-primary"
                        >
                          <i class="bi bi-pencil"></i>
                        </button>
                        <button
                          @click="deleteSchool(school._id)"
                          class="btn btn-sm btn-outline-danger"
                        >
                          <i class="bi bi-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Pagination -->
            <div
              class="card-footer bg-white d-flex justify-content-between align-items-center"
            >
              <div>
                <span class="text-muted">
                  Showing {{ paginatedSchools.length }} of
                  {{ filteredSchools.length }} schools
                </span>
              </div>
              <nav aria-label="Page navigation" v-if="totalPages > 1">
                <ul class="pagination mb-0">
                  <li
                    class="page-item"
                    :class="{ disabled: pagination.currentPage === 1 }"
                  >
                    <a class="page-link" href="#" @click.prevent="prevPage">
                      <i class="bi bi-chevron-left"></i>
                    </a>
                  </li>
                  <li
                    v-for="page in totalPages"
                    :key="page"
                    class="page-item"
                    :class="{ active: page === pagination.currentPage }"
                  >
                    <a
                      class="page-link"
                      href="#"
                      @click.prevent="goToPage(page)"
                    >
                      {{ page }}
                    </a>
                  </li>
                  <li
                    class="page-item"
                    :class="{ disabled: pagination.currentPage === totalPages }"
                  >
                    <a class="page-link" href="#" @click.prevent="nextPage">
                      <i class="bi bi-chevron-right"></i>
                    </a>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>
      </div>

      <!-- Departments Tab -->
      <div v-if="currentTab === 'departments'">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <div class="input-group" style="max-width: 300px">
            <span class="input-group-text"><i class="bi bi-search"></i></span>
            <input
              type="text"
              class="form-control"
              placeholder="Search departments..."
              v-model="searchQuery"
            />
          </div>
          <button class="btn btn-primary" @click="openAddDepartmentModal">
            <i class="bi bi-plus-circle me-2"></i>Add New Department
          </button>
        </div>

        <div class="card shadow-sm">
          <div class="card-body p-0">
            <div class="table-responsive">
              <table class="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Name</th>
                    <th>School</th>
                    <th>Description</th>
                    <th>Programs</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="loading">
                    <td colspan="5" class="text-center py-4">
                      <div class="spinner-border text-primary" role="status">
                        <span class="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                  <tr v-else-if="paginatedDepartments.length === 0">
                    <td colspan="5" class="text-center py-4">
                      No departments found.
                    </td>
                  </tr>
                  <tr
                    v-for="department in paginatedDepartments"
                    :key="department._id"
                  >
                    <td>{{ department.code }}</td>
                    <td>{{ department.name }}</td>
                    <td>{{ department.school?.name || "N/A" }}</td>
                    <td>{{ department.description }}</td>
                    <td>
                      <span class="badge bg-primary">
                        {{
                          programs.filter(
                            (prog) => prog.department._id === department._id
                          ).length
                        }}</span
                      >
                    </td>
                    <td>
                      <div class="btn-group">
                        <button
                          @click="editDepartment(department)"
                          class="btn btn-sm btn-outline-primary"
                        >
                          <i class="bi bi-pencil"></i>
                        </button>
                        <button
                          @click="deleteDepartment(department._id)"
                          class="btn btn-sm btn-outline-danger"
                        >
                          <i class="bi bi-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Pagination -->
            <div
              class="card-footer bg-white d-flex justify-content-between align-items-center"
            >
              <div>
                <span class="text-muted">
                  Showing {{ paginatedDepartments.length }} of
                  {{ filteredDepartments.length }} departments
                </span>
              </div>
              <nav aria-label="Page navigation" v-if="totalPages > 1">
                <ul class="pagination mb-0">
                  <li
                    class="page-item"
                    :class="{ disabled: pagination.currentPage === 1 }"
                  >
                    <a class="page-link" href="#" @click.prevent="prevPage">
                      <i class="bi bi-chevron-left"></i>
                    </a>
                  </li>
                  <li
                    v-for="page in totalPages"
                    :key="page"
                    class="page-item"
                    :class="{ active: page === pagination.currentPage }"
                  >
                    <a
                      class="page-link"
                      href="#"
                      @click.prevent="goToPage(page)"
                    >
                      {{ page }}
                    </a>
                  </li>
                  <li
                    class="page-item"
                    :class="{ disabled: pagination.currentPage === totalPages }"
                  >
                    <a class="page-link" href="#" @click.prevent="nextPage">
                      <i class="bi bi-chevron-right"></i>
                    </a>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>
      </div>

      <!-- Programs Tab -->
      <div v-if="currentTab === 'programs'">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <div class="input-group" style="max-width: 300px">
            <span class="input-group-text"><i class="bi bi-search"></i></span>
            <input
              type="text"
              class="form-control"
              placeholder="Search programs..."
              v-model="searchQuery"
            />
          </div>
          <button
            type="button"
            class="btn btn-primary"
            @click="openAddProgramModal()"
          >
            <i class="bi bi-plus-circle me-2"></i>Add New Program
          </button>
        </div>

        <div class="card shadow-sm">
          <div class="card-body p-0">
            <div class="table-responsive">
              <table class="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Name</th>
                    <th>Department</th>
                    <th>Program Offerings</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="loading">
                    <td colspan="5" class="text-center py-4">
                      <div class="spinner-border text-primary" role="status">
                        <span class="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                  <tr v-else-if="paginatedPrograms.length === 0">
                    <td colspan="5" class="text-center py-4">
                      No programs found.
                    </td>
                  </tr>
                  <tr v-for="program in paginatedPrograms" :key="program._id">
                    <td class="text-uppercase">{{ program.code }}</td>
                    <td class="text-capitalize">{{ program.name }}</td>
                    <td>{{ program.department?.name || "N/A" }}</td>
                    <td>
                      <!-- If offerings are available, show badges for each type-mode combination -->
                      <div class="d-flex flex-wrap gap-1">
                        <span
                          v-for="offering in program.offerings"
                          :key="offering._id"
                          class="me-1 text-uppercase badge text-bg-secondary"
                        >
                          {{ offering.structure?.type.type }}
                          {{ offering.structure?.mode.mode }}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div class="btn-group">
                        <button
                          @click="editProgram(program)"
                          class="btn btn-sm btn-outline-primary"
                        >
                          <i class="bi bi-pencil"></i>
                        </button>
                        <button
                          @click="deleteProgram(program._id)"
                          class="btn btn-sm btn-outline-danger"
                        >
                          <i class="bi bi-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Pagination -->
            <div
              class="card-footer bg-white d-flex justify-content-between align-items-center"
            >
              <div>
                <span class="text-muted">
                  Showing {{ paginatedPrograms.length }} of
                  {{ filteredPrograms.length }} programs
                </span>
              </div>
              <nav aria-label="Page navigation" v-if="totalPages > 1">
                <ul class="pagination mb-0">
                  <li
                    class="page-item"
                    :class="{ disabled: pagination.currentPage === 1 }"
                  >
                    <a class="page-link" href="#" @click.prevent="prevPage">
                      <i class="bi bi-chevron-left"></i>
                    </a>
                  </li>
                  <li
                    v-for="page in totalPages"
                    :key="page"
                    class="page-item"
                    :class="{ active: page === pagination.currentPage }"
                  >
                    <a
                      class="page-link"
                      href="#"
                      @click.prevent="goToPage(page)"
                    >
                      {{ page }}
                    </a>
                  </li>
                  <li
                    class="page-item"
                    :class="{ disabled: pagination.currentPage === totalPages }"
                  >
                    <a class="page-link" href="#" @click.prevent="nextPage">
                      <i class="bi bi-chevron-right"></i>
                    </a>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>
      </div>

      <!-- Courses Tab -->
      <div v-if="currentTab === 'courses'">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <div class="input-group" style="max-width: 300px">
            <span class="input-group-text"><i class="bi bi-search"></i></span>
            <input
              type="text"
              class="form-control"
              placeholder="Search course offerings..."
              v-model="searchQuery"
            />
          </div>
          <div class="btn-group">
            <button
              class="btn btn-outline-secondary"
              @click="openManageCoursesModal"
            >
              <i class="bi bi-gear me-2"></i>Manage Courses
            </button>
            <button class="btn btn-primary" @click="openAddCourseOfferingModal">
              <i class="bi bi-plus-circle me-2"></i>Add Course Offering
            </button>
          </div>
        </div>

        <div class="card shadow-sm">
          <div class="card-header bg-light">
            <h6 class="mb-0">
              <i class="bi bi-mortarboard me-2"></i>Course Offerings
            </h6>
            <small class="text-muted"
              >Manage course assignments to programs</small
            >
          </div>
          <div class="card-body p-0">
            <div class="table-responsive">
              <table class="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Course Code</th>
                    <th>Course Title</th>
                    <th>Program</th>
                    <th>Session</th>
                    <th>Year/Level</th>
                    <th>Semester</th>
                    <th>Category</th>
                    <th>Credit Units</th>
                    <th>Hours</th>
                    <th>Lecturer</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="loading">
                    <td colspan="11" class="text-center py-4">
                      <div class="spinner-border text-primary" role="status">
                        <span class="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                  <tr v-else-if="paginatedCourses.length === 0">
                    <td colspan="11" class="text-center py-4">
                      No courses found.
                    </td>
                  </tr>
                  <tr v-for="course in paginatedCourses" :key="course._id">
                    <td>{{ course.code }}</td>
                    <td>{{ course.title }}</td>
                    <td>{{ course.program || "N/A" }}</td>
                    <td>{{ course.session || "N/A" }}</td>
                    <td>{{ course.year }}</td>
                    <td>{{ course.semester }}</td>
                    <td>
                      <span
                        class="badge"
                        :class="
                          course.category === 'compulsory'
                            ? 'bg-primary'
                            : 'bg-success'
                        "
                      >
                        {{ course.category || "compulsory" }}
                      </span>
                    </td>
                    <td>{{ course.units }}</td>
                    <td>{{ course.hours }}</td>
                    <td>{{ course.lecturer || "Not assigned" }}</td>
                    <td>
                      <div class="btn-group">
                        <button
                          @click="editCourse(course)"
                          class="btn btn-sm btn-outline-primary"
                        >
                          <i class="bi bi-pencil"></i>
                        </button>
                        <button
                          @click="deleteCourse(course)"
                          class="btn btn-sm btn-outline-danger"
                        >
                          <i class="bi bi-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Pagination -->
            <div
              class="card-footer bg-white d-flex justify-content-between align-items-center"
            >
              <div>
                <span class="text-muted">
                  Showing {{ paginatedCourses.length }} of
                  {{ filteredCourses.length }} courses
                </span>
              </div>
              <nav aria-label="Page navigation" v-if="totalPages > 1">
                <ul class="pagination mb-0">
                  <li
                    class="page-item"
                    :class="{ disabled: pagination.currentPage === 1 }"
                  >
                    <a class="page-link" href="#" @click.prevent="prevPage">
                      <i class="bi bi-chevron-left"></i>
                    </a>
                  </li>
                  <li
                    v-for="page in totalPages"
                    :key="page"
                    class="page-item"
                    :class="{ active: page === pagination.currentPage }"
                  >
                    <a
                      class="page-link"
                      href="#"
                      @click.prevent="goToPage(page)"
                    >
                      {{ page }}
                    </a>
                  </li>
                  <li
                    class="page-item"
                    :class="{ disabled: pagination.currentPage === totalPages }"
                  >
                    <a class="page-link" href="#" @click.prevent="nextPage">
                      <i class="bi bi-chevron-right"></i>
                    </a>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>
      </div>

      <!-- Program Types Tab -->
      <div v-if="currentTab === 'programTypes'">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <div class="input-group" style="max-width: 300px">
            <span class="input-group-text"><i class="bi bi-search"></i></span>
            <input
              type="text"
              class="form-control"
              placeholder="Search program types..."
              v-model="searchQuery"
            />
          </div>
          <button class="btn btn-primary" @click="openAddProgramTypeModal">
            <i class="bi bi-plus-circle me-2"></i>Add New Program Type
          </button>
        </div>

        <div class="card shadow-sm">
          <div class="card-body p-0">
            <div class="table-responsive">
              <table class="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Description</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="loading">
                    <td colspan="5" class="text-center py-4">
                      <div class="spinner-border text-primary" role="status">
                        <span class="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                  <tr v-else-if="paginatedProgramTypes.length === 0">
                    <td colspan="5" class="text-center py-4">
                      No program types found.
                    </td>
                  </tr>
                  <tr
                    v-for="programType in paginatedProgramTypes"
                    :key="programType._id"
                    :class="{ 'table-secondary': !programType.active }"
                  >
                    <td>{{ programType.type }}</td>
                    <td>{{ programType.description }}</td>
                    <td>
                      <span
                        class="badge"
                        :class="
                          programType.active ? 'bg-success' : 'bg-secondary'
                        "
                      >
                        {{ programType.active ? "Active" : "Inactive" }}
                      </span>
                    </td>
                    <td>
                      <div class="btn-group">
                        <button
                          @click="editProgramType(programType)"
                          class="btn btn-sm btn-outline-primary"
                          title="Edit"
                        >
                          <i class="bi bi-pencil"></i>
                        </button>
                        <button
                          @click="toggleProgramTypeStatus(programType)"
                          class="btn btn-sm"
                          :class="
                            programType.active
                              ? 'btn-outline-warning'
                              : 'btn-outline-success'
                          "
                          :title="
                            programType.active ? 'Deactivate' : 'Activate'
                          "
                        >
                          <i
                            class="bi"
                            :class="
                              programType.active
                                ? 'bi-toggle-on'
                                : 'bi-toggle-off'
                            "
                          ></i>
                        </button>
                        <button
                          @click="deleteProgramType(programType._id)"
                          class="btn btn-sm btn-outline-danger"
                          title="Delete"
                        >
                          <i class="bi bi-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Pagination -->
            <div
              class="card-footer bg-white d-flex justify-content-between align-items-center"
            >
              <div>
                <span class="text-muted">
                  Showing {{ paginatedProgramTypes.length }} of
                  {{ filteredProgramTypes.length }} program types
                </span>
              </div>
              <nav aria-label="Page navigation" v-if="totalPages > 1">
                <ul class="pagination mb-0">
                  <li
                    class="page-item"
                    :class="{ disabled: pagination.currentPage === 1 }"
                  >
                    <a class="page-link" href="#" @click.prevent="prevPage">
                      <i class="bi bi-chevron-left"></i>
                    </a>
                  </li>
                  <li
                    v-for="page in totalPages"
                    :key="page"
                    class="page-item"
                    :class="{ active: page === pagination.currentPage }"
                  >
                    <a
                      class="page-link"
                      href="#"
                      @click.prevent="goToPage(page)"
                    >
                      {{ page }}
                    </a>
                  </li>
                  <li
                    class="page-item"
                    :class="{ disabled: pagination.currentPage === totalPages }"
                  >
                    <a class="page-link" href="#" @click.prevent="nextPage">
                      <i class="bi bi-chevron-right"></i>
                    </a>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>
      </div>

      <!-- Program Modes Tab -->
      <div v-if="currentTab === 'programModes'">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <div class="input-group" style="max-width: 300px">
            <span class="input-group-text"><i class="bi bi-search"></i></span>
            <input
              type="text"
              class="form-control"
              placeholder="Search program modes..."
              v-model="searchQuery"
            />
          </div>
          <button class="btn btn-primary" @click="openAddProgramModeModal">
            <i class="bi bi-plus-circle me-2"></i>Add New Program Mode
          </button>
        </div>

        <div class="card shadow-sm">
          <div class="card-body p-0">
            <div class="table-responsive">
              <table class="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Mode</th>
                    <th>Description</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="loading">
                    <td colspan="4" class="text-center py-4">
                      <div class="spinner-border text-primary" role="status">
                        <span class="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                  <tr v-else-if="paginatedProgramModes.length === 0">
                    <td colspan="4" class="text-center py-4">
                      No program modes found.
                    </td>
                  </tr>
                  <tr
                    v-for="programMode in paginatedProgramModes"
                    :key="programMode._id"
                    :class="{ 'table-secondary': !programMode.active }"
                  >
                    <td>{{ programMode.mode }}</td>
                    <td>{{ programMode.description }}</td>
                    <td>
                      <span
                        class="badge"
                        :class="
                          programMode.active ? 'bg-success' : 'bg-secondary'
                        "
                      >
                        {{ programMode.active ? "Active" : "Inactive" }}
                      </span>
                    </td>
                    <td>
                      <div class="btn-group">
                        <button
                          @click="editProgramMode(programMode)"
                          class="btn btn-sm btn-outline-primary"
                          title="Edit"
                        >
                          <i class="bi bi-pencil"></i>
                        </button>
                        <button
                          @click="toggleProgramModeStatus(programMode)"
                          class="btn btn-sm"
                          :class="
                            programMode.active
                              ? 'btn-outline-warning'
                              : 'btn-outline-success'
                          "
                          :title="
                            programMode.active ? 'Deactivate' : 'Activate'
                          "
                        >
                          <i
                            class="bi"
                            :class="
                              programMode.active
                                ? 'bi-toggle-on'
                                : 'bi-toggle-off'
                            "
                          ></i>
                        </button>
                        <button
                          @click="deleteProgramMode(programMode._id)"
                          class="btn btn-sm btn-outline-danger"
                          title="Delete"
                        >
                          <i class="bi bi-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Pagination -->
            <div
              class="card-footer bg-white d-flex justify-content-between align-items-center"
            >
              <div>
                <span class="text-muted">
                  Showing {{ paginatedProgramModes.length }} of
                  {{ filteredProgramModes.length }} program modes
                </span>
              </div>
              <nav aria-label="Page navigation" v-if="totalPages > 1">
                <ul class="pagination mb-0">
                  <li
                    class="page-item"
                    :class="{ disabled: pagination.currentPage === 1 }"
                  >
                    <a class="page-link" href="#" @click.prevent="prevPage">
                      <i class="bi bi-chevron-left"></i>
                    </a>
                  </li>
                  <li
                    v-for="page in totalPages"
                    :key="page"
                    class="page-item"
                    :class="{ active: page === pagination.currentPage }"
                  >
                    <a
                      class="page-link"
                      href="#"
                      @click.prevent="goToPage(page)"
                    >
                      {{ page }}
                    </a>
                  </li>
                  <li
                    class="page-item"
                    :class="{ disabled: pagination.currentPage === totalPages }"
                  >
                    <a class="page-link" href="#" @click.prevent="nextPage">
                      <i class="bi bi-chevron-right"></i>
                    </a>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>
      </div>

      <!-- Payments Tab -->
      <div v-if="currentTab === 'programSessionPayments'">
        <div class="d-flex justify-content-end align-items-center mb-3">
          <div>
            <button
              class="btn btn-primary me-2"
              @click="addProgramSessionPayment"
            >
              <i class="bi bi-plus-circle me-2"></i>Add New Payment
            </button>
            <button class="btn btn-outline-primary" @click="managePaymentTypes">
              <i class="bi bi-gear me-2"></i>Manage Payment Types
            </button>
          </div>
        </div>

        <div class="card shadow-sm">
          <div class="card-header d-flex justify-content-between gap-3">
            <div>
              <select
                v-model="selectedProgramSessionPaymentSession"
                class="form-select"
                aria-label="Filter payments by academic session"
              >
                <option value="all">All Sessions</option>
                <option
                  v-for="session in sortedAcademicSessionsForPaymentFilter"
                  :key="session._id"
                  :value="session._id"
                >
                  {{ session.sessionYear }}
                </option>
              </select>
            </div>
            <div class="input-group" style="max-width: 500px">
              <span class="input-group-text"><i class="bi bi-search"></i></span>
              <input
                type="text"
                class="form-control"
                placeholder="Search payments..."
                v-model="searchQuery"
              />
            </div>
          </div>
          <div class="card-body p-0">
            <div class="table-responsive">
              <table class="table table-hover mb-0">
                <thead>
                  <tr class="small">
                    <th>Payment Name</th>
                    <th>Academic Session</th>
                    <th>Applicable To</th>
                    <th>Amount</th>
                    <th>Payment Order</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="loading">
                    <td colspan="7" class="text-center py-4">
                      <div class="spinner-border text-primary" role="status">
                        <span class="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                  <tr v-else-if="paginatedProgramSessionPayments.length === 0">
                    <td colspan="7" class="text-center py-4">
                      No program session payments found.
                    </td>
                  </tr>
                  <tr
                    v-for="payment in paginatedProgramSessionPayments"
                    :key="payment._id"
                    :class="{ 'table-secondary': !payment.active }"
                  >
                    <td>{{ payment.payment?.name }}</td>
                    <td>
                      {{ payment.academicSession?.sessionYear }}
                    </td>
                    <td>
                      <span class="badge bg-info">
                        {{ formatProgramSessionPaymentApplicableTo(payment) }}
                      </span>
                    </td>
                    <td>{{ formatCurrency(payment.amount) }}</td>
                    <td>{{ payment.paymentOrder }}</td>
                    <td>
                      <span
                        class="badge"
                        :class="payment.active ? 'bg-success' : 'bg-secondary'"
                      >
                        {{ payment.active ? "Active" : "Inactive" }}
                      </span>
                    </td>
                    <td>
                      <div class="btn-group">
                        <button
                          @click="editProgramSessionPayment(payment)"
                          class="btn btn-sm btn-outline-primary"
                          title="Edit"
                        >
                          <i class="bi bi-pencil"></i>
                        </button>
                        <button
                          @click="togglePaymentStatus(payment)"
                          class="btn btn-sm"
                          :class="
                            payment.active
                              ? 'btn-outline-warning'
                              : 'btn-outline-success'
                          "
                          :title="payment.active ? 'Deactivate' : 'Activate'"
                        >
                          <i
                            class="bi"
                            :class="
                              payment.active ? 'bi-toggle-on' : 'bi-toggle-off'
                            "
                          ></i>
                        </button>
                        <button
                          @click="deletePayment(payment)"
                          class="btn btn-sm btn-outline-danger"
                          title="Delete"
                        >
                          <i class="bi bi-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Pagination -->
            <div
              class="card-footer bg-white d-flex justify-content-between align-items-center"
            >
              <div>
                <span class="text-muted">
                  Showing {{ paginatedProgramSessionPayments.length }} of
                  {{ filteredProgramSessionPayments.length }} payments
                </span>
              </div>
              <nav aria-label="Page navigation" v-if="totalPages > 1">
                <ul class="pagination mb-0">
                  <li
                    class="page-item"
                    :class="{ disabled: pagination.currentPage === 1 }"
                  >
                    <a class="page-link" href="#" @click.prevent="prevPage">
                      <i class="bi bi-chevron-left"></i>
                    </a>
                  </li>
                  <li
                    v-for="page in totalPages"
                    :key="page"
                    class="page-item"
                    :class="{ active: page === pagination.currentPage }"
                  >
                    <a
                      class="page-link"
                      href="#"
                      @click.prevent="goToPage(page)"
                    >
                      {{ page }}
                    </a>
                  </li>
                  <li
                    class="page-item"
                    :class="{ disabled: pagination.currentPage === totalPages }"
                  >
                    <a class="page-link" href="#" @click.prevent="nextPage">
                      <i class="bi bi-chevron-right"></i>
                    </a>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="modal fade"
      id="paymentModal"
      ref="paymentModal"
      tabindex="-1"
      aria-labelledby="paymentModalLabel"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-lg modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="paymentModalLabel">
              {{ paymentModalTitle }}
            </h5>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>

          <div class="modal-body">
            <div
              v-if="paymentFormError"
              class="alert alert-danger"
              role="alert"
            >
              {{ paymentFormError }}
            </div>

            <div class="row">
              <div class="col-md-6 mb-3">
                <label for="paymentModalType" class="form-label"
                  >Payment Type</label
                >
                <select
                  id="paymentModalType"
                  v-model="paymentForm.payment"
                  class="form-select"
                  @change="handlePaymentTypeSelection"
                  required
                >
                  <option value="">Select Payment Type</option>
                  <option
                    v-for="type in paymentTypes"
                    :key="type._id"
                    :value="type._id"
                  >
                    {{ type.name }}
                  </option>
                </select>
              </div>

              <div class="col-md-6 mb-3">
                <label for="paymentModalAmount" class="form-label"
                  >Amount</label
                >
                <div class="input-group">
                  <span class="input-group-text">₦</span>
                  <input
                    id="paymentModalAmount"
                    v-model.number="paymentForm.amount"
                    type="number"
                    class="form-control"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    required
                  />
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col-md-6 mb-3">
                <label for="paymentModalSession" class="form-label"
                  >Academic Session</label
                >
                <select
                  id="paymentModalSession"
                  v-model="paymentForm.academicSession"
                  class="form-select"
                  @change="handlePaymentAcademicSessionChange"
                  required
                >
                  <option value="">Select Academic Session</option>
                  <option
                    v-for="session in academicSessions"
                    :key="session._id"
                    :value="session._id"
                  >
                    {{ session.sessionYear }}
                  </option>
                </select>
              </div>

              <div class="col-md-6 mb-3">
                <label for="paymentModalApplicableTo" class="form-label"
                  >Applicable To</label
                >
                <select
                  id="paymentModalApplicableTo"
                  v-model="paymentForm.applicableTo"
                  class="form-select"
                  @change="handlePaymentApplicableToChange"
                  required
                >
                  <option value="" disabled>Select Applicable Audience</option>
                  <option value="students">Students</option>
                  <option value="applicants">Applicants</option>
                  <option value="individual_students">
                    Individual Students
                  </option>
                  <option value="specific_program">Specific Program</option>
                </select>
              </div>
            </div>

            <div class="alert alert-light border small mb-3" role="status">
              {{ getPaymentAudienceSummary(paymentForm.applicableTo) }}
            </div>

            <div v-if="shouldShowPaymentAudiencePreview" class="mb-3">
              <label class="form-label">
                Matching
                {{
                  paymentForm.applicableTo === "students"
                    ? "Students"
                    : "Applicants"
                }}
                <span class="text-muted"
                  >({{ paymentAudiencePreviewItems.length }})</span
                >
              </label>
              <div class="border rounded bg-light-subtle">
                <ul class="list-group list-group-flush small">
                  <li
                    v-if="paymentAudiencePreviewItems.length === 0"
                    class="list-group-item text-muted"
                  >
                    No matching records found for the selected academic session.
                  </li>
                  <li
                    v-for="item in paymentAudiencePreviewItems.slice(0, 8)"
                    :key="item._id"
                    class="list-group-item"
                  >
                    {{ item.label }}
                  </li>
                </ul>
                <div
                  v-if="paymentAudiencePreviewItems.length > 8"
                  class="px-3 py-2 small text-muted border-top"
                >
                  Showing first 8 of
                  {{ paymentAudiencePreviewItems.length }} records.
                </div>
              </div>
            </div>

            <div v-if="shouldShowPaymentStudentTargets" class="mb-3">
              <label for="paymentModalTargetStudents" class="form-label"
                >Select Students</label
              >
              <select
                id="paymentModalTargetStudents"
                v-model="paymentForm.targetUserIds"
                class="form-select"
                multiple
                size="8"
              >
                <option
                  v-for="student in paymentTargetOptions.students"
                  :key="student._id"
                  :value="student._id"
                >
                  {{ student.label }}
                </option>
              </select>
              <div class="form-text">
                Hold Command or Control to select multiple admitted students.
              </div>
            </div>

            <div v-if="shouldShowPaymentProgramTargets" class="mb-3">
              <label for="paymentModalTargetPrograms" class="form-label"
                >Select Program Targets</label
              >
              <select
                id="paymentModalTargetPrograms"
                v-model="paymentTargetProgramValues"
                class="form-select"
                multiple
                size="8"
                @change="syncSpecificProgramTargets"
              >
                <option
                  v-for="target in paymentTargetOptions.programTargets"
                  :key="target.value"
                  :value="target.value"
                >
                  {{ target.label }}
                </option>
              </select>
              <div class="form-text">
                {{ selectedSpecificProgramTargetCount }} specific program
                target(s) selected.
              </div>
            </div>

            <div class="row">
              <div class="col-md-6 mb-3">
                <label for="paymentModalOrder" class="form-label"
                  >Payment Order (Optional)</label
                >
                <input
                  id="paymentModalOrder"
                  v-model="paymentForm.paymentOrder"
                  type="number"
                  class="form-control"
                  min="1"
                  placeholder="eg 1 or 2 or 3 or ..."
                />
              </div>

              <div class="col-md-6 mb-3">
                <label for="paymentModalDueDate" class="form-label"
                  >Due Date (Optional)</label
                >
                <input
                  id="paymentModalDueDate"
                  v-model="paymentForm.dueDate"
                  type="date"
                  class="form-control"
                />
              </div>
            </div>

            <div class="form-check mb-3 d-flex gap-3">
              <input
                id="paymentModalApplyLateFee"
                v-model="paymentForm.applyLateFee"
                class="form-check-input"
                type="checkbox"
              />
              <label class="form-check-label" for="paymentModalApplyLateFee">
                Apply Late Fee
              </label>
            </div>

            <div v-if="paymentForm.applyLateFee" class="mb-3">
              <label for="paymentModalLateFee" class="form-label"
                >Late Fee Amount</label
              >
              <div class="input-group">
                <span class="input-group-text">₦</span>
                <input
                  id="paymentModalLateFee"
                  v-model.number="paymentForm.lateFee"
                  type="number"
                  class="form-control"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                />
              </div>
            </div>

            <div class="form-check d-flex gap-3">
              <input
                id="paymentModalActive"
                v-model="paymentForm.active"
                class="form-check-input"
                type="checkbox"
              />
              <label class="form-check-label" for="paymentModalActive">
                Active
              </label>
            </div>
          </div>

          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="closePaymentModal"
              :disabled="paymentFormSubmitting"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-primary"
              @click="savePaymentFromModal"
              :disabled="paymentFormSubmitting"
            >
              <span
                v-if="paymentFormSubmitting"
                class="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              {{
                paymentModalMode === "edit" ? "Update Payment" : "Save Payment"
              }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.table th {
  font-weight: 600;
  background-color: #f8f9fa;
}

.badge {
  font-size: 0.75rem;
  padding: 0.35em 0.65em;
}

.pagination {
  margin-bottom: 0;
}

.page-link {
  color: #0d6efd;
  cursor: pointer;
}

.page-item.active .page-link {
  background-color: #0d6efd;
  border-color: #0d6efd;
  color: white;
}

.nav-tabs .nav-link {
  cursor: pointer;
}
</style>
